import React, {
    useCallback,
    useEffect,
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
    type ChangeEvent,
    type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import {
    ArrowLeft,
    BatteryHigh,
    CaretRight,
    Check,
    CheckCircle,
    Circle,
    CircleNotch,
    CurrencyDollar,
    House,
    IdentificationBadge,
    Info,
    Bank,
    MapPin,
    Package,
    Plus,
    RocketLaunch,
    SlidersHorizontal,
    Sparkle,
    SquaresFour,
    Sun,
    TrendDown,
    TreeStructure,
    UsersThree,
    WarningCircle,
    Wrench,
    X,
} from '@phosphor-icons/react';
import { DEFAULT_SITE_VIEWER_MESSAGE } from '../../helpers/siteViewer3dAccess';
import { persistDashboardLayoutVariant } from '../../helpers/dashboardLayoutVariant';
import { AlertType } from '../../@types/enums';
import { notification } from 'antd';
import { useOneButtonStore } from '../../store/oneButtonStore';
import type { OpportunitySalesPerson } from '../../@types/oneButtonType';
import AddTitleContactsModal from '../../components/Layout-Components/Editor-Layout/oneBUTTONDashboard/AddTitleContactsModal';
import { DashboardV3SectionModals } from '../../components/Layout-Components/Editor-Layout/oneBUTTONDashboard/sections/dashboardV3/DashboardV3SectionModals';
import '../../components/Layout-Components/Editor-Layout/oneBUTTONDashboard/oneButton-css/ContactsSnapshotCard.css';
import '../../components/Layout-Components/Editor-Layout/oneBUTTONDashboard/oneButton-css/ContactsModalSplit.css';
import GenerateQuoteModal, {
    type QuoteFormState,
} from '../../components/Layout-Components/Editor-Layout/oneBUTTONDashboard/GenerateQuoteModal';
import {
    type selectedQuote,
} from '../../components/Layout-Components/Editor-Layout/oneBUTTONDashboard/FinancingGridCard';
import DesignLossesModal from '../../components/Layout-Components/Editor-Layout/oneBUTTONDashboard/DesignLossesModal';
import { getDesignLossesSourceLabel } from '../../components/Layout-Components/Editor-Layout/oneBUTTONDashboard/designLossesFinanceGate';
import { getOneButtonCapabilityValue } from '../../services/oneButtonServices/capabilityService';
import { RoofDetailsModal } from '../../components/Layout-Components/Editor-Layout/oneBUTTONDashboard/RoofDetailsModal';
import '../../components/Layout-Components/Editor-Layout/oneBUTTONDashboard/oneButton-css/GenerateQuoteModal.css';
import '../../components/Layout-Components/Editor-Layout/oneBUTTONDashboard/oneButton-css/ProductDesignsModal.css';
import { applyFinalizedViewerMetricsToSystemDesign } from '../../helpers/systemDesignSolarMetrics';
import designServices from '../../services/oneButtonServices/designServices';
import quoteServices from '../../services/oneButtonServices/quoteServices';
import { useViewerStore } from '../../store/store';
import packingSlipService from '../../services/packingSlipService';
import opportunityInfoService from '../../services/oneButtonServices/opportunityInfoService';
import FinancePartnerEligibility, {
    type FinancePartnerEligibilitySummary,
} from '../../components/Layout-Components/Editor-Layout/Finance-Partner-Eligibility/FinancePartnerEligibility';
import {
    clearDisabledPreContractProducts,
    defaultIntakeForm,
    loadIntakeForm,
    persistIntakeForm,
    normalizeFillOrientations,
    PRE_CONTRACT_DISABLED_PRODUCTS,
    PRE_CONTRACT_PRODUCTS,
    type PreContractDesignTool,
    type PreContractIntakeForm,
    type PreContractProduct,
    type PreContractRoofModuleLayout,
    type PreContractSiteContext,
    type PreContractSiteTree,
    US_STATE_OPTIONS,
} from '../lib/preContractTypes';
import { resolveStoreOpportunityId } from '../lib/preContractTitle';
import {
    fetchUtilityCompanyCatalog,
    hasUtilityPrefill,
    mergeUtilityPrefill,
    resolveUtilityPrefillFromStore,
    resolveUtilityCompanyCatalogMatch,
    resolveUtilityCompanyState,
    utilityCatalogEmptyMessage,
    utilityCompanyOptionsFromCatalog,
    utilityCompanySelectionPatch,
    preContractUtilityDetailsSignature,
    listPreContractUtilityDetailChanges,
} from '../lib/preContractUtilityPrefill';
import { usePreContractUtilityCatalog } from './usePreContractUtilityCatalog';
import {
    ensureSystemDesignForPrefill,
    fetchAndStoreEnergyCommunityStatus,
    hasProductPrefill,
    mergeBatteryPrefillFromSystemDesign,
    mergeProductPrefill,
    resolveProductPrefillFromOpportunity,
} from '../lib/preContractOpportunityPrefill';
import { ensurePreContractOpportunityHydrated } from '../lib/preContractOpportunityBootstrap';
import { resolvePreContractOpportunityLink } from '../lib/preContractOpportunityLink';
import { PreContractOpportunityLinkBanner } from './PreContractOpportunityLinkBanner';
import { PreContractQuoteReviewModal } from './PreContractQuoteReviewModal';
import {
    buildPreContractQuoteReview,
    type PreContractQuoteReview,
} from '../lib/preContractQuoteReview';
import {
    canLaunchWorkspace,
    canRunAiFromLayout,
    getRunAiFromLayoutIssues,
    canAdvanceFromIntakeBoard,
    canAddSolarProduct,
    CONTENT_STEPS,
    designAvailabilityIssues,
    formatInstallAddress,
    getOpportunityBlockReason,
    getSolarProductBlockReason,
    getStepValidation,
    isOpportunityReady,
    OPPORTUNITY_NOT_LOADED_MESSAGE,
    hasActiveDesignTool,
    isAuroraSiteDesignLoaded,
    type StepValidation,
    type WizardStepId,
} from '../lib/preContractValidation';
import {
    PreContractRoofSlopeSection,
} from './PreContractDesignExtras';
import BatteryDetailsModal from '../../components/Layout-Components/Editor-Layout/oneBUTTONDashboard/BatteryDetailsModal';
import { PreContractOneDrawEmbed } from './PreContractOneDrawEmbed';
import type { SavedRoofModulesLayout } from '../../@types/types';
import {
    applyRoofSelectionsToLayouts,
    collectAllModuleIds,
    collectIncludedModuleIds,
    mergeLiveModuleLayouts,
    mergePciSiteTrees,
    pciGhostedTreeIdKey,
    pciRoofModuleLayoutsEqual,
    pciSiteRoofsEqual,
    pciSiteTreesLookEqual,
    PCI_ONEDRAW_ELIGIBILITY_MSG,
    PCI_ONEDRAW_LOAD_ERROR_MSG,
    PCI_ONEDRAW_ROOFS_MSG,
    postPci3dViewToEmbed,
    postPciModuleSelectionToEmbed,
    postPciLossesVendorToEmbed,
    postPciRoofSelectionToEmbed,
    postPciTreeGhostToEmbed,
    postPciCancelShadeToEmbed,
    postPciViewportResizeToEmbed,
    requestPciApplyDesignOptionToEmbed,
    requestPciApplyMaxFillToEmbed,
    requestPciLoadDesignVersionToEmbed,
    requestPciModulesLayoutFromEmbed,
    sumPciSelectedRoofSquares,
    type PciEligibilitySceneValues,
    type PciSiteRoof,
    type PciSiteTree,
} from '../lib/preContractEmbedBridge';
import { PreContractModuleStep } from './PreContractModuleStep';
import { PreContractSolarLayoutDetailsBar } from './PreContractSolarLayoutDetailsBar';
import type { PreContractSummaryItem } from './PreContractModuleStep.types';
import { PreContractAiDesignsStep } from './PreContractAiDesignsStep';
import {
    PreContractSavedDesignsPanel,
    metricsFromSavedDesign,
    savedDesignDisplayName,
    savedDesignKey,
} from './PreContractSavedDesignsPanel';
import { PreContractSelectedDesignStep } from './PreContractSelectedDesignStep';
import { PreContractFinalizeModal } from './PreContractFinalizeModal';
import { PreContractFinalizeConfirmModal } from './PreContractFinalizeConfirmModal';
import {
    applyLossesVendor,
    getCurrentLossesVendor,
    readOpportunitySolarPartner,
    resolveActiveLossesVendor,
    resolveSolarFinancePartner,
} from '../lib/preContractPartnerLosses';
import {
    intakePatchFromAiDesign,
    type PreContractAiDesignOption,
    clearPreContractAiOptionsCache,
    hasPreContractAiOptionsCache,
    resolvePreContractAiOptionById,
    resolveSelectedPreContractAiOption,
} from '../lib/preContractAiDesigns';
import { fetchFinalizedDesignVersions } from '../../services/designVersionService';
import type { DesignData } from '../../@types/types';
import {
    countPresentModuleCells,
    expandLayoutsToMaxFill,
    loadPreContractFullModuleLayouts,
    persistPreContractFullModuleLayouts,
} from '../lib/preContractFullModuleLayouts';
import { PreContractLivePricingRail } from './PreContractLivePricingRail';
import { resolveEnergyCommunityLikelyQualifies } from '../../components/Layout-Components/Editor-Layout/Three-JS-Viewer/EnergyCommunityNotice';
import {
    buildPreContractRoofDesignSnapshot,
    patchFormFromRoofDesignSnapshot,
    roofDesignFingerprint,
} from '../lib/preContractRoofDesign';
import {
    isSolarLayoutModifiedFromBaseline,
    solarLayoutSignature,
} from '../lib/preContractSolarDesign';
import { PreContractProductDealCard } from './PreContractProductDealCard';
import { PreContractCommerceSection } from './PreContractCommerceSection';
import { PreContractProductCombinationsSection } from './PreContractProductCombinationsSection';
import type { PreContractProductQuotePlan } from './PreContractProductCombinationsSection.types';
import { usePreContractProductGenerate } from './usePreContractProductGenerate';
import { findFirstIncompleteQuoteSyncTarget } from '../../components/Layout-Components/Editor-Layout/oneBUTTONDashboard/generateQuoteSyncGate';
import { openPartnerSyncForQuoteTarget } from '../../components/Layout-Components/Editor-Layout/oneBUTTONDashboard/openPartnerSyncFromQuoteBlock';
import { showPartnerSyncRequiredModal } from '../../components/Layout-Components/Editor-Layout/oneBUTTONDashboard/quoteValidationModals';
import { useOptionalProductsFinanceHubContext } from '../../components/Layout-Components/Editor-Layout/oneBUTTONDashboard/ProductsFinanceHubContext';
import {
    PreContractProductsSetupProvider,
    usePreContractProductsSetup,
} from './PreContractProductsSetupContext';
import {
    fetchProductPricingBundles,
    findPricingOptionForSavedPartner,
    optionToDesignRecommendation,
    pricingOptionsMatch,
    resolvePartnerSyncState,
    type PreContractDesignPartnerRecommendation,
    type PreContractProductPricingBundle,
    type PreContractProductPricingOption,
} from '../lib/preContractProductPartners';
import {
    preContractSystemSizeKw,
} from '../lib/preContractPricing';
import { normalizeLossesVendor, type ActiveLossesVendor } from '../../Constants';
import {
    lossesSourceLabel,
    usePreContractLossesPricing,
} from '../lib/usePreContractLossesPricing';
import { PreContractLossesPricingPanel } from './PreContractLossesPricingPanel';
import { resolvePreContractSolarMetrics, listSolarOneDrawDesignDetailChanges, type SolarOneDrawDesignChange } from '../lib/preContractSolarDesignMetrics';
import { productQuoteDesignBlockReason } from '../lib/preContractProductDesignReady';
import {
    buildRoofModuleLayouts,
    clonePreContractRoofModuleLayouts,
    countIncludedModules,
    estimateLayoutAnnualKwh,
    estimateTreeCount,
    preContractSelectionsFromLayouts,
    setPreContractLayoutRoofIncluded,
    togglePreContractLayoutModule,
} from '../lib/preContractModuleLayout';
import {
    markPreContractLaunch,
    markPreContractReturn,
    persistPreContractOpenDesign,
    peekPreContractReturnStep,
    type PreContractLaunchAction,
} from '../lib/preContractLaunch';
import {
    buildPreContractLivePpwQuotes,
    pickRecommendedPartnerQuote,
    averageIncludedMultiplier,
    type PreContractPpwMetricsOverride,
} from '../lib/preContractPpwPricing';
import {
    fetchPreContractSandboxPpaRates,
    formatSandboxEscalator,
    resolvePreContractEscalator,
    sandboxEscalatorPercent,
    sandboxEscalatorFraction,
    PRECONTRACT_ESCALATOR_OPTIONS,
    type PreContractSandboxPpaOffer,
} from '../lib/preContractSandboxPricing';
import { PreContractSandboxPpaRatesModal } from './PreContractSandboxPpaRatesModal';
import { savePreContractOneButtonSelection } from '../lib/preContractSaveOneButton';
import {
    extractTitleFields,
    isTitleInfoAvailable,
    loadPreContractTitleContext,
} from '../lib/preContractTitle';
import {
    opportunityHasSyncedTitleContact,
    syncedTitleContactName,
} from '../../helpers/titleContactStatus';

const PRODUCT_META: Record<
    PreContractProduct,
    { description: string; icon: React.ReactNode; accent: string }
> = {
    Solar: {
        description: 'Photovoltaic system & production modeling',
        icon: <Sun size={18} weight="duotone" />,
        accent: 'amber',
    },
    Roof: {
        description: 'Full or partial roof replacement',
        icon: <House size={18} weight="duotone" />,
        accent: 'slate',
    },
    Battery: {
        description: 'Energy storage & backup capacity',
        icon: <BatteryHigh size={18} weight="duotone" />,
        accent: 'green',
    },
    RR: {
        description: 'Remove & reinstall existing arrays',
        icon: <Wrench size={18} weight="duotone" />,
        accent: 'blue',
    },
    RRR: {
        description: 'Remove, roof, and reinstall workflow',
        icon: <TreeStructure size={18} weight="duotone" />,
        accent: 'violet',
    },
};

export type PreContractIntakeProps = {
    site: PreContractSiteContext;
    userName?: string;
};

type PanelId = 'site' | 'design' | 'title';

/** Panes in the solar layout popup's right-hand column. */
type SolarLayoutPanelTab = 'all' | 'config' | 'designs' | 'onedraw' | 'enfin' | 'saved';

function isAllDesignsTab(tab: SolarLayoutPanelTab): boolean {
    return tab === 'all' || tab === 'saved' || tab === 'onedraw' || tab === 'enfin';
}

function defaultSolarNestedLayoutTab(): SolarLayoutPanelTab {
    return 'all';
}

const PANEL_STEPS: Record<PanelId, WizardStepId> = {
    site: 'site',
    design: 'design',
    title: 'property',
};

const PANEL_LABELS: Record<PanelId, string> = {
    site: 'Site address',
    design: 'Design',
    title: 'Title',
};

function productPillLabel(product: PreContractProduct): string {
    if (product === 'RRR') return 'R&R';
    return product.toUpperCase();
}

function stepToPanel(stepId: WizardStepId): PanelId | 'utility' | 'designTool' | null {
    switch (stepId) {
        case 'site':
            return 'site';
        case 'property':
            return 'title';
        case 'design':
            return 'design';
        case 'utility':
            return 'utility';
        default:
            return null;
    }
}

const TILE_STEPS: WizardStepId[] = ['products', 'site', 'property', 'utility', 'design'];

const UTILITY_POPOVER_WIDTH = 320;
const UTILITY_POPOVER_GAP = 10;
const VIEWPORT_MARGIN = 8;

type DesignViewerDockRect = {
    left: number;
    top: number;
    width: number;
    height: number;
    /** Pixels of the docked iframe that sit under the unified header while scrolling. */
    clipTop: number;
};

/** Unified header height; the docked 3D viewport must stay visually below this band. */
function getAppUnifiedHeaderHeight(): number {
    const raw = getComputedStyle(document.documentElement)
        .getPropertyValue('--app-unified-header-height')
        .trim();
    const parsed = Number.parseFloat(raw);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 60;
}

function applyPciDockHostStyles(
    host: HTMLElement,
    next: DesignViewerDockRect,
) {
    host.style.left = `${next.left}px`;
    host.style.top = `${next.top}px`;
    host.style.width = `${next.width}px`;
    host.style.height = `${next.height}px`;
    host.style.clipPath = next.clipTop > 0 ? `inset(${next.clipTop}px 0 0 0)` : 'none';
}

/**
 * Solar popup viewport rect from the popup body grid (3fr | 2fr, row-2 viewport).
 * Do not trust #pci-section-viewport getBoundingClientRect — it can stay at the
 * global 320–440px slot height while the grid cell is much taller.
 */
function resolveSolarLayoutPopupDockRect(body: HTMLElement): DesignViewerDockRect | null {
    const details = body.querySelector('.pci-solar-layout-details');
    if (!details) return null;

    const bodyRect = body.getBoundingClientRect();
    const detailsRect = details.getBoundingClientRect();
    const bodyStyle = window.getComputedStyle(body);
    const padLeft = Number.parseFloat(bodyStyle.paddingLeft) || 0;
    const padRight = Number.parseFloat(bodyStyle.paddingRight) || 0;
    const padTop = Number.parseFloat(bodyStyle.paddingTop) || 0;
    const colGap = Number.parseFloat(bodyStyle.columnGap || bodyStyle.gap) || 0;
    const rowGap = Number.parseFloat(bodyStyle.rowGap || bodyStyle.gap) || 0;

    const contentWidth = bodyRect.width - padLeft - padRight;
    const contentHeight = bodyRect.height - padTop - (Number.parseFloat(bodyStyle.paddingBottom) || 0);
    const detailsHeight = detailsRect.height;
    const viewportWidth = Math.max(0, (contentWidth - colGap) * (3 / 5));
    const viewportHeight = Math.max(0, contentHeight - detailsHeight - rowGap);

    const left = bodyRect.left + padLeft;
    const top = bodyRect.top + padTop + detailsHeight + rowGap;

    if (viewportWidth < 8 || viewportHeight < 8) return null;

    return { left, top, width: viewportWidth, height: viewportHeight, clipTop: 0 };
}

/** Portal into `.app` so docked z-index stacks under `.app-unified-header`. */
function getPciViewportPortalRoot(): HTMLElement {
    return document.querySelector('.app') ?? document.body;
}

/** Back to a max-fill selection: an all-inactive board can never run AI. */
function includeAllModules(
    layouts: PreContractRoofModuleLayout[],
): PreContractRoofModuleLayout[] {
    return layouts.map((layout) => ({
        ...layout,
        modules: layout.modules.map((cell) => (cell ? { ...cell, included: true } : null)),
    }));
}

function ProductsSectionLoading({ message = 'Loading products…' }: { message?: string }) {
    return (
        <div className="pci-product-cards pci-product-cards--loading" role="status" aria-live="polite">
            <CircleNotch className="pci-ai-designs__spinner" size={22} weight="bold" aria-hidden />
            <span>{message}</span>
        </div>
    );
}

function countLayoutModules(layout: SavedRoofModulesLayout | null | undefined): number {
    if (!layout?.length) return 0;
    return layout.reduce((sum, roof) => sum + (roof.moduleArr?.length ?? 0), 0);
}

export const PreContractIntake: React.FC<PreContractIntakeProps> = ({ site }) => {
    const navigate = useNavigate();
    const utilityButtonRef = useRef<HTMLButtonElement>(null);
    const utilitySolarLayoutButtonRef = useRef<HTMLButtonElement>(null);
    const utilityWrapRef = useRef<HTMLDivElement>(null);
    const utilitySolarLayoutWrapRef = useRef<HTMLDivElement>(null);
    const utilityPopoverAnchorRef = useRef<HTMLButtonElement | null>(null);
    const utilityPopoverRef = useRef<HTMLDivElement>(null);
    const designToolButtonRef = useRef<HTMLButtonElement>(null);
    const designToolPopoverRef = useRef<HTMLDivElement>(null);
    const designViewerHostRef = useRef<HTMLDivElement>(null);
    const designViewerSlotRef = useRef<HTMLDivElement>(null);
    /** Solar layout popup: measure the full grid viewport, not the inner slot alone. */
    const designViewerViewportRef = useRef<HTMLDivElement>(null);
    /** Serialize scene restores and suppress ordinary selection sync until done. */
    const designApplyQueueRef = useRef<Promise<void>>(Promise.resolve());
    const pendingDesignAppliesRef = useRef(0);
    const designSwitchGenRef = useRef(0);
    /** Keep park size = last dock size so WebGL isn't resized cold on every open. */
    const lastDesignViewerDockRectRef = useRef<DesignViewerDockRect | null>(null);
    const [designViewerDocked, setDesignViewerDocked] = useState(false);

    const [form, setForm] = useState<PreContractIntakeForm>(() => {
        const saved = loadIntakeForm(site.salesForceId);
        const base = saved ?? defaultIntakeForm(site);
        return {
            ...base,
            products: clearDisabledPreContractProducts(base.products),
        };
    });
    const [ppaRateLoading, setPpaRateLoading] = useState(false);
    const [sandboxPpaOffers, setSandboxPpaOffers] = useState<PreContractSandboxPpaOffer[]>([]);
    const [selectedSandboxPpaOfferId, setSelectedSandboxPpaOfferId] = useState<string | null>(
        null,
    );
    const [ppaRatesModalOpen, setPpaRatesModalOpen] = useState(false);
    const hadSavedIntakeFormRef = useRef(Boolean(loadIntakeForm(site.salesForceId)));
    const savedSolarBaselineRef = useRef<string | null>(
        (() => {
            const saved = loadIntakeForm(site.salesForceId);
            return saved ? solarLayoutSignature(saved) : null;
        })(),
    );

    const [activePanel, setActivePanel] = useState<PanelId | null>(null);
    const [utilityPopoverOpen, setUtilityPopoverOpen] = useState(false);
    const [financeEligibilityOpen, setFinanceEligibilityOpen] = useState(false);
    const [designLossesOpen, setDesignLossesOpen] = useState(false);
    const [utilityPopoverPos, setUtilityPopoverPos] = useState<{
        top: number;
        left: number;
        placement: 'below' | 'above';
    } | null>(null);
    const [designToolPopoverOpen, setDesignToolPopoverOpen] = useState(false);
    const [designToolPopoverPos, setDesignToolPopoverPos] = useState<{
        top: number;
        left: number;
        placement: 'below' | 'above';
    } | null>(null);
    const [highlightValidation, setHighlightValidation] = useState(false);
    const [utilityFromOpportunity, setUtilityFromOpportunity] = useState(false);
    const [boardStep, setBoardStep] = useState<1 | 2 | 3 | 4>(() => peekPreContractReturnStep() ?? 1);
    const [aiDesignRunId, setAiDesignRunId] = useState(0);
    const [aiGenerationForm, setAiGenerationForm] = useState<PreContractIntakeForm | null>(null);
    const [aiDesignCacheVersion, setAiDesignCacheVersion] = useState(0);
    const preAiSnapshotRef = useRef<{
        roofModuleLayouts: PreContractIntakeForm['roofModuleLayouts'];
        roofSelections: boolean[];
        systemUsageKwh: string;
    } | null>(null);
    const solarConfigSnapshotRef = useRef<{
        roofModuleLayouts: PreContractIntakeForm['roofModuleLayouts'];
        roofSelections: boolean[];
        ghostedTreeIds: string[];
        modulesLayout: SavedRoofModulesLayout | null;
    } | null>(null);
    const [solarViewportPreviewId, setSolarViewportPreviewId] = useState<string | null>(null);
    const [solarViewportPreview, setSolarViewportPreview] = useState<{
        optionId: string;
        optionLabel: string;
        layouts: PreContractIntakeForm['roofModuleLayouts'];
        roofSelections: boolean[];
    } | null>(null);
    const solarViewportPreviewRef = useRef(solarViewportPreview);
    solarViewportPreviewRef.current = solarViewportPreview;
    /** Metrics + losses from the solar-layout popup at the moment Finalize is clicked. */
    const finalizeLayoutMetricsRef = useRef<{
        systemKw: number | null;
        annualProductionKwh: number | null;
        multiplier: number | null;
        lossesVendor: ActiveLossesVendor;
    } | null>(null);
    const [pricingPanelOpen, setPricingPanelOpen] = useState(false);
    const [treeCuttingPreviewActive, setTreeCuttingPreviewActive] = useState(false);
    const [viewportPopupOpen, setViewportPopupOpen] = useState(false);
    const [viewportFinalizeOpen, setViewportFinalizeOpen] = useState(false);
    const [finalizeModalOpen, setFinalizeModalOpen] = useState(false);
    const [finalizeConfirmOpen, setFinalizeConfirmOpen] = useState(false);
    const [solarLayoutPopupOpen, setSolarLayoutPopupOpen] = useState(false);
    const [solarLayoutPanelTab, setSolarLayoutPanelTab] =
        useState<SolarLayoutPanelTab>('all');
    const [savedOneDrawDesigns, setSavedOneDrawDesigns] = useState<DesignData[]>([]);
    const [savedOneDrawDesignsLoading, setSavedOneDrawDesignsLoading] = useState(false);
    const [selectedSavedDesignKey, setSelectedSavedDesignKey] = useState<string | null>(null);
    const [selectingSavedDesignKey, setSelectingSavedDesignKey] = useState<string | null>(null);
    const [allDesignsCard, setAllDesignsCard] = useState<'max-onedraw' | 'max-enfin' | null>(
        null,
    );
    const [selectingMaxLayout, setSelectingMaxLayout] = useState(false);
    const [savedDesignLayoutBaseline, setSavedDesignLayoutBaseline] = useState<string | null>(
        null,
    );
    const skipAiConfigMaxFillRef = useRef(false);
    const savedDesignLiveIngestRef = useRef(false);
    /** Which design is currently on the 3D viewport: saved version, AI option, or AI Config. */
    const viewportDesignSourceRef = useRef<'saved' | 'ai' | 'config' | null>(null);
    const lastViewportTabRef = useRef<SolarLayoutPanelTab | null>(null);
    const solarLayoutPopupOpenRef = useRef(solarLayoutPopupOpen);
    solarLayoutPopupOpenRef.current = solarLayoutPopupOpen;
    const savedOneDrawDesignsRef = useRef(savedOneDrawDesigns);
    savedOneDrawDesignsRef.current = savedOneDrawDesigns;
    const solarLayoutPanelTabRef = useRef(solarLayoutPanelTab);
    solarLayoutPanelTabRef.current = solarLayoutPanelTab;
    /** Last tab that owns the design, so the losses tabs don't count as leaving it. */
    const solarLayoutEditingTabRef = useRef<'config' | 'designs'>('config');
    /** Losses vendor currently previewed in the 3D viewport, if any. */
    const [solarLossesViewportVendor, setSolarLossesViewportVendor] =
        useState<ActiveLossesVendor | null>(null);
    const lossesViewportRestoreRef = useRef<ActiveLossesVendor | null>(null);
    /**
     * Puts the viewer back on the vendor it had before a losses preview started. The
     * preview writes the shared losses vendor, which finalize and quoting also read, so
     * it must never outlive the popup.
     */
    const endLossesViewportPreview = useCallback(() => {
        const restore = lossesViewportRestoreRef.current;
        lossesViewportRestoreRef.current = null;
        setSolarLossesViewportVendor(null);
        if (!restore) return false;
        applyLossesVendor(restore);
        postPciLossesVendorToEmbed(restore);
        return true;
    }, []);
    /** Put this vendor on the 3D scene without treating a second call as a toggle-off. */
    const previewLossesVendorOnViewport = useCallback((vendor: ActiveLossesVendor) => {
        if (lossesViewportRestoreRef.current == null) {
            lossesViewportRestoreRef.current = getCurrentLossesVendor();
        }
        setSolarLossesViewportVendor(vendor);
        applyLossesVendor(vendor);
        postPciLossesVendorToEmbed(vendor);
    }, []);
    const [productPickerOpen, setProductPickerOpen] = useState(false);
    const [designProductFocus, setDesignProductFocus] = useState<PreContractProduct | null>(null);
    const [designDrawerFooter, setDesignDrawerFooter] = useState<ReactNode | null>(null);
    const [roofDesignBaseline, setRoofDesignBaseline] = useState<string | null>(null);
    const [roofDesignDirty, setRoofDesignDirty] = useState(false);
    const roofSavedDirtyRef = useRef(false);
    const [confirmQuoteCellKey, setConfirmQuoteCellKey] = useState<selectedQuote | null>(null);
    const [quoteForm, setQuoteForm] = useState<QuoteFormState | null>(null);
    /** Keep oneDRAW iframe warm from page load so roof data posts before Design opens. */
    const [designViewerKept, setDesignViewerKept] = useState(() => Boolean(site.salesForceId?.trim()));
    /** Actual roofs from the embedded oneDRAW site model. */
    const [siteRoofs, setSiteRoofs] = useState<PciSiteRoof[]>([]);
    const [siteViewportError, setSiteViewportError] = useState<string | null>(null);
    const lastPostedGhostKeyRef = useRef<string>('');
    const generatePartnerBridgeRef = useRef<{
        resolveDesignPartner: (
            product: PreContractProduct,
        ) => PreContractDesignPartnerRecommendation | null;
        resolveSelectedPricingOption: (
            product: PreContractProduct,
        ) => import('../lib/preContractProductPartners').PreContractProductPricingOption | null;
        selectPricingOption: (
            product: PreContractProduct,
            option: PreContractProductPricingOption,
        ) => void;
        syncDesignPartner: (
            product: PreContractProduct,
            recommendation: PreContractDesignPartnerRecommendation,
        ) => Promise<{ ok: boolean; message?: string }>;
        openPartnerSyncOnBlock?: (
            product: PreContractProduct,
            partnerName: string,
            reason?: string,
        ) => boolean;
    } | null>(null);
    const suggestedSolarPartnerRef = useRef<string | null>(null);
    const [titleLoading, setTitleLoading] = useState(true);
    const [titleInfo, setTitleInfo] = useState<Record<string, unknown> | null>(null);
    const [titleSearchData, setTitleSearchData] = useState<unknown[]>([]);
    const [addTitleContactsModalOpen, setAddTitleContactsModalOpen] = useState(false);
    const [contactsModalOpen, setContactsModalOpen] = useState(false);
    const [solarSelectedPricingOption, setSolarSelectedPricingOption] =
        useState<PreContractProductPricingOption | null>(null);
    const [solarPricingOptions, setSolarPricingOptions] = useState<
        PreContractProductPricingOption[]
    >([]);
    const opportunityDetails = useOneButtonStore((s) => s.opportunityDetails);
    const storeOpportunityId = useOneButtonStore((s) => s.opportunityID);
    const [opportunityLoading, setOpportunityLoading] = useState(true);
    const [opportunityLoadError, setOpportunityLoadError] = useState<string | null>(null);
    /** Saved design lookup settled — gates the "nothing synced yet" callout. */
    const [savedDesignChecked, setSavedDesignChecked] = useState(false);
    /** Opportunity product flags applied (or confirmed empty) — hides the empty Add product flash. */
    const [productsPrefillReady, setProductsPrefillReady] = useState(false);
    const [utilityDetailsBaseline, setUtilityDetailsBaseline] = useState<string | null>(null);
    const utilityDetailsDirtyRef = useRef(false);
    const saveUtilityDetailsRef = useRef<() => Promise<void>>(async () => {});
    /** Scene-derived eligibility reads published by the oneDRAW embed. */
    const [embedEligibility, setEmbedEligibility] = useState<PciEligibilitySceneValues | null>(
        null,
    );
    const [financePartnerEligibility, setFinancePartnerEligibility] = useState<
        FinancePartnerEligibilitySummary[]
    >([]);
    const energyCommunityStatus = useOneButtonStore((s) => s.energyCommunityStatus);
    const priceQuoteDetails = useOneButtonStore((s) => s.priceQuoteDetails);
    const setQuotesRefreshTrigger = useOneButtonStore((s) => s.setQuotesRefreshTrigger);
    const onebuttonCapabilities = useOneButtonStore((s) => s.onebuttonCapabilities);
    const selectedLossesVendor = useViewerStore((s) => s.selectedLossesVendor);
    const systemDesignLosses = useOneButtonStore((s) => s.systemDesignLosses);
    const setSystemDesignLosses = useOneButtonStore((s) => s.setSystemDesignLosses);
    const setSystemDesign = useOneButtonStore((s) => s.setSystemDesign);
    const setOpportunityDetails = useOneButtonStore((s) => s.setOpportunityDetails);
    const systemDesign = useOneButtonStore((s) => s.systemDesign);
    const priceQuoteOverrides = useOneButtonStore((s) => s.priceQuoteOverrides);
    const theme = useOneButtonStore((s) => s.theme);
    const storeSalesForceId = useOneButtonStore((s) => s.salesForceId);
    const titleContactSynced =
        Boolean(site.salesForceId) &&
        String(storeSalesForceId ?? '') === String(site.salesForceId) &&
        opportunityHasSyncedTitleContact(opportunityDetails);
    const titleContactName = titleContactSynced ? syncedTitleContactName(opportunityDetails) : '';

    const refreshTitleContext = useCallback(async (options?: { silent?: boolean }) => {
        if (!options?.silent) setTitleLoading(true);
        try {
            const next = await loadPreContractTitleContext(site.salesForceId);
            await fetchAndStoreEnergyCommunityStatus(site.salesForceId);
            setTitleInfo(next.titleInfo);
            setTitleSearchData(next.titleSearchData);
        } finally {
            setTitleLoading(false);
        }
    }, [site.salesForceId]);

    useEffect(() => {
        let cancelled = false;
        void (async () => {
            setTitleLoading(true);
            const next = await loadPreContractTitleContext(site.salesForceId);
            await fetchAndStoreEnergyCommunityStatus(site.salesForceId);
            if (cancelled) return;
            setTitleInfo(next.titleInfo);
            setTitleSearchData(next.titleSearchData);
            setTitleLoading(false);
        })();
        return () => {
            cancelled = true;
        };
    }, [site.salesForceId]);

    useEffect(() => {
        let cancelled = false;
        setProductsPrefillReady(false);

        void (async () => {
            await ensurePreContractOpportunityHydrated(site.salesForceId);
            if (cancelled) return;

            const hydratedStore = useOneButtonStore.getState();
            const utilityPrefill = resolveUtilityPrefillFromStore(
                hydratedStore.solarUtilityData,
            );
            const productPrefill = resolveProductPrefillFromOpportunity(
                hydratedStore.opportunityDetails,
            );

            setForm((prev) => {
                let next = prev;

                if (utilityPrefill && hasUtilityPrefill(utilityPrefill)) {
                    const utilityPatch = mergeUtilityPrefill(next, utilityPrefill);
                    if (Object.keys(utilityPatch).length > 0) {
                        if (!cancelled) setUtilityFromOpportunity(true);
                        next = { ...next, ...utilityPatch };
                    }
                }

                if (
                    productPrefill &&
                    hasProductPrefill(productPrefill)
                ) {
                    const productPatch = mergeProductPrefill(
                        next,
                        productPrefill,
                        { replacePrimaryProducts: !hadSavedIntakeFormRef.current },
                    );
                    if (Object.keys(productPatch).length > 0) {
                        next = { ...next, ...productPatch };
                    }
                }

                return next;
            });

            if (!cancelled) setProductsPrefillReady(true);

            const selectedProducts = productPrefill && hasProductPrefill(productPrefill);
            if (!selectedProducts) return;

            const design =
                useOneButtonStore.getState().systemDesign ??
                (await ensureSystemDesignForPrefill(site.salesForceId));
            if (cancelled || !design) return;

            setForm((prev) => {
                const batteryPatch = mergeBatteryPrefillFromSystemDesign(prev, design);
                if (Object.keys(batteryPatch).length === 0) return prev;
                return { ...prev, ...batteryPatch };
            });
        })();

        return () => {
            cancelled = true;
        };
    }, [site.salesForceId]);

    const roofDesignDrawerOpen =
        activePanel === 'design' &&
        form.products.Roof &&
        designProductFocus !== 'Battery';

    useEffect(() => {
        if (!roofDesignDrawerOpen) {
            setDesignDrawerFooter(null);
        }
    }, [roofDesignDrawerOpen]);

    useEffect(() => {
        if (!roofDesignDrawerOpen) return undefined;

        let cancelled = false;
        void (async () => {
            await ensurePreContractOpportunityHydrated(site.salesForceId);
            const oppId = resolveStoreOpportunityId();
            if (cancelled || !oppId) return;

            if (useOneButtonStore.getState().systemDesign) return;

            try {
                const design = await designServices.RetrieveSystemDesign(oppId);
                if (!cancelled && design) setSystemDesign(design);
            } catch {
                /* roof save still works once design loads elsewhere */
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [roofDesignDrawerOpen, site.salesForceId, setSystemDesign]);

    const hydrateOpportunityForIntake = useCallback(async (): Promise<boolean> => {
        setOpportunityLoading(true);
        setOpportunityLoadError(null);
        useOneButtonStore.getState().setSalesForceId(site.salesForceId);
        const existing = resolveStoreOpportunityId();
        if (existing) {
            setOpportunityLoading(false);
            return true;
        }
        const hydrated = await ensurePreContractOpportunityHydrated(site.salesForceId);
        const oppId = resolveStoreOpportunityId();
        setOpportunityLoading(false);
        if (!hydrated || !oppId) {
            setOpportunityLoadError(OPPORTUNITY_NOT_LOADED_MESSAGE);
            return false;
        }
        return true;
    }, [site.salesForceId]);

    useEffect(() => {
        void hydrateOpportunityForIntake();
    }, [hydrateOpportunityForIntake]);

    const reloadOpportunityDesign = useCallback(async () => {
        const sfId = site.salesForceId.trim();
        const oppId = resolveStoreOpportunityId() ?? storeOpportunityId;
        if (!sfId && !oppId) return;
        try {
            if (sfId) {
                const refreshed = await opportunityInfoService.getOpportunityDetail(sfId);
                const detailData = refreshed?.data ?? refreshed;
                if (detailData) setOpportunityDetails(detailData);
            }
        } catch {
            /* keep the current opportunity if refresh fails */
        }
        if (!oppId) return;
        try {
            const design = await designServices.RetrieveSystemDesign(String(oppId));
            if (design) setSystemDesign(design);
        } catch {
            /* card metrics stay on the last known design */
        }
    }, [setOpportunityDetails, setSystemDesign, site.salesForceId, storeOpportunityId]);

    /** The light hydration path skips the design bundle, so fetch losses on their own. */
    useEffect(() => {
        if (systemDesignLosses) return undefined;
        const oppId = resolveStoreOpportunityId() ?? storeOpportunityId;
        if (!isOpportunityReady(oppId)) return undefined;

        let cancelled = false;
        void (async () => {
            try {
                const losses = await designServices.SystemDesignLosses(String(oppId));
                if (!cancelled && losses) setSystemDesignLosses(losses);
            } catch {
                /* the header badge stays hidden until losses are available */
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [setSystemDesignLosses, storeOpportunityId, systemDesignLosses]);

    useEffect(() => {
        if (isOpportunityReady(resolveStoreOpportunityId() ?? storeOpportunityId)) {
            setOpportunityLoadError(null);
            setOpportunityLoading(false);
        }
    }, [storeOpportunityId, opportunityDetails]);

    useEffect(() => {
        if (!form.products.Roof && !form.products.Solar && !form.products.Battery) {
            setSavedDesignChecked(true);
            return undefined;
        }

        let cancelled = false;
        void (async () => {
            const design = await ensureSystemDesignForPrefill(site.salesForceId);
            if (cancelled) return;
            setSavedDesignChecked(true);
            if (!design) return;

            setForm((prev) => {
                const batteryPatch = mergeBatteryPrefillFromSystemDesign(prev, design);
                if (Object.keys(batteryPatch).length === 0) return prev;
                return { ...prev, ...batteryPatch };
            });
        })();

        return () => {
            cancelled = true;
        };
    }, [form.products.Battery, form.products.Roof, form.products.Solar, site.salesForceId]);

    useEffect(() => {
        persistIntakeForm(site.salesForceId, form);
    }, [form, site.salesForceId]);

    useEffect(() => {
        if (!viewportPopupOpen) return undefined;
        const onKey = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                setViewportPopupOpen(false);
            }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [viewportPopupOpen]);

    useEffect(() => {
        if (!solarLayoutPopupOpen) return undefined;
        const onKey = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                setSolarLayoutPopupOpen(false);
            }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [solarLayoutPopupOpen]);

    useEffect(() => {
        if (!productPickerOpen) return undefined;
        const onKey = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                setProductPickerOpen(false);
            }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [productPickerOpen]);

    useEffect(() => {
        if (!form.products.Solar) {
            setSolarLayoutPopupOpen(false);
        }
    }, [form.products.Solar]);

    const auroraSiteViewport = useMemo(
        () => ({
            siteViewportError,
            siteRoofCount: siteRoofs.length,
        }),
        [siteRoofs.length, siteViewportError],
    );
    const auroraSiteDesignLoaded = isAuroraSiteDesignLoaded(auroraSiteViewport);
    const solarProductBlockReason = getSolarProductBlockReason(form, auroraSiteViewport);
    const solarProductAllowed = canAddSolarProduct(form, auroraSiteViewport);

    useEffect(() => {
        setForm((prev) => {
            const hasLayoutsWithoutSiteRoofs =
                siteRoofs.length === 0 &&
                prev.roofModuleLayouts.some((layout) => layout.modules.some((cell) => cell != null));
            const auroraUnavailable = Boolean(siteViewportError?.trim());
            const nextProducts =
                auroraUnavailable && prev.products.Solar
                    ? { ...prev.products, Solar: false }
                    : prev.products;
            if (
                !hasLayoutsWithoutSiteRoofs &&
                nextProducts === prev.products
            ) {
                return prev;
            }
            return {
                ...prev,
                products: nextProducts,
                roofModuleLayouts: hasLayoutsWithoutSiteRoofs ? [] : prev.roofModuleLayouts,
                roofSelections: hasLayoutsWithoutSiteRoofs ? [] : prev.roofSelections,
            };
        });
        if (siteViewportError?.trim()) {
            setSolarLayoutPopupOpen(false);
        }
    }, [siteRoofs.length, siteViewportError]);

    const updateUtilityPopoverPosition = useCallback(() => {
        const button =
            utilityPopoverAnchorRef.current ??
            utilitySolarLayoutButtonRef.current ??
            utilityButtonRef.current;
        if (!button) return;

        const buttonRect = button.getBoundingClientRect();
        const popoverWidth = utilityPopoverRef.current?.offsetWidth ?? UTILITY_POPOVER_WIDTH;
        const popoverHeight = utilityPopoverRef.current?.offsetHeight ?? 289;

        let left = buttonRect.left + buttonRect.width / 2 - popoverWidth / 2;
        const maxLeft = window.innerWidth - popoverWidth - VIEWPORT_MARGIN;
        left = Math.max(VIEWPORT_MARGIN, Math.min(left, maxLeft));

        const spaceBelow = window.innerHeight - buttonRect.bottom - UTILITY_POPOVER_GAP - VIEWPORT_MARGIN;
        const spaceAbove = buttonRect.top - UTILITY_POPOVER_GAP - VIEWPORT_MARGIN;
        const placement: 'below' | 'above' =
            spaceBelow >= popoverHeight || spaceBelow >= spaceAbove ? 'below' : 'above';

        let top =
            placement === 'below'
                ? buttonRect.bottom + UTILITY_POPOVER_GAP
                : buttonRect.top - popoverHeight - UTILITY_POPOVER_GAP;

        const maxTop = window.innerHeight - popoverHeight - VIEWPORT_MARGIN;
        top = Math.max(VIEWPORT_MARGIN, Math.min(top, maxTop));

        setUtilityPopoverPos({ top, left, placement });
    }, []);

    const updateDesignToolPopoverPosition = useCallback(() => {
        const button = designToolButtonRef.current;
        if (!button) return;

        const buttonRect = button.getBoundingClientRect();
        const popoverWidth = designToolPopoverRef.current?.offsetWidth ?? UTILITY_POPOVER_WIDTH;
        const popoverHeight = designToolPopoverRef.current?.offsetHeight ?? 220;

        let left = buttonRect.left + buttonRect.width / 2 - popoverWidth / 2;
        const maxLeft = window.innerWidth - popoverWidth - VIEWPORT_MARGIN;
        left = Math.max(VIEWPORT_MARGIN, Math.min(left, maxLeft));

        const spaceBelow = window.innerHeight - buttonRect.bottom - UTILITY_POPOVER_GAP - VIEWPORT_MARGIN;
        const spaceAbove = buttonRect.top - UTILITY_POPOVER_GAP - VIEWPORT_MARGIN;
        const placement: 'below' | 'above' =
            spaceBelow >= popoverHeight || spaceBelow >= spaceAbove ? 'below' : 'above';

        let top =
            placement === 'below'
                ? buttonRect.bottom + UTILITY_POPOVER_GAP
                : buttonRect.top - popoverHeight - UTILITY_POPOVER_GAP;

        const maxTop = window.innerHeight - popoverHeight - VIEWPORT_MARGIN;
        top = Math.max(VIEWPORT_MARGIN, Math.min(top, maxTop));

        setDesignToolPopoverPos({ top, left, placement });
    }, []);

    useLayoutEffect(() => {
        if (!utilityPopoverOpen) {
            setUtilityPopoverPos(null);
            return;
        }
        updateUtilityPopoverPosition();
        const frame = requestAnimationFrame(() => updateUtilityPopoverPosition());
        return () => cancelAnimationFrame(frame);
    }, [utilityPopoverOpen, updateUtilityPopoverPosition]);

    useLayoutEffect(() => {
        if (!designToolPopoverOpen) {
            setDesignToolPopoverPos(null);
            return;
        }
        updateDesignToolPopoverPosition();
        const frame = requestAnimationFrame(() => updateDesignToolPopoverPosition());
        return () => cancelAnimationFrame(frame);
    }, [designToolPopoverOpen, updateDesignToolPopoverPosition]);

    useEffect(() => {
        if (!utilityPopoverOpen) return undefined;

        const onDocClick = (event: MouseEvent) => {
            const target = event.target as Node | null;
            if (!target) return;
            if (utilityWrapRef.current?.contains(target)) return;
            if (utilitySolarLayoutWrapRef.current?.contains(target)) return;
            if (utilityPopoverRef.current?.contains(target)) return;
            setUtilityPopoverOpen(false);
        };
        const onReflow = () => updateUtilityPopoverPosition();

        const attachListenerTimer = window.setTimeout(() => {
            document.addEventListener('click', onDocClick, true);
        }, 0);
        window.addEventListener('resize', onReflow);
        window.addEventListener('scroll', onReflow, true);

        return () => {
            window.clearTimeout(attachListenerTimer);
            document.removeEventListener('click', onDocClick, true);
            window.removeEventListener('resize', onReflow);
            window.removeEventListener('scroll', onReflow, true);
        };
    }, [utilityPopoverOpen, updateUtilityPopoverPosition]);

    useEffect(() => {
        if (!designToolPopoverOpen) return undefined;

        const onDocClick = (event: MouseEvent) => {
            const target = event.target as Node | null;
            if (!target) return;
            if (designToolButtonRef.current?.contains(target)) return;
            if (designToolPopoverRef.current?.contains(target)) return;
            setDesignToolPopoverOpen(false);
        };
        const onReflow = () => updateDesignToolPopoverPosition();

        document.addEventListener('mousedown', onDocClick);
        window.addEventListener('resize', onReflow);
        window.addEventListener('scroll', onReflow, true);

        return () => {
            document.removeEventListener('mousedown', onDocClick);
            window.removeEventListener('resize', onReflow);
            window.removeEventListener('scroll', onReflow, true);
        };
    }, [designToolPopoverOpen, updateDesignToolPopoverPosition]);

    useEffect(() => {
        if (!activePanel) return undefined;
        const prev = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => {
            document.body.style.overflow = prev;
        };
    }, [activePanel]);

    useEffect(() => {
        if (site.salesForceId?.trim()) {
            setDesignViewerKept(true);
        }
    }, [site.salesForceId]);

    const syncSelectionToEmbed = useCallback(() => {
        if (pendingDesignAppliesRef.current > 0) return;
        const preview = solarViewportPreviewRef.current;
        if (preview) {
            if (siteRoofs.length) {
                const includedTagIds = siteRoofs
                    .filter((_, index) => preview.roofSelections[index] !== false)
                    .map((roof) => roof.roofTagId);
                postPciRoofSelectionToEmbed(includedTagIds);
            }
            postPciModuleSelectionToEmbed(collectIncludedModuleIds(preview.layouts));
            return;
        }
        const configMaxFill =
            solarLayoutPopupOpen && solarLayoutPanelTab === 'config'
                ? expandLayoutsToMaxFill(form.roofModuleLayouts, loadPreContractFullModuleLayouts(), {
                      includeGreyPanels: form.includeGreyPanels,
                      includePurplePanels: form.includePurplePanels,
                  })
                : null;
        const layouts = configMaxFill ?? form.roofModuleLayouts;
        const roofSelections = configMaxFill
            ? preContractSelectionsFromLayouts(configMaxFill)
            : form.roofSelections;
        if (siteRoofs.length) {
            const includedTagIds = siteRoofs
                .filter((_, index) => roofSelections[index] !== false)
                .map((r) => r.roofTagId);
            postPciRoofSelectionToEmbed(includedTagIds);
        }
        const includedModuleIds = collectIncludedModuleIds(layouts);
        const hasLiveModules = layouts.some((layout) =>
            layout.modules.some((cell) => cell != null),
        );
        // Catalog ids are stale until the live max-fill republish lands. Posting them
        // would hide the modules we just placed on empty roofs.
        const formHasFullFill =
            !configMaxFill ||
            countPresentModuleCells(form.roofModuleLayouts) >=
                countPresentModuleCells(configMaxFill);
        if (hasLiveModules && formHasFullFill) {
            postPciModuleSelectionToEmbed(includedModuleIds);
        }
        if (form.siteTrees.length > 0) {
            // Solar layout popup opens at board step 1 — still sync ghosts to the embed.
            const persistTreeGhosts = boardStep >= 2 || solarLayoutPopupOpen;
            const ghostIds = persistTreeGhosts
                ? form.siteTrees.filter((tree) => tree.ghosted).map((tree) => tree.id)
                : [];
            const ghostKey = persistTreeGhosts
                ? pciGhostedTreeIdKey(form.siteTrees)
                : '__pci-no-tree-preview__';
            if (ghostKey !== lastPostedGhostKeyRef.current) {
                lastPostedGhostKeyRef.current = ghostKey;
                postPciTreeGhostToEmbed(ghostIds);
            }
        }
    }, [
        siteRoofs,
        form.includeGreyPanels,
        form.includePurplePanels,
        form.roofSelections,
        form.roofModuleLayouts,
        form.siteTrees,
        boardStep,
        solarLayoutPanelTab,
        solarLayoutPopupOpen,
    ]);
    const syncSelectionToEmbedRef = useRef(syncSelectionToEmbed);
    syncSelectionToEmbedRef.current = syncSelectionToEmbed;

    const commitSolarConfigSnapshot = useCallback((source: PreContractIntakeForm) => {
        const snapshot = {
            roofModuleLayouts: clonePreContractRoofModuleLayouts(source.roofModuleLayouts),
            roofSelections: [...source.roofSelections],
            ghostedTreeIds: source.siteTrees
                .filter((tree) => tree.ghosted)
                .map((tree) => String(tree.id).trim())
                .filter(Boolean),
            modulesLayout: null as SavedRoofModulesLayout | null,
        };
        solarConfigSnapshotRef.current = snapshot;
        const includedModuleIds = collectIncludedModuleIds(snapshot.roofModuleLayouts);
        if (includedModuleIds.length === 0) return;
        void requestPciModulesLayoutFromEmbed(includedModuleIds).then((layout) => {
            if (solarConfigSnapshotRef.current === snapshot && layout?.length) {
                snapshot.modulesLayout = layout;
            }
        });
    }, []);

    const ensureSolarConfigSnapshotEmbedLayout = useCallback(async () => {
        const snapshot = solarConfigSnapshotRef.current;
        if (!snapshot || snapshot.modulesLayout?.length) return;
        const includedModuleIds = collectIncludedModuleIds(snapshot.roofModuleLayouts);
        if (includedModuleIds.length === 0) return;
        const layout = await requestPciModulesLayoutFromEmbed(includedModuleIds);
        if (layout?.length && solarConfigSnapshotRef.current === snapshot) {
            snapshot.modulesLayout = layout;
        }
    }, []);

    const restoreSolarConfigViewport = useCallback(() => {
        const run = async () => {
            const snapshot = solarConfigSnapshotRef.current;
            if (!snapshot) {
                syncSelectionToEmbedRef.current();
                return;
            }
            if (!snapshot.modulesLayout?.length) {
                await ensureSolarConfigSnapshotEmbedLayout();
            }
            const includedModuleIds = collectIncludedModuleIds(snapshot.roofModuleLayouts);
            pendingDesignAppliesRef.current += 1;
            try {
                if (includedModuleIds.length > 0) {
                    await requestPciApplyDesignOptionToEmbed({
                        includedModuleIds,
                        modulesLayout: snapshot.modulesLayout ?? null,
                        ghostedTreeIds: snapshot.ghostedTreeIds,
                    });
                }
                if (siteRoofs.length) {
                    const includedTagIds = siteRoofs
                        .filter((_, index) => snapshot.roofSelections[index] !== false)
                        .map((roof) => roof.roofTagId);
                    postPciRoofSelectionToEmbed(includedTagIds);
                }
                if (includedModuleIds.length > 0) {
                    postPciModuleSelectionToEmbed(includedModuleIds);
                }
            } finally {
                pendingDesignAppliesRef.current = Math.max(
                    0,
                    pendingDesignAppliesRef.current - 1,
                );
                if (pendingDesignAppliesRef.current === 0) {
                    syncSelectionToEmbedRef.current();
                }
            }
        };
        const queued = designApplyQueueRef.current.then(run, run);
        designApplyQueueRef.current = queued;
        return queued;
    }, [ensureSolarConfigSnapshotEmbedLayout, siteRoofs]);

    const clearSolarViewportPreview = useCallback(() => {
        setSolarViewportPreviewId(null);
        setSolarViewportPreview(null);
        solarViewportPreviewRef.current = null;
        restoreSolarConfigViewport();
    }, [restoreSolarConfigViewport]);

    useEffect(() => {
        if (solarLayoutPanelTab === 'config' || solarLayoutPanelTab === 'designs') {
            solarLayoutEditingTabRef.current = solarLayoutPanelTab;
        }
    }, [solarLayoutPanelTab]);

    const handleTreeGhostChange = useCallback((siteTrees: PreContractSiteTree[]) => {
        const ghostIds = siteTrees.filter((tree) => tree.ghosted).map((tree) => tree.id);
        lastPostedGhostKeyRef.current = pciGhostedTreeIdKey(siteTrees);
        postPciTreeGhostToEmbed(ghostIds);
    }, []);

    const scrollToSection = useCallback((sectionId: string) => {
        document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, []);

    const moduleViewportEligible =
        designViewerKept &&
        hasActiveDesignTool(form) &&
        ((form.preferredDesignTool === 'aurora' && form.auroraAvailable) ||
            (form.preferredDesignTool === 'hover' && form.hoverAvailable));
    const solarLayoutPopupActive =
        hasActiveDesignTool(form) &&
        form.products.Solar &&
        solarLayoutPopupOpen &&
        auroraSiteDesignLoaded;
    const moduleViewportActive = moduleViewportEligible && solarLayoutPopupActive;
    const viewportPopupActive = designViewerKept && viewportPopupOpen;
    /** Exactly one DOM slot owns the shared iframe at a time (popup wins over inline module). */
    const designViewerSlotMode: 'popup' | 'module' | null = viewportPopupActive
        ? 'popup'
        : moduleViewportActive
          ? 'module'
          : null;
    const viewportDockActive = designViewerSlotMode != null;
    /** Float above Design drawer; otherwise sit inside the stack/popup slot. */
    const dockHostElevated = viewportDockActive && activePanel === 'design' && !viewportPopupActive;

    /**
     * Keep a single iframe instance for the whole visit.
     * Its portal parent never changes; only fixed positioning moves it over the
     * active module/popup slot. Changing a portal container remounts the iframe
     * and would create another oneDRAW/WebGL instance.
     */
    useLayoutEffect(() => {
        if (!viewportDockActive) {
            setDesignViewerDocked(false);
            return undefined;
        }

        let cancelled = false;
        let slotWaitRaf = 0;
        let scrollRaf = 0;
        let resizeTimer = 0;
        let observedSlot: HTMLElement | null = null;
        let observer: ResizeObserver | null = null;
        let lastPostedSize = '';

        const applyDockStyles = (next: DesignViewerDockRect) => {
            const host = designViewerHostRef.current;
            if (!host) return;
            applyPciDockHostStyles(host, next);
        };

        const nudgeEmbedResize = (next: DesignViewerDockRect, force = false) => {
            const sizeKey = `${Math.round(next.width)}x${Math.round(next.height)}`;
            if (!force && sizeKey === lastPostedSize) return;
            lastPostedSize = sizeKey;
            window.clearTimeout(resizeTimer);
            resizeTimer = window.setTimeout(() => {
                postPciViewportResizeToEmbed();
                if (viewportPopupActive) {
                    postPci3dViewToEmbed();
                }
                syncSelectionToEmbedRef.current();
            }, 50);
        };

        const updateDock = () => {
            if (cancelled) return;
            const slot = designViewerSlotRef.current;
            const dockTarget =
                solarLayoutPopupOpen && designViewerViewportRef.current
                    ? designViewerViewportRef.current
                    : slot;
            if (!dockTarget) {
                slotWaitRaf = window.requestAnimationFrame(updateDock);
                return;
            }
            if (observer && observedSlot !== dockTarget) {
                if (observedSlot) observer.unobserve(observedSlot);
                observer.observe(dockTarget);
                observedSlot = dockTarget;
            }

            const measuredRect = dockTarget.getBoundingClientRect();
            if (measuredRect.width < 8 || measuredRect.height < 8) {
                slotWaitRaf = window.requestAnimationFrame(updateDock);
                return;
            }
            const popupBodyEl = document.querySelector('.pci-solar-layout-popup__body');
            const solarPopupRect =
                solarLayoutPopupOpen && popupBodyEl instanceof HTMLElement
                    ? resolveSolarLayoutPopupDockRect(popupBodyEl)
                    : null;
            const rect = solarPopupRect ?? measuredRect;
            const headerH = getAppUnifiedHeaderHeight();
            let left = rect.left;
            let width = rect.width;
            // Keep the docked canvas out from under the pricing sidepanel (desktop only).
            if (pricingPanelOpen && activePanel === 'design' && !viewportPopupActive) {
                const panel = document.querySelector('.pci-pricing-sidepanel');
                if (panel) {
                    const panelRect = panel.getBoundingClientRect();
                    const isBottomSheet =
                        panelRect.width >= window.innerWidth * 0.9 &&
                        panelRect.top > headerH + 40;
                    if (!isBottomSheet) {
                        const panelLeft = panelRect.left;
                        if (Number.isFinite(panelLeft) && panelLeft < rect.left + rect.width) {
                            width = Math.max(8, panelLeft - rect.left - 12);
                        }
                    }
                }
            }
            const next: DesignViewerDockRect = {
                left,
                top: rect.top,
                width,
                height: rect.height,
                clipTop:
                    activePanel === 'design' && !viewportPopupActive
                        ? Math.max(0, headerH - rect.top)
                        : 0,
            };
            lastDesignViewerDockRectRef.current = next;
            applyDockStyles(next);
            setDesignViewerDocked(true);
            nudgeEmbedResize(next, Boolean(solarPopupRect));
        };

        const onScrollOrResize = () => {
            if (scrollRaf) return;
            scrollRaf = window.requestAnimationFrame(() => {
                scrollRaf = 0;
                updateDock();
            });
        };

        observer =
            typeof ResizeObserver !== 'undefined'
                ? new ResizeObserver(() => onScrollOrResize())
                : null;

        updateDock();
        const settleTimers = [50, 120, 240, 400, 700, 1000, 1500, 2500, 4000].map((ms) =>
            window.setTimeout(updateDock, ms),
        );

        const popupDialog = document.querySelector('.pci-solar-layout-popup__dialog');
        const popupBody = document.querySelector('.pci-solar-layout-popup__body');
        if (popupDialog && observer) {
            observer.observe(popupDialog);
        }
        if (popupBody && observer) {
            observer.observe(popupBody);
        }

        window.addEventListener('resize', onScrollOrResize);
        window.addEventListener('scroll', onScrollOrResize, true);
        return () => {
            cancelled = true;
            window.cancelAnimationFrame(slotWaitRaf);
            window.cancelAnimationFrame(scrollRaf);
            window.clearTimeout(resizeTimer);
            settleTimers.forEach((id) => window.clearTimeout(id));
            observer?.disconnect();
            window.removeEventListener('resize', onScrollOrResize);
            window.removeEventListener('scroll', onScrollOrResize, true);
            const host = designViewerHostRef.current;
            if (host) {
                host.style.left = '';
                host.style.top = '';
                host.style.width = '';
                host.style.height = '';
                host.style.clipPath = '';
            }
            setDesignViewerDocked(false);
        };
    }, [
        activePanel,
        boardStep,
        viewportDockActive,
        viewportPopupActive,
        pricingPanelOpen,
        solarLayoutPopupOpen,
    ]);

    useLayoutEffect(() => {
        if (!designViewerDocked) return;
        const next = lastDesignViewerDockRectRef.current;
        const host = designViewerHostRef.current;
        if (!next || !host) return;
        applyPciDockHostStyles(host, next);
    });

    useEffect(() => {
        const onMessage = (event: MessageEvent) => {
            if (event.origin !== window.location.origin) return;
            const data = event.data as {
                type?: string;
                url?: string;
                message?: string;
                roofs?: PciSiteRoof[];
                layouts?: PreContractRoofModuleLayout[];
                trees?: PciSiteTree[];
                eligibility?: PciEligibilitySceneValues;
                lossesVendor?: string;
                shadeReady?: boolean;
            } | null;
            if (!data?.type) return;

            if (data.type === 'pci-onedraw-layout-snapshot') {
                return;
            }

            if (data.type === PCI_ONEDRAW_LOAD_ERROR_MSG) {
                const message = data.message?.trim() || DEFAULT_SITE_VIEWER_MESSAGE;
                setSiteViewportError(message);
                return;
            }

            if (data.type === PCI_ONEDRAW_ELIGIBILITY_MSG && data.eligibility) {
                const next = data.eligibility;
                setEmbedEligibility((prev) =>
                    prev && JSON.stringify(prev) === JSON.stringify(next) ? prev : next,
                );
                return;
            }

            if (data.type === PCI_ONEDRAW_ROOFS_MSG && Array.isArray(data.roofs)) {
                const roofs = data.roofs.filter(
                    (r) => r && typeof r.roofTagId === 'number' && Number.isFinite(r.roofTagId),
                );
                if (!roofs.length) return;
                setSiteViewportError(null);
                // Anchor for the losses ratio: the vendor the published production was
                // solved under, which lags our selection until the embed re-runs shade.
                const solvedVendor = data.lossesVendor;
                // Live kWh stay attributed to the previous vendor until this shade pass
                // finishes for the vendor we actually switched to.
                if (solvedVendor && data.shadeReady) {
                    const store = useViewerStore.getState();
                    const pending = store.pciProductionLossesPending
                        ? normalizeLossesVendor(store.pciProductionLossesPending)
                        : null;
                    const published = normalizeLossesVendor(solvedVendor);
                    if (pending) {
                        if (published === pending) {
                            useViewerStore.setState({
                                pciProductionLossesVendor: published,
                                pciProductionLossesPending: null,
                            });
                        }
                    } else if (store.pciProductionLossesVendor !== published) {
                        useViewerStore.setState({ pciProductionLossesVendor: published });
                    }
                }
                const publishedLayouts = Array.isArray(data.layouts)
                    ? (data.layouts as PreContractRoofModuleLayout[]).filter(
                          (layout) =>
                              layout &&
                              typeof layout.cols === 'number' &&
                              Array.isArray(layout.modules),
                      )
                    : [];
                // Roofs without panels stay on the board so they can still be toggled on,
                // but a publish with no panels anywhere means the viewer has not placed
                // them yet and must not replace a board that already has them.
                const liveLayouts = publishedLayouts.some((layout) =>
                    layout.modules.some((cell) => cell != null),
                )
                    ? publishedLayouts
                    : [];
                if (liveLayouts.length > 0) {
                    persistPreContractFullModuleLayouts(liveLayouts, {
                        force:
                            solarLayoutPopupOpenRef.current &&
                            solarLayoutPanelTabRef.current === 'config',
                    });
                }
                setSiteRoofs((prevRoofs) =>
                    pciSiteRoofsEqual(prevRoofs, roofs) ? prevRoofs : roofs,
                );
                // A design apply restores the full fill before trimming to the selection.
                // Ingesting that intermediate scene would reset the board to every panel.
                if (pendingDesignAppliesRef.current > 0) return;
                const ingestingSavedDesign = savedDesignLiveIngestRef.current;
                const keepAppliedDesign =
                    solarLayoutPopupOpenRef.current &&
                    solarLayoutPanelTabRef.current === 'config' &&
                    !ingestingSavedDesign;
                setForm((prev) => {
                    const nextSelections = keepAppliedDesign
                        ? prev.roofSelections
                        : roofs.map((_, index) => prev.roofSelections[index] !== false);
                    const hasPrevLive = prev.roofModuleLayouts.some((layout) =>
                        layout.modules.some((cell) => cell != null),
                    );
                    const roofModuleLayouts =
                        ingestingSavedDesign && liveLayouts.length > 0
                            ? applyRoofSelectionsToLayouts(liveLayouts, nextSelections)
                            : keepAppliedDesign
                              ? prev.roofModuleLayouts
                              : liveLayouts.length > 0
                                ? mergeLiveModuleLayouts(
                                      prev.roofModuleLayouts,
                                      liveLayouts,
                                      nextSelections,
                                  )
                                : hasPrevLive
                                  ? applyRoofSelectionsToLayouts(
                                        prev.roofModuleLayouts,
                                        nextSelections,
                                    )
                                  : prev.roofModuleLayouts;
                    if (ingestingSavedDesign && liveLayouts.length > 0) {
                        savedDesignLiveIngestRef.current = false;
                        const baseline = solarLayoutSignature({
                            ...prev,
                            roofModuleLayouts,
                            roofSelections: nextSelections,
                        });
                        savedSolarBaselineRef.current = baseline;
                        setSavedDesignLayoutBaseline(baseline);
                    }
                    const incomingTrees = Array.isArray(data.trees) ? data.trees : [];
                    const mergedTrees = mergePciSiteTrees(prev.siteTrees, incomingTrees);
                    const siteTrees = pciSiteTreesLookEqual(prev.siteTrees, mergedTrees)
                        ? prev.siteTrees
                        : mergedTrees;
                    const selectionsUnchanged = nextSelections.every(
                        (selected, index) => prev.roofSelections[index] !== false === selected,
                    );
                    if (
                        selectionsUnchanged &&
                        pciRoofModuleLayoutsEqual(prev.roofModuleLayouts, roofModuleLayouts) &&
                        siteTrees === prev.siteTrees
                    ) {
                        return prev;
                    }
                    return {
                        ...prev,
                        roofSelections: nextSelections,
                        roofModuleLayouts,
                        siteTrees,
                        treeCount: siteTrees.length || prev.treeCount,
                    };
                });
            }
        };
        window.addEventListener('message', onMessage);
        return () => window.removeEventListener('message', onMessage);
    }, []);

    useEffect(() => {
        syncSelectionToEmbed();
    }, [syncSelectionToEmbed]);

    /** Keep Design-drawer slope totals in sync with viewer Roof Square Table + selected roofs. */
    useEffect(() => {
        const squares = sumPciSelectedRoofSquares(siteRoofs, form.roofSelections);
        if (squares.total <= 0) return;
        setForm((prev) => {
            if (
                prev.roofSlopeLowTotal === squares.low &&
                prev.roofSlopeNormalTotal === squares.standard
            ) {
                return prev;
            }
            return {
                ...prev,
                roofSlopeLowTotal: squares.low,
                roofSlopeNormalTotal: squares.standard,
            };
        });
    }, [siteRoofs, form.roofSelections]);

    useEffect(() => {
        if (!form.products.Roof) return;
        setForm((prev) => {
            const snapshot = buildPreContractRoofDesignSnapshot({
                form: prev,
                systemDesign,
                priceQuoteOverrides,
            });
            const patch = patchFormFromRoofDesignSnapshot(
                prev,
                snapshot,
                priceQuoteOverrides,
                systemDesign,
            );
            if (!patch) return prev;
            return { ...prev, ...patch };
        });
    }, [form.products.Roof, priceQuoteOverrides, systemDesign]);

    useEffect(() => {
        setForm((prev) => {
            if (prev.preferredDesignTool === 'aurora' && !prev.auroraAvailable) {
                return { ...prev, preferredDesignTool: null };
            }
            if (prev.preferredDesignTool === 'hover' && !prev.hoverAvailable) {
                return { ...prev, preferredDesignTool: null };
            }
            return prev;
        });
    }, [form.auroraAvailable, form.hoverAvailable]);

    const patchForm = useCallback((patch: Partial<PreContractIntakeForm>) => {
        setForm((prev) => ({ ...prev, ...patch }));
    }, []);

    const applySandboxPpaOffer = useCallback(
        (offer: PreContractSandboxPpaOffer) => {
            setSelectedSandboxPpaOfferId(offer.id);
            patchForm({
                targetMinPpaRate: String(offer.startingRate),
                targetEscalator: String(sandboxEscalatorFraction(offer.escalator)),
            });
        },
        [patchForm],
    );

    const openPpaRatesModal = useCallback(() => {
        setPpaRatesModalOpen(true);
    }, []);

    const closePpaRatesModal = useCallback(() => {
        setPpaRatesModalOpen(false);
    }, []);

    const handleSelectSandboxPpaOffer = useCallback(
        (offer: PreContractSandboxPpaOffer) => {
            applySandboxPpaOffer(offer);
            setPpaRatesModalOpen(false);
        },
        [applySandboxPpaOffer],
    );

    const minimumPpaRate =
        sandboxPpaOffers.length > 0
            ? Math.min(...sandboxPpaOffers.map((offer) => offer.startingRate))
            : null;
    const selectedSandboxPpaOffer =
        sandboxPpaOffers.find((offer) => offer.id === selectedSandboxPpaOfferId) ?? null;

    /** Refresh the workbook-suggested sell rate when its pricing inputs change. */
    useEffect(() => {
        const state = form.installState.trim().toUpperCase();
        const utility = form.utilityCompany.trim();
        if (!form.products.Solar || !state || !utility) {
            setPpaRateLoading(false);
            setSandboxPpaOffers([]);
            setSelectedSandboxPpaOfferId(null);
            return undefined;
        }

        let cancelled = false;
        setPpaRateLoading(true);
        void fetchPreContractSandboxPpaRates({
            state,
            utility,
            batteryCount: form.products.Battery
                ? Math.max(0, Math.round(form.batteryQuantity || 0))
                : 0,
        })
            .then((rates) => {
                if (cancelled) return;
                if (!rates?.defaultOffer) {
                    setSandboxPpaOffers(rates?.offers ?? []);
                    setSelectedSandboxPpaOfferId(null);
                    return;
                }
                setSandboxPpaOffers(rates.offers);
                applySandboxPpaOffer(rates.defaultOffer);
            })
            .catch((error) => {
                if (cancelled) return;
                setSandboxPpaOffers([]);
                setSelectedSandboxPpaOfferId(null);
                console.error('Pre-contract Sandbox Pricing request failed', error);
            })
            .finally(() => {
                if (!cancelled) setPpaRateLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [
        applySandboxPpaOffer,
        form.batteryQuantity,
        form.installState,
        form.products.Battery,
        form.products.Solar,
        form.utilityCompany,
    ]);

    /** Prefilled utilities arrive as a name only — resolve the id PPWDetails needs. */
    useEffect(() => {
        const name = form.utilityCompany.trim();
        if (!name || form.utilityCompanyId) return undefined;
        const stateCode = resolveUtilityCompanyState(form, opportunityDetails);
        if (!stateCode) return undefined;

        let cancelled = false;
        void fetchUtilityCompanyCatalog(stateCode).then((catalog) => {
            if (cancelled) return;
            const match = resolveUtilityCompanyCatalogMatch(name, catalog);
            if (!match?.Utility_company_id) return;
            // Snap the prefilled label to the GetCompanies name so saves match the catalog.
            patchForm({
                utilityCompany: match.name || name,
                utilityCompanyId: match.Utility_company_id,
            });
        });
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [form.utilityCompany, form.utilityCompanyId, form.installState, opportunityDetails, patchForm]);

    const openPanel = (panelId: PanelId) => {
        setUtilityPopoverOpen(false);
        setFinanceEligibilityOpen(false);
        setDesignToolPopoverOpen(false);
        setActivePanel(panelId);
    };

    const closePanel = () => {
        const reloadRoof = roofSavedDirtyRef.current;
        roofSavedDirtyRef.current = false;
        setActivePanel(null);
        setDesignProductFocus(null);
        if (reloadRoof) void reloadOpportunityDesign();
    };

    const toggleUtilityPopover = (event?: React.MouseEvent) => {
        event?.stopPropagation();
        if (event?.currentTarget instanceof HTMLButtonElement) {
            utilityPopoverAnchorRef.current = event.currentTarget;
        }
        setActivePanel(null);
        setDesignToolPopoverOpen(false);
        setFinanceEligibilityOpen(false);
        setUtilityPopoverOpen((open) => !open);
    };

    const toggleFinanceEligibility = (event?: React.MouseEvent) => {
        event?.stopPropagation();
        setActivePanel(null);
        setDesignToolPopoverOpen(false);
        setUtilityPopoverOpen(false);
        const next = !financeEligibilityOpen;
        if (next) {
            const stateCode = String(form.installState ?? '').trim();
            if (stateCode) {
                try {
                    sessionStorage.setItem('stateCode', stateCode);
                } catch {
                    /* ignore */
                }
            }
            packingSlipService.fetchFinancePartnerDetails().then((details) => {
                if (details) useViewerStore.setState({ financePartnerDetails: details });
            }).catch(() => undefined);
        }
        setFinanceEligibilityOpen(next);
    };

    const toggleDesignToolPopover = () => {
        setActivePanel(null);
        setUtilityPopoverOpen(false);
        setFinanceEligibilityOpen(false);
        setDesignToolPopoverOpen((open) => !open);
    };

    const selectDesignTool = (tool: PreContractDesignTool) => {
        const available = tool === 'aurora' ? form.auroraAvailable : form.hoverAvailable;
        if (!available) return;
        patchForm({ preferredDesignTool: tool });
        setDesignToolPopoverOpen(false);
    };

    const restorePreAiSnapshot = () => {
        const snap = preAiSnapshotRef.current;
        if (!snap) return;
        patchForm({
            roofModuleLayouts: snap.roofModuleLayouts,
            roofSelections: snap.roofSelections,
            systemUsageKwh: snap.systemUsageKwh,
        });
    };

    const goBackFromBoard = () => {
        if (boardStep === 4) {
            setPricingPanelOpen(false);
            setBoardStep(3);
            endLossesViewportPreview();
            setSolarLayoutPanelTab('designs');
            setSolarLayoutPopupOpen(true);
            return;
        }
        if (boardStep === 3) {
            setPricingPanelOpen(false);
            setSolarLayoutPanelTab('config');
            restorePreAiSnapshot();
            setBoardStep(2);
            endLossesViewportPreview();
            setSolarLayoutPopupOpen(true);
            return;
        }
        if (boardStep === 2) {
            setPricingPanelOpen(false);
            setBoardStep(1);
            setTreeCuttingPreviewActive(false);
            scrollToSection('pci-unified-stack');
            return;
        }
        navigate('/sites');
    };

    const handleRunAiDesign = (retainPrevious = false) => {
        if (!canRunAiFromLayout(form)) {
            setHighlightValidation(true);
            const firstIssue = TILE_STEPS.find(
                (stepId) => getStepValidation(stepId, form).status !== 'complete',
            );
            if (firstIssue) {
                focusIncompleteStep(firstIssue);
            }
            return;
        }
        const roofModuleLayouts = expandLayoutsToMaxFill(
            form.roofModuleLayouts,
            loadPreContractFullModuleLayouts(),
            {
                includeGreyPanels: form.includeGreyPanels,
                includePurplePanels: form.includePurplePanels,
            },
        );
        const roofSelections = preContractSelectionsFromLayouts(roofModuleLayouts);
        const nextForm = { ...form, roofModuleLayouts, roofSelections };
        patchForm({ roofModuleLayouts, roofSelections });
        const ghostIds = nextForm.siteTrees
            .filter((tree) => tree.ghosted)
            .map((tree) => tree.id);
        if (ghostIds.length > 0) {
            postPciTreeGhostToEmbed(ghostIds);
        }
        persistIntakeForm(site.salesForceId, nextForm);
        setAiGenerationForm(nextForm);
        preAiSnapshotRef.current = {
            roofModuleLayouts,
            roofSelections,
            systemUsageKwh: nextForm.systemUsageKwh,
        };
        if (!retainPrevious) {
            clearPreContractAiOptionsCache();
            setAiDesignRunId((n) => n + 1);
        }
        setPricingPanelOpen(false);
        setBoardStep(3);
        setFinanceEligibilityOpen(false);
        commitSolarConfigSnapshot(nextForm);
        setSolarLayoutPanelTab('designs');
    };

    const goToModuleStep = () => {
        const titleReady = !titleLoading && (isTitleInfoAvailable(titleInfo) || titleContactSynced);
        if (!canAdvanceFromIntakeBoard(form, titleReady)) {
            setHighlightValidation(true);
            return;
        }
        setUtilityPopoverOpen(false);
        setDesignToolPopoverOpen(false);
        setViewportPopupOpen(false);
        setActivePanel(null);
        if (form.preferredDesignTool === 'aurora' && !auroraSiteDesignLoaded) {
            setHighlightValidation(true);
            return;
        }
        setForm((prev) => {
            const roofs =
                siteRoofs.length > 0
                    ? siteRoofs.map((r) => ({ roofTagId: r.roofTagId, label: r.label }))
                    : undefined;
            if (!roofs?.length) return prev;
            // Keep Design drawer roof inclusion; fill modules on every included roof.
            const roofSelections = roofs.map(
                (_, index) => prev.roofSelections[index] !== false,
            );
            const hasLiveLayouts = prev.roofModuleLayouts.some((layout) =>
                layout.modules.some((cell) => cell != null),
            );
            const layouts = hasLiveLayouts
                ? applyRoofSelectionsToLayouts(prev.roofModuleLayouts, roofSelections)
                : buildRoofModuleLayouts(roofSelections, {
                      maxFill: true,
                      roofs,
                  });
            // Every roof inactive disables Run AI, and the board is persisted, so
            // opening the step must never leave the user stuck with nothing selected.
            const anyIncluded = layouts.some((layout) =>
                layout.modules.some((cell) => cell?.included),
            );
            const roofModuleLayouts = anyIncluded ? layouts : includeAllModules(layouts);
            return {
                ...prev,
                roofSelections: anyIncluded ? roofSelections : roofModuleLayouts.map(() => true),
                treeCount: estimateTreeCount(site.salesForceId),
                roofModuleLayouts,
            };
        });
        setBoardStep(2);
        scrollToSection('pci-section-layout');
    };

    useEffect(() => {
        if (!hasActiveDesignTool(form)) return;
        if (form.preferredDesignTool === 'aurora' && !auroraSiteDesignLoaded) return;
        setForm((prev) => {
            const hasLiveLayouts = prev.roofModuleLayouts.some((layout) =>
                layout.modules.some((cell) => cell != null),
            );
            if (hasLiveLayouts) return prev;
            if (siteRoofs.length === 0) return prev;
            const roofs = siteRoofs.map((r) => ({ roofTagId: r.roofTagId, label: r.label }));
            const roofSelections = roofs.map((_, index) => prev.roofSelections[index] !== false);
            return {
                ...prev,
                roofSelections,
                treeCount: estimateTreeCount(site.salesForceId),
                roofModuleLayouts: buildRoofModuleLayouts(roofSelections, {
                    maxFill: true,
                    roofs,
                }),
            };
        });
    }, [
        auroraSiteDesignLoaded,
        form.preferredDesignTool,
        form.auroraAvailable,
        form.hoverAvailable,
        siteRoofs,
        site.salesForceId,
    ]);

    /**
     * Placement of every candidate module, captured while the embed still holds the
     * full max-fill. Applying a design deletes the unselected meshes, so this snapshot
     * is the only way to put a panel back when the user re-selects it.
     */
    const embedFillLayoutRef = useRef<SavedRoofModulesLayout | null>(null);

    const captureEmbedFillLayout = useCallback(
        async (layouts: PreContractIntakeForm['roofModuleLayouts']) => {
            const catalog = loadPreContractFullModuleLayouts() ?? layouts;
            const moduleIds = collectAllModuleIds(catalog);
            if (moduleIds.length === 0) return;
            const layout = await requestPciModulesLayoutFromEmbed(moduleIds);
            if (!layout?.length) return;
            if (countLayoutModules(layout) > countLayoutModules(embedFillLayoutRef.current)) {
                embedFillLayoutRef.current = layout;
            }
        },
        [],
    );
    const captureEmbedFillLayoutRef = useRef(captureEmbedFillLayout);
    captureEmbedFillLayoutRef.current = captureEmbedFillLayout;

    useEffect(() => {
        const liveCount = countPresentModuleCells(form.roofModuleLayouts);
        if (liveCount === 0) return;
        const catalog = loadPreContractFullModuleLayouts();
        if (catalog && liveCount < countPresentModuleCells(catalog)) return;
        void captureEmbedFillLayout(form.roofModuleLayouts);
    }, [captureEmbedFillLayout, form.roofModuleLayouts]);

    const applyAiOptionToEmbed = useCallback((option: PreContractAiDesignOption) => {
        const includedModuleIds = collectIncludedModuleIds(option.layouts);
        if (includedModuleIds.length === 0) return Promise.resolve(false);

        postPciCancelShadeToEmbed();
        designSwitchGenRef.current += 1;
        const switchGen = designSwitchGenRef.current;
        pendingDesignAppliesRef.current += 1;
        let result = false;
        const apply = async () => {
            try {
                if (switchGen !== designSwitchGenRef.current) return;
                await captureEmbedFillLayoutRef.current(option.layouts);
                if (switchGen !== designSwitchGenRef.current) return;
                result = await requestPciApplyDesignOptionToEmbed({
                    includedModuleIds,
                    modulesLayout: option.modulesLayout ?? null,
                    fillLayout: embedFillLayoutRef.current,
                    ghostedTreeIds: option.ghostedTreeIds ?? [],
                });
                if (switchGen !== designSwitchGenRef.current) {
                    result = false;
                    return;
                }
                if (result && siteRoofs.length) {
                    const includedTagIds = siteRoofs
                        .filter((_, index) => option.roofSelections[index] !== false)
                        .map((roof) => roof.roofTagId);
                    if (includedTagIds.length > 0) {
                        postPciRoofSelectionToEmbed(includedTagIds);
                    }
                }
                if (result) postPci3dViewToEmbed();
            } finally {
                pendingDesignAppliesRef.current = Math.max(
                    0,
                    pendingDesignAppliesRef.current - 1,
                );
                // Suppression is ref-based, so nothing re-renders when it lifts.
                if (pendingDesignAppliesRef.current === 0) syncSelectionToEmbedRef.current();
            }
        };
        const queued = designApplyQueueRef.current.then(apply, apply);
        designApplyQueueRef.current = queued;
        return queued.then(() => result);
    }, [siteRoofs]);
    const applyAiOptionToEmbedRef = useRef(applyAiOptionToEmbed);
    applyAiOptionToEmbedRef.current = applyAiOptionToEmbed;

    /** Place the working (selected) design in the embed — not the AI Config max-fill. */
    const applyWorkingDesignToEmbed = useCallback(
        (
            layouts: PreContractIntakeForm['roofModuleLayouts'] = form.roofModuleLayouts,
            roofSelections: boolean[] = form.roofSelections,
        ) => {
            const includedModuleIds = collectIncludedModuleIds(layouts);
            if (includedModuleIds.length === 0) {
                syncSelectionToEmbedRef.current();
                return Promise.resolve(false);
            }
            pendingDesignAppliesRef.current += 1;
            let result = false;
            const apply = async () => {
                try {
                    await captureEmbedFillLayoutRef.current(layouts);
                    result = await requestPciApplyDesignOptionToEmbed({
                        includedModuleIds,
                        fillLayout: embedFillLayoutRef.current,
                        ghostedTreeIds: form.siteTrees
                            .filter((tree) => tree.ghosted)
                            .map((tree) => tree.id),
                    });
                    if (result && siteRoofs.length) {
                        const includedTagIds = siteRoofs
                            .filter((_, index) => roofSelections[index] !== false)
                            .map((roof) => roof.roofTagId);
                        if (includedTagIds.length > 0) {
                            postPciRoofSelectionToEmbed(includedTagIds);
                        }
                    }
                    if (result) postPci3dViewToEmbed();
                } finally {
                    pendingDesignAppliesRef.current = Math.max(
                        0,
                        pendingDesignAppliesRef.current - 1,
                    );
                    if (pendingDesignAppliesRef.current === 0) {
                        syncSelectionToEmbedRef.current();
                    }
                }
            };
            const queued = designApplyQueueRef.current.then(apply, apply);
            designApplyQueueRef.current = queued;
            return queued.then(() => result);
        },
        [form.roofModuleLayouts, form.roofSelections, form.siteTrees, siteRoofs],
    );

    const applyAiConfigMaxFillToEmbed = useCallback(() => {
        const selected = normalizeFillOrientations(form.fillOrientations);
        const orientation =
            selected.length === 1 ? selected[0] : selected.includes('portrait') ? 'portrait' : selected[0];
        postPciCancelShadeToEmbed();
        designSwitchGenRef.current += 1;
        const switchGen = designSwitchGenRef.current;
        pendingDesignAppliesRef.current += 1;
        let result = false;
        const apply = async () => {
            try {
                if (switchGen !== designSwitchGenRef.current) return;
                result = await requestPciApplyMaxFillToEmbed(orientation);
                if (switchGen !== designSwitchGenRef.current) result = false;
            } finally {
                pendingDesignAppliesRef.current = Math.max(
                    0,
                    pendingDesignAppliesRef.current - 1,
                );
            }
        };
        const queued = designApplyQueueRef.current.then(apply, apply);
        designApplyQueueRef.current = queued;
        return queued.then(() => result);
    }, [form.fillOrientations]);

    const aiConfigViewportAppliedRef = useRef(false);
    useEffect(() => {
        if (!solarLayoutPopupOpen || solarLayoutPanelTab !== 'config') {
            aiConfigViewportAppliedRef.current = false;
            return;
        }
        if (skipAiConfigMaxFillRef.current) {
            skipAiConfigMaxFillRef.current = false;
            aiConfigViewportAppliedRef.current = true;
            return;
        }
        if (aiConfigViewportAppliedRef.current) return;
        if (!solarConfigSnapshotRef.current) {
            commitSolarConfigSnapshot(form);
        }
        void applyAiConfigMaxFillToEmbed().then((ok) => {
            if (ok && solarLayoutPanelTab === 'config' && solarLayoutPopupOpen) {
                aiConfigViewportAppliedRef.current = true;
                viewportDesignSourceRef.current = 'config';
            }
        });
    }, [
        applyAiConfigMaxFillToEmbed,
        form,
        solarLayoutPanelTab,
        solarLayoutPopupOpen,
    ]);

    const lossesViewportSyncedTabRef = useRef<SolarLayoutPanelTab | null>(null);
    useEffect(() => {
        if (!solarLayoutPopupOpen) {
            lossesViewportSyncedTabRef.current = null;
            return;
        }
        const isLossesTab =
            isAllDesignsTab(solarLayoutPanelTab) &&
            (allDesignsCard === 'max-onedraw' || allDesignsCard === 'max-enfin');
        if (!isLossesTab) {
            if (
                lossesViewportSyncedTabRef.current &&
                viewportDesignSourceRef.current !== 'saved'
            ) {
                endLossesViewportPreview();
            }
            lossesViewportSyncedTabRef.current = null;
            return;
        }
        const previousTab = lossesViewportSyncedTabRef.current;
        lossesViewportSyncedTabRef.current = solarLayoutPanelTab;
        const vendor: ActiveLossesVendor =
            allDesignsCard === 'max-enfin' ? 'EnFin' : 'oneDRAW';
        const alreadyOnLosses =
            previousTab === 'onedraw' ||
            previousTab === 'enfin' ||
            previousTab === 'all';
        const preview = solarViewportPreviewRef.current;
        const source = viewportDesignSourceRef.current;
        void (async () => {
            // Saved / AI layouts are already on the viewport; only Config needs a
            // working-design apply so losses don't replace a loaded version.
            if (!alreadyOnLosses && (source === 'config' || source == null)) {
                await applyWorkingDesignToEmbed(
                    preview?.layouts ?? form.roofModuleLayouts,
                    preview?.roofSelections ?? form.roofSelections,
                );
            }
            if (
                !solarLayoutPopupOpenRef.current ||
                solarLayoutPanelTabRef.current !== solarLayoutPanelTab
            ) {
                return;
            }
            previewLossesVendorOnViewport(vendor);
        })();
    }, [
        applyWorkingDesignToEmbed,
        endLossesViewportPreview,
        form.roofModuleLayouts,
        form.roofSelections,
        previewLossesVendorOnViewport,
        allDesignsCard,
        solarLayoutPanelTab,
        solarLayoutPopupOpen,
    ]);

    /** Design requested by the latest Open in 3D, applied once the embed is ready. */
    const pendingViewportOptionRef = useRef<PreContractAiDesignOption | null>(null);
    /** Option id already restored for this popup session. */
    const appliedViewportOptionRef = useRef<string | null>(null);

    /**
     * Prepare the 3D scene every time the viewer is revealed so the canvas is
     * never blank, then restore the design only after the embed has published
     * its site model: an apply sent before that never gets acknowledged.
     */
    useEffect(() => {
        if (!viewportPopupActive) {
            appliedViewportOptionRef.current = null;
            return;
        }
        postPci3dViewToEmbed();
        const target =
            pendingViewportOptionRef.current ??
            (boardStep >= 3 ? resolveSelectedPreContractAiOption() : null);
        if (!target || siteRoofs.length === 0) return;
        pendingViewportOptionRef.current = null;
        if (appliedViewportOptionRef.current === target.id) return;
        appliedViewportOptionRef.current = target.id;
        void applyAiOptionToEmbedRef.current(target);
    }, [viewportPopupActive, boardStep, siteRoofs.length]);

    const openViewportPopup = (option?: PreContractAiDesignOption | null) => {
        setUtilityPopoverOpen(false);
        setDesignToolPopoverOpen(false);
        setActivePanel(null);
        // Guard: header buttons pass a click event if wired as onClick={() => openViewportPopup()}.
        pendingViewportOptionRef.current =
            option &&
            typeof option === 'object' &&
            Array.isArray((option as PreContractAiDesignOption).layouts)
                ? (option as PreContractAiDesignOption)
                : null;
        setViewportPopupOpen(true);
    };

    const handlePreviewAiOption = (option: PreContractAiDesignOption) => {
        if (!viewportPopupOpen) return;
        appliedViewportOptionRef.current = option.id;
        void applyAiOptionToEmbed(option);
    };

    const closeViewportPopup = () => {
        setViewportPopupOpen(false);
        setViewportFinalizeOpen(false);
    };

    const closeSolarLayoutPopup = () => {
        if (
            form.products.Solar &&
            isSolarLayoutModifiedFromBaseline(form, savedSolarBaselineRef.current)
        ) {
            patchForm({
                solarDesignRevision: 'manual',
                appliedAiDesignTitle: null,
            });
        }
        restoreSolarConfigViewport();
        solarConfigSnapshotRef.current = null;
        setSolarViewportPreviewId(null);
        setSolarViewportPreview(null);
        solarViewportPreviewRef.current = null;
        endLossesViewportPreview();
        setSolarLayoutPanelTab('config');
        setSolarLayoutPopupOpen(false);
    };

    const syncSolarPreviewLayoutsToEmbed = useCallback(
        (layouts: PreContractIntakeForm['roofModuleLayouts'], roofSelections: boolean[]) => {
            if (siteRoofs.length) {
                const includedTagIds = siteRoofs
                    .filter((_, index) => roofSelections[index] !== false)
                    .map((roof) => roof.roofTagId);
                postPciRoofSelectionToEmbed(includedTagIds);
            }
            postPciModuleSelectionToEmbed(collectIncludedModuleIds(layouts));
        },
        [siteRoofs],
    );

    const handlePreviewOnViewport = useCallback(
        (option: PreContractAiDesignOption) => {
            if (solarViewportPreviewId === option.id) {
                clearSolarViewportPreview();
                return;
            }
            const layouts = clonePreContractRoofModuleLayouts(option.layouts);
            const roofSelections = preContractSelectionsFromLayouts(layouts);
            const optionLabel =
                option.title?.trim() || `Option ${option.rank ?? 1}`;
            const preview = {
                optionId: option.id,
                optionLabel,
                layouts,
                roofSelections,
            };
            setSolarViewportPreviewId(option.id);
            setSolarViewportPreview(preview);
            solarViewportPreviewRef.current = preview;
            viewportDesignSourceRef.current = 'ai';
            void (async () => {
                await ensureSolarConfigSnapshotEmbedLayout();
                await applyAiOptionToEmbed(option);
            })();
        },
        [
            applyAiOptionToEmbed,
            clearSolarViewportPreview,
            ensureSolarConfigSnapshotEmbedLayout,
            solarViewportPreviewId,
        ],
    );

    /**
     * Re-place the design from the max-fill snapshot before filtering to the new
     * selection. Needed whenever modules come back: the previous apply deleted them.
     * Shade stays as the design left it — the board already carries per-module values,
     * so a toggle must not kick off another shade pass in the viewport.
     */
    const restorePreviewLayoutsOnEmbed = useCallback(
        (
            optionId: string,
            layouts: PreContractIntakeForm['roofModuleLayouts'],
            roofSelections: boolean[],
        ) => {
            const includedModuleIds = collectIncludedModuleIds(layouts);
            if (includedModuleIds.length === 0 || !embedFillLayoutRef.current?.length) {
                syncSolarPreviewLayoutsToEmbed(layouts, roofSelections);
                return;
            }
            const option = resolvePreContractAiOptionById(optionId);
            const run = async () => {
                pendingDesignAppliesRef.current += 1;
                try {
                    await requestPciApplyDesignOptionToEmbed({
                        includedModuleIds,
                        modulesLayout: null,
                        fillLayout: embedFillLayoutRef.current,
                        ghostedTreeIds: option?.ghostedTreeIds ?? [],
                        skipShadeRefresh: true,
                    });
                } finally {
                    pendingDesignAppliesRef.current = Math.max(
                        0,
                        pendingDesignAppliesRef.current - 1,
                    );
                }
                syncSolarPreviewLayoutsToEmbed(layouts, roofSelections);
            };
            const queued = designApplyQueueRef.current.then(run, run);
            designApplyQueueRef.current = queued;
        },
        [syncSolarPreviewLayoutsToEmbed],
    );

    const handlePreviewToggleModule = useCallback(
        (roofIndex: number, moduleIndex: number) => {
            const prev = solarViewportPreviewRef.current;
            if (!prev) return;
            const wasIncluded = Boolean(
                prev.layouts.find((layout) => layout.roofIndex === roofIndex)?.modules[
                    moduleIndex
                ]?.included,
            );
            const nextLayouts = togglePreContractLayoutModule(
                prev.layouts,
                roofIndex,
                moduleIndex,
                form.includeGreyPanels,
                form.includePurplePanels,
            );
            if (!nextLayouts) return;
            const nextRoofSelections = preContractSelectionsFromLayouts(nextLayouts);
            const next = {
                ...prev,
                layouts: nextLayouts,
                roofSelections: nextRoofSelections,
            };
            solarViewportPreviewRef.current = next;
            setSolarViewportPreview(next);
            if (wasIncluded) {
                syncSolarPreviewLayoutsToEmbed(nextLayouts, nextRoofSelections);
                return;
            }
            restorePreviewLayoutsOnEmbed(prev.optionId, nextLayouts, nextRoofSelections);
        },
        [
            form.includeGreyPanels,
            form.includePurplePanels,
            restorePreviewLayoutsOnEmbed,
            syncSolarPreviewLayoutsToEmbed,
        ],
    );

    const handlePreviewToggleRoof = useCallback(
        (roofIndex: number, included: boolean) => {
            const prev = solarViewportPreviewRef.current;
            if (!prev) return;
            const nextLayouts = setPreContractLayoutRoofIncluded(
                prev.layouts,
                roofIndex,
                included,
                form.includeGreyPanels,
                form.includePurplePanels,
            );
            const nextRoofSelections = preContractSelectionsFromLayouts(nextLayouts);
            const next = {
                ...prev,
                layouts: nextLayouts,
                roofSelections: nextRoofSelections,
            };
            solarViewportPreviewRef.current = next;
            setSolarViewportPreview(next);
            if (!included) {
                syncSolarPreviewLayoutsToEmbed(nextLayouts, nextRoofSelections);
                return;
            }
            restorePreviewLayoutsOnEmbed(prev.optionId, nextLayouts, nextRoofSelections);
        },
        [
            form.includeGreyPanels,
            form.includePurplePanels,
            restorePreviewLayoutsOnEmbed,
            syncSolarPreviewLayoutsToEmbed,
        ],
    );

    /**
     * The losses tabs edit the same design as AI Config, so toggles route to whichever
     * board is live: the viewport preview when one is running, otherwise the form.
     */
    const handleLossesToggleModule = useCallback(
        (roofIndex: number, moduleIndex: number) => {
            if (solarViewportPreviewRef.current) {
                handlePreviewToggleModule(roofIndex, moduleIndex);
                return;
            }
            const nextLayouts = togglePreContractLayoutModule(
                form.roofModuleLayouts,
                roofIndex,
                moduleIndex,
                form.includeGreyPanels,
                form.includePurplePanels,
            );
            if (!nextLayouts) return;
            patchForm({
                roofModuleLayouts: nextLayouts,
                roofSelections: preContractSelectionsFromLayouts(nextLayouts),
            });
        },
        [
            form.includeGreyPanels,
            form.includePurplePanels,
            form.roofModuleLayouts,
            handlePreviewToggleModule,
            patchForm,
        ],
    );

    const handleLossesToggleRoof = useCallback(
        (roofIndex: number, included: boolean) => {
            if (solarViewportPreviewRef.current) {
                handlePreviewToggleRoof(roofIndex, included);
                return;
            }
            const nextLayouts = setPreContractLayoutRoofIncluded(
                form.roofModuleLayouts,
                roofIndex,
                included,
                form.includeGreyPanels,
                form.includePurplePanels,
            );
            patchForm({
                roofModuleLayouts: nextLayouts,
                roofSelections: preContractSelectionsFromLayouts(nextLayouts),
            });
        },
        [
            form.includeGreyPanels,
            form.includePurplePanels,
            form.roofModuleLayouts,
            handlePreviewToggleRoof,
            patchForm,
        ],
    );

    const handleSolarLayoutPanelTab = (tab: SolarLayoutPanelTab) => {
        const nextTab = isAllDesignsTab(tab) ? 'all' : tab;
        if (nextTab === solarLayoutPanelTab) return;
        // The losses tabs only read the design, so snapshotting keys off the last
        // editing tab rather than whatever pane the user is leaving.
        const fromTab = solarLayoutEditingTabRef.current;
        if (tab === 'config' || tab === 'designs') {
            solarLayoutEditingTabRef.current = tab;
        }
        if (tab === 'designs' && fromTab === 'config') {
            commitSolarConfigSnapshot(form);
        }
        if (tab === 'config' && fromTab === 'designs') {
            const snapshot = solarConfigSnapshotRef.current;
            setSolarViewportPreviewId(null);
            setSolarViewportPreview(null);
            solarViewportPreviewRef.current = null;
            if (snapshot) {
                patchForm({
                    roofModuleLayouts: snapshot.roofModuleLayouts,
                    roofSelections: snapshot.roofSelections,
                });
            }
            restoreSolarConfigViewport();
        }
        setSolarLayoutPanelTab(nextTab);
    };

    const refreshSavedOneDrawDesigns = useCallback(() => {
        if (!site.salesForceId?.trim()) {
            setSavedOneDrawDesigns([]);
            return Promise.resolve([] as DesignData[]);
        }
        setSavedOneDrawDesignsLoading(true);
        return fetchFinalizedDesignVersions(site.salesForceId)
            .then((list) => {
                setSavedOneDrawDesigns(list);
                return list;
            })
            .catch(() => {
                setSavedOneDrawDesigns([]);
                return [] as DesignData[];
            })
            .finally(() => setSavedOneDrawDesignsLoading(false));
    }, [site.salesForceId]);

    useEffect(() => {
        void refreshSavedOneDrawDesigns();
    }, [refreshSavedOneDrawDesigns]);

    useEffect(() => {
        if (!solarLayoutPopupOpen) return;
        void refreshSavedOneDrawDesigns();
    }, [refreshSavedOneDrawDesigns, solarLayoutPopupOpen]);

    const handleSelectSavedOneDrawDesign = useCallback(
        async (design: DesignData) => {
            const key = savedDesignKey(design);
            postPciCancelShadeToEmbed();
            designSwitchGenRef.current += 1;
            const switchGen = designSwitchGenRef.current;
            setSelectingSavedDesignKey(key);
            savedDesignLiveIngestRef.current = true;
            try {
                const ok = await requestPciLoadDesignVersionToEmbed({
                    salesForceId: site.salesForceId,
                    designNo: design.Design_No,
                    versionNo: design.Version_No,
                });
                if (switchGen !== designSwitchGenRef.current) return;
                if (!ok) {
                    notification.error({
                        message: 'Could not open that design',
                        description: 'The oneDRAW viewport did not load this version. Try again.',
                        placement: 'topRight',
                    });
                    return;
                }
                setSelectedSavedDesignKey(key);
                savedSolarBaselineRef.current = null;
                setSavedDesignLayoutBaseline(null);
                setSolarViewportPreviewId(null);
                setSolarViewportPreview(null);
                solarViewportPreviewRef.current = null;
                patchForm({
                    solarDesignRevision: null,
                    appliedAiDesignTitle: savedDesignDisplayName(design),
                });
                const designVendor = normalizeLossesVendor(design.MultipliersVendor);
                lossesViewportRestoreRef.current = null;
                setSolarLossesViewportVendor(null);
                useViewerStore.setState({
                    selectedLossesVendor: designVendor,
                    pciProductionLossesVendor: designVendor,
                    pciProductionLossesPending: null,
                });
                postPciLossesVendorToEmbed(designVendor, { skipShade: true });
                skipAiConfigMaxFillRef.current = true;
                viewportDesignSourceRef.current = 'saved';
                setAllDesignsCard(null);
                setSolarLayoutPanelTab('all');
            } finally {
                if (switchGen === designSwitchGenRef.current) {
                    setSelectingSavedDesignKey(null);
                }
            }
        },
        [patchForm, site.salesForceId],
    );

    const handleSelectMaxLayoutCard = useCallback(
        async (vendor: ActiveLossesVendor) => {
            postPciCancelShadeToEmbed();
            designSwitchGenRef.current += 1;
            const switchGen = designSwitchGenRef.current;
            setAllDesignsCard(vendor === 'EnFin' ? 'max-enfin' : 'max-onedraw');
            setSelectedSavedDesignKey(null);
            setSelectingMaxLayout(true);
            savedDesignLiveIngestRef.current = true;
            viewportDesignSourceRef.current = 'config';
            try {
                if (lossesViewportRestoreRef.current == null) {
                    lossesViewportRestoreRef.current = getCurrentLossesVendor();
                }
                setSolarLossesViewportVendor(vendor);
                useViewerStore.setState({
                    selectedLossesVendor: vendor,
                    pciProductionLossesVendor: vendor,
                    pciProductionLossesPending: null,
                });
                postPciLossesVendorToEmbed(vendor, { skipShade: true });
                const selected = normalizeFillOrientations(form.fillOrientations);
                const orientation =
                    selected.length === 1
                        ? selected[0]
                        : selected.includes('portrait')
                          ? 'portrait'
                          : selected[0];
                const ok = await requestPciApplyMaxFillToEmbed(orientation);
                if (switchGen !== designSwitchGenRef.current) return;
                if (!ok) return;
                patchForm({
                    solarDesignRevision: 'manual',
                    appliedAiDesignTitle: `Max layout · ${
                        vendor === 'EnFin' ? 'Enfin losses' : 'oneDRAW losses'
                    }`,
                });
                setSolarLayoutPanelTab('all');
            } finally {
                if (switchGen === designSwitchGenRef.current) {
                    setSelectingMaxLayout(false);
                }
            }
        },
        [form.fillOrientations, patchForm],
    );

    useEffect(() => {
        if (!solarLayoutPopupOpen) {
            lastViewportTabRef.current = null;
            return;
        }
        const tab = solarLayoutPanelTab;
        const previousTab = lastViewportTabRef.current;

        if (isAllDesignsTab(tab)) {
            if (savedOneDrawDesignsLoading || selectingSavedDesignKey || selectingMaxLayout) {
                return;
            }
            if (previousTab === 'all') return;
            lastViewportTabRef.current = 'all';
            const selected =
                selectedSavedDesignKey != null
                    ? savedOneDrawDesigns.find(
                          (design) => savedDesignKey(design) === selectedSavedDesignKey,
                      )
                    : undefined;
            const lastFinalized = selected ?? savedOneDrawDesigns[0];
            if (lastFinalized) {
                void handleSelectSavedOneDrawDesign(lastFinalized);
                return;
            }
            if (allDesignsCard === 'max-enfin' || allDesignsCard === 'max-onedraw') {
                void handleSelectMaxLayoutCard(
                    allDesignsCard === 'max-enfin' ? 'EnFin' : 'oneDRAW',
                );
            }
            return;
        }

        if (previousTab === tab) return;
        lastViewportTabRef.current = tab;

        if (tab === 'designs') {
            const option =
                (solarViewportPreviewId
                    ? resolvePreContractAiOptionById(solarViewportPreviewId)
                    : null) ?? resolveSelectedPreContractAiOption();
            if (!option) return;
            viewportDesignSourceRef.current = 'ai';
            patchForm({
                solarDesignRevision: 'ai',
                appliedAiDesignTitle: option.title?.trim() || `Design ${option.rank}`,
            });
            void applyAiOptionToEmbed(option);
        }
    }, [
        allDesignsCard,
        applyAiOptionToEmbed,
        handleSelectMaxLayoutCard,
        handleSelectSavedOneDrawDesign,
        patchForm,
        selectingMaxLayout,
        savedOneDrawDesigns,
        savedOneDrawDesignsLoading,
        selectedSavedDesignKey,
        selectingSavedDesignKey,
        solarLayoutPanelTab,
        solarLayoutPopupOpen,
        solarViewportPreviewId,
    ]);

    const openSolarLayoutPopup = (panelTab?: SolarLayoutPanelTab) => {
        if (!designToolReady || !solarProductAllowed) return;
        endLossesViewportPreview();
        patchForm({
            products: {
                ...form.products,
                Solar: true,
            },
        });
        const nextTab = panelTab ?? defaultSolarNestedLayoutTab();
        if (nextTab === 'designs') {
            commitSolarConfigSnapshot(form);
        }
        setSolarLayoutPanelTab(nextTab);
        setSolarLayoutPopupOpen(true);
    };

    const openAiDesignsPopup = () => {
        if (!designToolReady || !(boardStep >= 3 || hasPreviousAiDesigns)) return;
        openSolarLayoutPopup('designs');
        setFinanceEligibilityOpen(false);
        setUtilityPopoverOpen(false);
    };

    const handleApplyAiDesign = useCallback(
        (option: PreContractAiDesignOption) => {
            const patch = {
                ...intakePatchFromAiDesign(option, form.siteTrees),
                solarDesignRevision: 'ai' as const,
                appliedAiDesignTitle: option.title?.trim() || `Design ${option.rank}`,
            };
            const nextForm = { ...form, ...patch };
            patchForm(patch);
            persistIntakeForm(site.salesForceId, nextForm);
            if (option.fullModuleLayouts?.length) {
                persistPreContractFullModuleLayouts(option.fullModuleLayouts);
            }
            applyAiOptionToEmbed(option);
            setSolarLayoutPanelTab('config');
            setSolarLayoutPopupOpen(false);
            setPricingPanelOpen(false);
            setBoardStep(1);
            window.setTimeout(() => scrollToSection('pci-unified-deal'), 0);
        },
        [applyAiOptionToEmbed, form, patchForm, site.salesForceId],
    );

    const handleAiDesignsContinue = () => {
        const option = resolveSelectedPreContractAiOption();
        if (option) {
            handleApplyAiDesign(option);
            return;
        }
        setSolarLayoutPanelTab('config');
        setSolarLayoutPopupOpen(false);
        setPricingPanelOpen(false);
        setBoardStep(4);
        scrollToSection('pci-section-selected');
    };

    const handleTitleSyncSuccess = useCallback(() => {
        void refreshTitleContext({ silent: true });
    }, [refreshTitleContext]);

    const openAddTitleContactsModal = useCallback(async () => {
        setUtilityPopoverOpen(false);
        setFinanceEligibilityOpen(false);
        setDesignToolPopoverOpen(false);
        setActivePanel(null);
        if (!useOneButtonStore.getState().opportunityDetails) {
            await refreshTitleContext();
        }
        setAddTitleContactsModalOpen(true);
    }, [refreshTitleContext]);

    const openManageContactsModal = useCallback(() => {
        setUtilityPopoverOpen(false);
        setDesignToolPopoverOpen(false);
        setContactsModalOpen(true);
    }, []);

    const closeManageContactsModal = useCallback(() => {
        setContactsModalOpen(false);
        void refreshTitleContext({ silent: true });
    }, [refreshTitleContext]);

    const contactsModalProps = useMemo(
        () => ({
            loading: opportunityLoading || titleLoading,
            opportunityId: String(storeOpportunityId ?? site.salesForceId ?? ''),
            isTitleContactExist: titleSearchData.length > 0,
            onAddTitleContactsClick: () => {
                setContactsModalOpen(false);
                void openAddTitleContactsModal();
            },
            onBlackKnightTitleCheckClick: () => {
                setContactsModalOpen(false);
                void openAddTitleContactsModal();
            },
        }),
        [
            opportunityLoading,
            titleLoading,
            storeOpportunityId,
            site.salesForceId,
            titleSearchData,
            openAddTitleContactsModal,
        ],
    );

    const focusIncompleteStep = (stepId: WizardStepId) => {
        const target = stepToPanel(stepId);
        if (target === 'utility') {
            setUtilityPopoverOpen(true);
            setDesignToolPopoverOpen(false);
            setActivePanel(null);
            return;
        }
        if (target === 'designTool') {
            setDesignToolPopoverOpen(true);
            setUtilityPopoverOpen(false);
            setActivePanel(null);
            return;
        }
        if (target) {
            openPanel(target);
        }
    };

    const designIssues = useMemo(() => designAvailabilityIssues(form), [form]);
    const installDisplay = formatInstallAddress(form) || site.address;
    const allComplete = canLaunchWorkspace(form);
    const canRunAiFromLayoutPopup = canRunAiFromLayout(form);
    /** Exact blockers for the layout popup Run AI button. */
    const runAiIssues = useMemo(
        () => getRunAiFromLayoutIssues(form),
        [form],
    );
    const solarDesignModified = useMemo(() => {
        if (!form.products.Solar) return false;
        return isSolarLayoutModifiedFromBaseline(
            form,
            savedDesignLayoutBaseline ?? savedSolarBaselineRef.current,
        );
    }, [form, savedDesignLayoutBaseline]);

    const roofLiveFingerprint = useMemo(() => {
        if (!form.products.Roof) return null;
        return roofDesignFingerprint({ form, systemDesign, priceQuoteOverrides });
    }, [form, priceQuoteOverrides, systemDesign]);

    useEffect(() => {
        if (!form.products.Roof) {
            setRoofDesignBaseline(null);
            return;
        }
        if (roofDesignBaseline != null || !systemDesign) return;
        if (roofLiveFingerprint) setRoofDesignBaseline(roofLiveFingerprint);
    }, [form.products.Roof, roofDesignBaseline, roofLiveFingerprint, systemDesign]);

    const roofDesignModified = Boolean(
        form.products.Roof &&
            (roofDesignDirty ||
                buildPreContractRoofDesignSnapshot({
                    form,
                    systemDesign,
                    priceQuoteOverrides,
                }).configured ||
                (roofDesignBaseline &&
                    roofLiveFingerprint &&
                    roofLiveFingerprint !== roofDesignBaseline)),
    );
    const oneDrawFinalizePendingRef = useRef(false);
    const finalizeCurrentLayoutRef = useRef<() => Promise<void>>(async () => {});
    const finalizeSavedWaiterRef = useRef<{
        resolve: () => void;
        reject: (error: Error) => void;
    } | null>(null);
    const finalizeConfirmWaiterRef = useRef<{
        resolve: (confirmed: boolean) => void;
    } | null>(null);

    const selectedSavedDesign = useMemo(
        () =>
            selectedSavedDesignKey
                ? savedOneDrawDesigns.find(
                      (design) => savedDesignKey(design) === selectedSavedDesignKey,
                  ) ?? null
                : null,
        [savedOneDrawDesigns, selectedSavedDesignKey],
    );

    const solarLayoutDetailsViewportPreview = useMemo(() => {
        if (solarViewportPreview) {
            const option = resolvePreContractAiOptionById(solarViewportPreview.optionId);
            const modified =
                option != null &&
                !pciRoofModuleLayoutsEqual(solarViewportPreview.layouts, option.layouts);
            return {
                label: solarViewportPreview.optionLabel,
                layouts: solarViewportPreview.layouts,
                roofSelections: solarViewportPreview.roofSelections,
                baseMetrics: option
                    ? {
                          systemKw: option.systemKw > 0 ? option.systemKw : null,
                          annualProductionKwh: option.annualKwh > 0 ? option.annualKwh : null,
                          multiplier:
                              option.multiplier != null && option.multiplier > 0
                                  ? option.multiplier
                                  : null,
                      }
                    : null,
                modified,
            };
        }
        if (
            solarLayoutPopupOpen &&
            (solarLayoutPanelTab === 'config' ||
                (isAllDesignsTab(solarLayoutPanelTab) &&
                    (allDesignsCard === 'max-onedraw' || allDesignsCard === 'max-enfin')))
        ) {
            const layouts = expandLayoutsToMaxFill(
                form.roofModuleLayouts,
                loadPreContractFullModuleLayouts(),
                {
                    includeGreyPanels: form.includeGreyPanels,
                    includePurplePanels: form.includePurplePanels,
                },
            );
            return {
                label: 'Max-fill layout',
                layouts,
                roofSelections: preContractSelectionsFromLayouts(layouts),
                baseMetrics: null,
                modified: true,
                showPreviewFlags: false,
            };
        }
        if (selectedSavedDesign && allDesignsCard == null) {
            const savedMetrics = metricsFromSavedDesign(selectedSavedDesign);
            const { count: liveCount } = countIncludedModules(
                form.roofModuleLayouts,
                form.roofSelections,
            );
            const liveKw = preContractSystemSizeKw(form);
            const savedCount = savedMetrics.moduleCount;
            const savedKw = savedMetrics.systemKw;
            const modified =
                solarDesignModified ||
                (savedCount != null && liveCount !== savedCount) ||
                (savedKw != null && liveKw > 0 && Math.abs(liveKw - savedKw) > 0.02);
            return {
                label: savedDesignDisplayName(selectedSavedDesign),
                layouts: form.roofModuleLayouts,
                roofSelections: form.roofSelections,
                baseMetrics: savedMetrics,
                modified,
                showPreviewFlags: false,
            };
        }
        return null;
    }, [
        form.includeGreyPanels,
        form.includePurplePanels,
        form.roofModuleLayouts,
        form.roofSelections,
        allDesignsCard,
        selectedSavedDesign,
        solarDesignModified,
        solarLayoutPanelTab,
        solarLayoutPopupOpen,
        solarViewportPreview,
    ]);
    const designToolReady = hasActiveDesignTool(form);
    const hasPreviousAiDesigns = hasPreContractAiOptionsCache();
    const showSolarLayoutStatus = designToolReady && form.products.Solar;
    const showAiSection = designToolReady && (boardStep >= 3 || hasPreviousAiDesigns);
    const showSelectedSection = boardStep >= 4;
    const solarDesignsPanelActive =
        solarLayoutPopupOpen && solarLayoutPanelTab === 'designs';
    const designDrawerTitle =
        activePanel === 'design'
            ? designProductFocus === 'Roof'
                ? 'Roof design'
                : designProductFocus === 'Battery'
                  ? 'Battery design'
                  : roofDesignDrawerOpen
                    ? 'Product designs'
                    : PANEL_LABELS.design
            : activePanel
              ? PANEL_LABELS[activePanel]
              : '';
    const siteViewportHasRoofs = siteRoofs.length > 0;
    const siteViewportHasModules = form.roofModuleLayouts.some((layout) =>
        layout.modules.some((cell) => cell != null),
    );
    const siteViewportLoadingMessage =
        siteViewportError || !designToolReady
            ? null
            : !siteViewportHasRoofs
              ? '3D viewport for this site is loading. Roof data will appear when the model is ready.'
              : !siteViewportHasModules
                ? '3D viewport loaded — finishing module layout from the site model…'
                : null;
    const designToolLabel =
        form.preferredDesignTool === 'hover'
            ? 'Hover'
            : form.preferredDesignTool === 'aurora'
              ? 'Aurora'
              : 'Design tool';
    const designToolViewportStatus: 'error' | 'loading' | 'ready' | null = designToolReady
        ? siteViewportError
            ? 'error'
            : siteViewportHasRoofs
              ? 'ready'
              : 'loading'
        : null;
    const designToolViewportLabel =
        designToolViewportStatus === 'error'
            ? `${designToolLabel} unavailable`
            : designToolViewportStatus === 'loading'
              ? `${designToolLabel} loading…`
              : `${designToolLabel} ready`;
    const summaryItems = useMemo(() => {
        const viewportLoading = Boolean(siteViewportLoadingMessage);
        const systemKw = preContractSystemSizeKw(form);
        const systemUsage =
            form.systemUsageKwh.trim() ||
            (() => {
                const estimated = estimateLayoutAnnualKwh({
                    layouts: form.roofModuleLayouts,
                    roofSelections: form.roofSelections,
                    moduleWattage: form.moduleTypeWattage,
                });
                return estimated != null && estimated > 0 ? String(estimated) : '';
            })();
        return [
            {
                label: 'Utility Usage',
                value: form.annualUsageKwh.trim()
                    ? `${form.annualUsageKwh.trim()} kWh`
                    : '—',
            },
            {
                label: 'System Size',
                value: systemKw > 0 ? `${systemKw.toFixed(2)} kW` : '—',
            },
            {
                label: 'System Production',
                value: siteViewportError
                    ? 'Unavailable'
                    : viewportLoading
                      ? 'Loading'
                      : systemUsage
                        ? `${systemUsage} kWh`
                        : '—',
                loading: viewportLoading,
                error: Boolean(siteViewportError),
            },
            {
                label: 'All-in Rate',
                value: form.utilityRate.trim()
                    ? `$${form.utilityRate.trim()}/kWh`
                    : '—',
            },
        ];
    }, [
        form.annualUsageKwh,
        form.systemUsageKwh,
        form.moduleTypeWattage,
        form.roofModuleLayouts,
        form.roofSelections,
        form.utilityRate,
        siteViewportError,
        siteViewportLoadingMessage,
    ]);

    /** Keep System Usage in sync when included panels / wattage change (not on every embed republish). */
    const systemUsageSyncKeyRef = useRef<string>('');
    useEffect(() => {
        const estimated = estimateLayoutAnnualKwh({
            layouts: form.roofModuleLayouts,
            roofSelections: form.roofSelections,
            moduleWattage: form.moduleTypeWattage,
        });
        if (estimated == null) return;
        const systemKw = preContractSystemSizeKw(form);
        const { count } = countIncludedModules(form.roofModuleLayouts, form.roofSelections);
        const syncKey = `${count}|${form.moduleTypeWattage ?? 0}|${systemKw}`;
        const next = estimated > 0 ? String(estimated) : '';
        if (systemUsageSyncKeyRef.current === syncKey) return;
        systemUsageSyncKeyRef.current = syncKey;
        setForm((prev) => {
            if (prev.systemUsageKwh === next) return prev;
            return { ...prev, systemUsageKwh: next };
        });
    }, [form.roofModuleLayouts, form.roofSelections, form.moduleTypeWattage]);

    useEffect(() => {
        if (allComplete) {
            setHighlightValidation(false);
        }
    }, [allComplete]);

    const handleLaunch = (action: PreContractLaunchAction, formOverride?: PreContractIntakeForm) => {
        const payloadForm = formOverride ?? form;
        const launchReady =
            action === 'openDesign'
                ? canRunAiFromLayout(payloadForm)
                : canLaunchWorkspace(payloadForm);
        if (!launchReady) {
            setHighlightValidation(true);
            const firstIssue = TILE_STEPS.find(
                (stepId) => getStepValidation(stepId, payloadForm).status !== 'complete',
            );
            if (firstIssue) {
                focusIncompleteStep(firstIssue);
            }
            return;
        }
        persistIntakeForm(site.salesForceId, payloadForm);
        persistDashboardLayoutVariant('v2');
        sessionStorage.setItem('SalesForceId', site.salesForceId);
        sessionStorage.setItem('currentStage', String(parseInt(site.stage.split(':')[0], 10) || 0));
        markPreContractLaunch(action);
        if (action === 'openDesign') {
            markPreContractReturn(4);
        } else if (action === 'runAi') {
            markPreContractReturn(2);
        }
        useOneButtonStore.getState().setSalesForceId(String(site.salesForceId));

        const openCanvas = action === 'runAi' || action === 'openDesign';
        useViewerStore.setState({
            dashboardLayoutVariant: 'v2',
            treeCartItems: [],
            pathname: openCanvas ? '/configure/roof' : '/configure/modules',
            isDashboardOpen: !openCanvas,
            siteViewerCalculationsPending: false,
            viewerAlert: {
                show: false,
                title: '',
                message: '',
                messageType: AlertType.info,
                isModal: false,
            },
            isStreetViewVisible: false,
            loadingCompleted: false,
            siteLoadingError: null,
            siteMultipliersPendingNotice: null,
            siteViewerUnavailablePrompt: null,
        });

        const query = new URLSearchParams({ sfid: site.salesForceId });
        if (openCanvas) query.set('view', 'onedraw');
        if (action === 'runPricing') query.set('section', 'financing');
        navigate(`/configure?${query.toString()}`);
    };

    const handleOpenDashboardV2 = useCallback(() => {
        persistIntakeForm(site.salesForceId, form);
        persistDashboardLayoutVariant('v2');
        sessionStorage.setItem('SalesForceId', site.salesForceId);
        useOneButtonStore.getState().setSalesForceId(String(site.salesForceId));
        useViewerStore.setState({
            dashboardLayoutVariant: 'v2',
            isDashboardOpen: true,
            pathname: '/configure/modules',
        });
        navigate(`/configure?sfid=${encodeURIComponent(site.salesForceId)}`);
    }, [form, navigate, site.salesForceId]);

    const utilityValidation = getStepValidation('utility', form);
    const designValidation = getStepValidation('design', form);
    const isEnergyCommunity = useMemo(
        () => resolveEnergyCommunityLikelyQualifies(opportunityDetails, energyCommunityStatus),
        [opportunityDetails, energyCommunityStatus],
    );
    const resolvedOpportunityId = resolveStoreOpportunityId() ?? storeOpportunityId ?? null;
    const opportunityBlockReason = getOpportunityBlockReason(
        opportunityLoading,
        resolvedOpportunityId,
        opportunityLoadError,
    );
    const opportunityReady = opportunityBlockReason == null;

    useEffect(() => {
        if (!opportunityBlockReason) return;
        setUtilityPopoverOpen(false);
        setDesignToolPopoverOpen(false);
        setActivePanel(null);
        setProductPickerOpen(false);
        setAddTitleContactsModalOpen(false);
    }, [opportunityBlockReason]);

    const titleInfoAvailable = isTitleInfoAvailable(titleInfo) || titleContactSynced;
    const showTitleActions = !titleLoading && !titleInfoAvailable;
    const showAddTitleButton = showTitleActions;
    const titleValidation: StepValidation = titleInfoAvailable
        ? { status: 'complete', label: 'Verified', issues: [] }
        : titleLoading
          ? { status: 'incomplete', label: 'Loading', issues: [] }
          : {
                status: 'incomplete',
                label: 'Required',
                issues: ['Title records are not available. Run Black Knight or add title contacts.'],
            };
    const canGoNext = canAdvanceFromIntakeBoard(form, titleValidation.status === 'complete');
    const nextBlockedReasons = [
        ...(designValidation.status !== 'complete' ? designValidation.issues : []),
        ...(utilityValidation.status !== 'complete' ? utilityValidation.issues : []),
        ...(titleValidation.status !== 'complete' ? titleValidation.issues : []),
    ];
    const activePanelValidation =
        activePanel === 'title'
            ? titleValidation
            : activePanel
              ? getStepValidation(PANEL_STEPS[activePanel], form)
              : null;
    const backAriaLabel =
        boardStep === 4
            ? 'Back to AI design options'
            : boardStep === 3
            ? 'Back to module layout'
            : boardStep === 2
              ? 'Back to intake'
              : 'Back to sites';
    const handleOpenPricingPanel = () => setPricingPanelOpen(true);

    const generateQuoteAccess = getOneButtonCapabilityValue(
        onebuttonCapabilities,
        'Generate_Quote_Access',
    );
    const isGenerateQuotePermissionDisabled =
        generateQuoteAccess !== 'Edit' && generateQuoteAccess !== 'View';

    const generateQuoteDisabledReason = useMemo(() => {
        if (opportunityBlockReason) return opportunityBlockReason;
        if (isGenerateQuotePermissionDisabled) {
            return 'Generate Quote is not available for your role.';
        }
        if (!form.products.Solar && !form.products.Roof && !form.products.Battery) {
            return 'Select at least one product first.';
        }
        return undefined;
    }, [
        form.products.Battery,
        form.products.Roof,
        form.products.Solar,
        isGenerateQuotePermissionDisabled,
        opportunityBlockReason,
    ]);

    const selectedProducts = useMemo(
        () => PRE_CONTRACT_PRODUCTS.filter((product) => form.products[product]),
        [form.products],
    );

    /** Same vendor as the solar-layout details bar (viewer pick, else saved design). */
    const designLossesLabel = getDesignLossesSourceLabel(
        resolveActiveLossesVendor(selectedLossesVendor, systemDesignLosses),
    );

    const availableProductsToAdd = useMemo(
        () =>
            PRE_CONTRACT_PRODUCTS.filter(
                (product) =>
                    !form.products[product] && !PRE_CONTRACT_DISABLED_PRODUCTS.has(product),
            ),
        [form.products],
    );

    const isProductAddBlocked = useCallback(
        (product: PreContractProduct): string | null => {
            if (product === 'Solar') return solarProductBlockReason;
            return null;
        },
        [solarProductBlockReason],
    );

    const openProductDesign = useCallback(
        (product: PreContractProduct) => {
            setProductPickerOpen(false);

            if (product === 'Solar') {
                setDesignProductFocus(null);
                setActivePanel(null);
                setSolarLayoutPanelTab('all');
                if (!designToolReady || !solarProductAllowed) return;
                endLossesViewportPreview();
                setSolarLayoutPopupOpen(true);
                return;
            }

            if (product === 'Roof' || product === 'Battery') {
                setSolarLayoutPopupOpen(false);
                setSolarLayoutPanelTab('config');
                setDesignProductFocus(product);
                openPanel('design');
            }
        },
        [designToolReady, endLossesViewportPreview, openPanel, solarProductAllowed],
    );

    const addProductAndOpenDesign = useCallback(
        (product: PreContractProduct) => {
            if (PRE_CONTRACT_DISABLED_PRODUCTS.has(product) || form.products[product]) return;
            const blockReason = isProductAddBlocked(product);
            if (blockReason) {
                notification.warning({
                    message: `${product} unavailable`,
                    description: blockReason,
                    className: 'obd-notification',
                });
                return;
            }
            patchForm({
                products: {
                    ...form.products,
                    [product]: true,
                },
            });
            setProductPickerOpen(false);
            openProductDesign(product);
        },
        [form.products, isProductAddBlocked, openProductDesign, patchForm],
    );

    const removeProduct = useCallback(
        (product: PreContractProduct) => {
            const nextProducts = {
                ...form.products,
                [product]: false,
            };
            patchForm({ products: nextProducts });
            if (product === 'Solar') {
                setSolarLayoutPopupOpen(false);
            }
            if (activePanel === 'design') {
                const stillHasDesignProduct = nextProducts.Roof || nextProducts.Battery;
                if (!stillHasDesignProduct) {
                    closePanel();
                }
            }
        },
        [activePanel, closePanel, form.products, patchForm],
    );

    const {
        activeProduct: activeGenerateProduct,
        steps: generateSteps,
        running: generateRunning,
        review: quoteReview,
        startGenerate: startProductGenerate,
        runGenerateStep: runProductGenerateStep,
        confirmReview: confirmQuoteReview,
        cancelReview: cancelQuoteReview,
        notifyQuoteSaved,
        cancelQuoteWait,
        dismissQuoteWait,
        linkedQuotes,
        unlinkQuote,
    } = usePreContractProductGenerate({
        salesForceId: site.salesForceId,
        generateDisabled: Boolean(generateQuoteDisabledReason),
        generateDisabledReason: generateQuoteDisabledReason,
        isGenerateQuotePermissionDisabled,
        productPartners: useMemo(() => {
            const oppProducts = opportunityDetails?.OpportunityProduct ?? [];
            const entries = selectedProducts.map((product) => {
                const row = oppProducts.find(
                    (p: { Product_Type_Name?: string; ActiveFlag?: boolean }) =>
                        p.Product_Type_Name === product && p.ActiveFlag !== false,
                );
                return [
                    product,
                    {
                        partner: row?.Finance_Partner_Display_Name ?? null,
                        method: row?.Finance_Method_Name ?? null,
                    },
                ] as const;
            });
            return Object.fromEntries(entries);
        }, [selectedProducts, opportunityDetails]),
        resolveDesignPartner: (product) =>
            generatePartnerBridgeRef.current?.resolveDesignPartner(product) ?? null,
        syncDesignPartner: (product, recommendation) =>
            generatePartnerBridgeRef.current?.syncDesignPartner(product, recommendation) ??
            Promise.resolve({
                ok: false,
                message: 'Partner sync is not ready yet. Try again in a moment.',
            }),
        resolveQuoteReview: (product, partnerName, financeMethod): PreContractQuoteReview =>
            buildPreContractQuoteReview({
                product,
                partnerName,
                financeMethod,
                form,
                systemDesign,
                priceQuoteOverrides,
                metricsOverride: selectedAiPricingOverride,
                selectedOption:
                    generatePartnerBridgeRef.current?.resolveSelectedPricingOption(product) ?? null,
                summaryItems,
                preferEnfin: isEnergyCommunity,
            }),
        onOpenGenerateQuote: (payload, product) => {
            const option =
                generatePartnerBridgeRef.current?.resolveSelectedPricingOption(product) ?? null;
            const startingRate = Number(
                String(form.targetMinPpaRate ?? '').replace(/[$,]/g, '').trim(),
            );
            const existingOffer =
                payload.ExcelOffer && typeof payload.ExcelOffer === 'object'
                    ? payload.ExcelOffer
                    : {};
            const excelOffer = { ...existingOffer };
            if (!(Number(excelOffer.startingRate) > 0) && Number.isFinite(startingRate) && startingRate > 0) {
                excelOffer.startingRate = startingRate;
            }
            if (!(Number(excelOffer.startingPPW) > 0) && option?.ppw != null && option.ppw > 0) {
                excelOffer.startingPPW = option.ppw;
            }
            const rawEscalator = Number(
                excelOffer.escalator ?? option?.escalator ?? form.targetEscalator,
            );
            if (Number.isFinite(rawEscalator)) {
                excelOffer.escalator = sandboxEscalatorPercent(rawEscalator);
            }
            setConfirmQuoteCellKey({
                ...payload,
                ExcelOffer: excelOffer,
                escalator:
                    excelOffer.escalator ??
                    payload.escalator ??
                    option?.escalator ??
                    payload.escalator,
            });
        },
        onGenerateComplete: () => {
            setQuotesRefreshTrigger(Date.now());
        },
        needsFinalizeDesign: (product) =>
            (product === 'Solar' || product === 'Battery') && oneDrawFinalizePendingRef.current,
        onFinalizeDesign: async () => {
            const confirmed = await new Promise<boolean>((resolve) => {
                finalizeConfirmWaiterRef.current = { resolve };
                setFinalizeConfirmOpen(true);
            });
            if (!confirmed) throw new Error('FINALIZE_CANCELLED');
            await finalizeCurrentLayoutRef.current();
            await new Promise<void>((resolve, reject) => {
                finalizeSavedWaiterRef.current = { resolve, reject };
            });
        },
        needsUtilityDetails: (product) =>
            utilityDetailsDirtyRef.current && (product === 'Solar' || product === 'Battery'),
        onSaveUtilityDetails: async () => {
            await saveUtilityDetailsRef.current();
        },
        onOpenPartnerSetup: async (product) => {
            generatePartnerBridgeRef.current?.openPartnerSetup?.(product);
        },
    });

    const handleGenerateQuoteComplete = useCallback((quoteId?: string) => {
        notifyQuoteSaved(quoteId);
        setConfirmQuoteCellKey(null);
        setQuoteForm(null);
        setQuotesRefreshTrigger(Date.now());
    }, [notifyQuoteSaved, setQuotesRefreshTrigger]);

    const handleSetConfirmQuoteCellKey = useCallback(
        (value: React.SetStateAction<selectedQuote | null>) => {
            const nextValue =
                typeof value === 'function' ? value(confirmQuoteCellKey) : value;
            if (nextValue == null && generateRunning) {
                dismissQuoteWait();
            }
            setConfirmQuoteCellKey(nextValue);
        },
        [confirmQuoteCellKey, dismissQuoteWait, generateRunning],
    );
    const handleOpenOneDrawFromAi = async (option: PreContractAiDesignOption) => {
        const patch = intakePatchFromAiDesign(option, form.siteTrees);
        const nextForm = { ...form, ...patch };
        patchForm(patch);
        const includedModuleIds = collectIncludedModuleIds(option.layouts);
        const includedRoofTagIds = option.layouts
            .filter((layout) => option.roofSelections[layout.roofIndex] !== false)
            .map((layout) => {
                if (typeof layout.roofTagId === 'number' && Number.isFinite(layout.roofTagId)) {
                    return layout.roofTagId;
                }
                return siteRoofs[layout.roofIndex]?.roofTagId;
            })
            .filter((id): id is number => typeof id === 'number' && Number.isFinite(id));
        postPciModuleSelectionToEmbed(includedModuleIds);
        const modulesLayout =
            option.modulesLayout && option.modulesLayout.length > 0
                ? option.modulesLayout
                : await requestPciModulesLayoutFromEmbed(includedModuleIds);
        const ghostedTreeIds = Array.from(
            new Set(
                (option.ghostedTreeIds ??
                    option.treeCut?.treeIds ??
                    nextForm.siteTrees.filter((tree) => tree.ghosted).map((tree) => tree.id)
                )
                    .map((id) => String(id).trim())
                    .filter(Boolean),
            ),
        );
        persistPreContractOpenDesign({
            includedModuleIds,
            includedRoofTagIds,
            ...(modulesLayout ? { modulesLayout } : {}),
            ...(ghostedTreeIds.length ? { ghostedTreeIds } : {}),
        });
        handleLaunch('openDesign', nextForm);
    };

    const handleFinalizeFromAi = async (option: PreContractAiDesignOption) => {
        if (!opportunityReady) return;
        const patch = {
            ...intakePatchFromAiDesign(option, form.siteTrees),
            solarDesignRevision: 'ai' as const,
            appliedAiDesignTitle: option.title?.trim() || `Design ${option.rank}`,
        };
        const nextForm = { ...form, ...patch };
        patchForm(patch);
        persistIntakeForm(site.salesForceId, nextForm);
        finalizeLayoutMetricsRef.current = {
            systemKw: option.systemKw > 0 ? option.systemKw : null,
            annualProductionKwh: option.annualKwh > 0 ? option.annualKwh : null,
            multiplier:
                option.multiplier != null && option.multiplier > 0 ? option.multiplier : null,
            lossesVendor: getCurrentLossesVendor(),
        };
        lossesViewportRestoreRef.current = null;
        setSolarLossesViewportVendor(null);
        setSolarViewportPreviewId(null);
        setSolarViewportPreview(null);
        solarViewportPreviewRef.current = null;
        solarConfigSnapshotRef.current = null;
        lastViewportTabRef.current = 'all';
        setSolarLayoutPanelTab('all');
        setViewportPopupOpen(false);
        setViewportFinalizeOpen(false);
        setFinalizeModalOpen(true);
        await applyAiOptionToEmbed(option);
    };

    const handleFinalizeFromCurrentLayout = useCallback(async () => {
        if (!opportunityReady) return;

        const preview = solarViewportPreviewRef.current;
        let nextForm = form;
        if (preview) {
            const option = resolvePreContractAiOptionById(preview.optionId);
            const fromOption = option
                ? intakePatchFromAiDesign(
                      {
                          ...option,
                          layouts: preview.layouts,
                          roofSelections: preview.roofSelections,
                      },
                      form.siteTrees,
                  )
                : {
                      roofModuleLayouts: preview.layouts,
                      roofSelections: preview.roofSelections,
                  };
            const estimatedKwh = estimateLayoutAnnualKwh({
                layouts: preview.layouts,
                roofSelections: preview.roofSelections,
                moduleWattage: form.moduleTypeWattage,
            });
            const patch = {
                ...fromOption,
                systemUsageKwh:
                    estimatedKwh != null && estimatedKwh > 0
                        ? String(Math.round(estimatedKwh))
                        : form.systemUsageKwh,
                solarDesignRevision: 'ai' as const,
                appliedAiDesignTitle: preview.optionLabel,
            };
            nextForm = { ...form, ...patch };
            patchForm(patch);
        }

        persistIntakeForm(site.salesForceId, nextForm);
        const popupMetrics = finalizeLayoutMetricsRef.current;
        finalizeLayoutMetricsRef.current = {
            systemKw: popupMetrics?.systemKw ?? (preContractSystemSizeKw(nextForm) || null),
            annualProductionKwh:
                popupMetrics?.annualProductionKwh ??
                estimateLayoutAnnualKwh({
                    layouts: nextForm.roofModuleLayouts,
                    roofSelections: nextForm.roofSelections,
                    moduleWattage: nextForm.moduleTypeWattage,
                }),
            multiplier: popupMetrics?.multiplier ?? null,
            lossesVendor: getCurrentLossesVendor(),
        };

        // Keep the losses vendor shown in the popup — do not restore a prior preview.
        lossesViewportRestoreRef.current = null;
        setSolarLossesViewportVendor(null);
        setSolarViewportPreviewId(null);
        setSolarViewportPreview(null);
        solarViewportPreviewRef.current = null;
        solarConfigSnapshotRef.current = null;
        lastViewportTabRef.current = 'all';
        setSolarLayoutPanelTab('all');
        setViewportPopupOpen(false);
        setViewportFinalizeOpen(false);
        setFinalizeModalOpen(true);

        const includedModuleIds = collectIncludedModuleIds(nextForm.roofModuleLayouts);
        if (includedModuleIds.length === 0) return;

        const ghostedTreeIds = nextForm.siteTrees
            .filter((tree) => tree.ghosted)
            .map((tree) => String(tree.id).trim())
            .filter(Boolean);

        await requestPciApplyDesignOptionToEmbed({
            includedModuleIds,
            modulesLayout: null,
            ghostedTreeIds,
        });
        syncSelectionToEmbedRef.current();
        postPci3dViewToEmbed();
    }, [form, opportunityReady, patchForm, site.salesForceId]);
    finalizeCurrentLayoutRef.current = handleFinalizeFromCurrentLayout;

    const resolveFinalizeConfirm = useCallback((confirmed: boolean) => {
        const waiter = finalizeConfirmWaiterRef.current;
        finalizeConfirmWaiterRef.current = null;
        setFinalizeConfirmOpen(false);
        waiter?.resolve(confirmed);
    }, []);

    /**
     * After Confirm, re-read the latest saved design, compare the Solar card to that
     * snapshot, and lock or unlock quote steps from the new baseline.
     */
    const handleFinalizeSaved = useCallback(() => {
        const finishWaiter = (error?: Error) => {
            const waiter = finalizeSavedWaiterRef.current;
            finalizeSavedWaiterRef.current = null;
            if (!waiter) return;
            if (error) waiter.reject(error);
            else waiter.resolve();
        };

        const oppId = resolveStoreOpportunityId() ?? storeOpportunityId;
        if (!isOpportunityReady(oppId)) {
            finishWaiter(new Error('Opportunity is not loaded.'));
            return;
        }
        const id = String(oppId);
        const commit = finalizeLayoutMetricsRef.current;
        const lossesSource = commit?.lossesVendor === 'EnFin' ? 'EnFin' : 'oneDRAW';

        const baselineForm = { ...form, solarDesignRevision: null, appliedAiDesignTitle: null };
        savedSolarBaselineRef.current = solarLayoutSignature(baselineForm);
        patchForm({ solarDesignRevision: null, appliedAiDesignTitle: null });
        persistIntakeForm(site.salesForceId, baselineForm);

        lossesViewportRestoreRef.current = null;
        setSolarLossesViewportVendor(null);
        setSolarViewportPreviewId(null);
        setSolarViewportPreview(null);
        solarViewportPreviewRef.current = null;
        setAllDesignsCard(null);
        viewportDesignSourceRef.current = 'saved';
        useViewerStore.setState({
            selectedLossesVendor: lossesSource,
            pciProductionLossesVendor: lossesSource,
            pciProductionLossesPending: null,
        });

        const overlayDesign = (design: Record<string, unknown> | null) =>
            applyFinalizedViewerMetricsToSystemDesign(design ?? {}, {
                actualKwh: commit?.annualProductionKwh ?? null,
                systemKw: commit?.systemKw ?? null,
                multiplier: commit?.multiplier ?? null,
            });

        const currentDesign = useOneButtonStore.getState().systemDesign as Record<
            string,
            unknown
        > | null;
        let latestDesign = overlayDesign(currentDesign);
        setSystemDesign(latestDesign);
        const currentLosses = useOneButtonStore.getState().systemDesignLosses;
        let latestLosses = {
            ...(currentLosses ?? {}),
            LossesSource: lossesSource,
        };
        setSystemDesignLosses(latestLosses);
        oneDrawFinalizePendingRef.current = false;

        void (async () => {
            try {
                for (let attempt = 0; attempt < 4; attempt += 1) {
                    if (attempt > 0) {
                        await new Promise((resolve) => window.setTimeout(resolve, 800));
                    }
                    const [design, losses] = await Promise.all([
                        designServices.RetrieveSystemDesign(id).catch(() => null),
                        designServices.SystemDesignLosses(id).catch(() => null),
                    ]);
                    if (design) {
                        latestDesign = overlayDesign(
                            design as unknown as Record<string, unknown>,
                        );
                        setSystemDesign(latestDesign);
                    }
                    if (losses) {
                        latestLosses = {
                            ...losses,
                            LossesSource: losses.LossesSource ?? lossesSource,
                        };
                        setSystemDesignLosses(latestLosses);
                    }
                    if (design) break;
                }

                const priorKeys = new Set(
                    savedOneDrawDesignsRef.current.map((design) => savedDesignKey(design)),
                );
                let versions: DesignData[] = [];
                let latestVersion: DesignData | undefined;
                for (let attempt = 0; attempt < 6; attempt += 1) {
                    if (attempt > 0) {
                        await new Promise((resolve) => window.setTimeout(resolve, 700));
                    }
                    versions = await refreshSavedOneDrawDesigns();
                    latestVersion =
                        versions.find((design) => !priorKeys.has(savedDesignKey(design))) ??
                        versions[0];
                    if (
                        latestVersion &&
                        (priorKeys.size === 0 ||
                            !priorKeys.has(savedDesignKey(latestVersion)) ||
                            versions.length > priorKeys.size)
                    ) {
                        break;
                    }
                }

                setAllDesignsCard(null);
                lastViewportTabRef.current = 'all';
                setSolarLayoutPanelTab('all');
                setSolarLayoutPopupOpen(true);

                if (latestVersion) {
                    setSelectedSavedDesignKey(savedDesignKey(latestVersion));
                    try {
                        await handleSelectSavedOneDrawDesign(latestVersion);
                    } catch {
                        setSelectedSavedDesignKey(savedDesignKey(latestVersion));
                    }
                }

                const cardMetrics = resolvePreContractSolarMetrics({
                    form: baselineForm,
                    systemDesign: latestDesign,
                    metricsOverride: null,
                });
                const savedLosses = resolveActiveLossesVendor(null, latestLosses);
                const remaining = listSolarOneDrawDesignDetailChanges({
                    savedSystemKw: cardMetrics.systemKw,
                    savedProductionKwh: cardMetrics.annualProductionKwh,
                    savedMultiplier: cardMetrics.multiplier,
                    savedLossesVendor: savedLosses,
                    liveSystemKw: cardMetrics.systemKw,
                    liveProductionKwh: cardMetrics.annualProductionKwh,
                    displayedProductionKwh: cardMetrics.annualProductionKwh,
                    displayedMultiplier: cardMetrics.multiplier,
                    liveLossesVendor: savedLosses,
                });
                oneDrawFinalizePendingRef.current = remaining.length > 0;
                finishWaiter();
            } catch (error) {
                finishWaiter(
                    error instanceof Error
                        ? error
                        : new Error('Could not refresh the saved design.'),
                );
            }
        })();
    }, [
        form,
        handleSelectSavedOneDrawDesign,
        patchForm,
        refreshSavedOneDrawDesigns,
        setSystemDesign,
        setSystemDesignLosses,
        site.salesForceId,
        storeOpportunityId,
    ]);

    const handleSaveOneButtonFromSelected = async () => {
        if (!opportunityReady) {
            return { ok: false, message: opportunityBlockReason ?? OPPORTUNITY_NOT_LOADED_MESSAGE };
        }
        persistIntakeForm(site.salesForceId, form);
        return savePreContractOneButtonSelection(site.salesForceId, form);
    };

    const handleSaveUtilityDetailsStep = useCallback(async () => {
        persistIntakeForm(site.salesForceId, form);
        const result = await savePreContractOneButtonSelection(site.salesForceId, form);
        if (!result.ok) {
            throw new Error(result.message ?? 'Could not save utility details.');
        }
        setUtilityDetailsBaseline(preContractUtilityDetailsSignature(form));
    }, [form, site.salesForceId]);
    saveUtilityDetailsRef.current = handleSaveUtilityDetailsStep;

    useEffect(() => {
        if (!productsPrefillReady || utilityDetailsBaseline != null) return;
        setUtilityDetailsBaseline(preContractUtilityDetailsSignature(form));
    }, [form, productsPrefillReady, utilityDetailsBaseline]);

    const selectedAiPricingOverride = useMemo((): PreContractPpwMetricsOverride | null => {
        if (boardStep < 3) return null;
        if (form.solarDesignRevision !== 'ai') return null;
        const option = resolveSelectedPreContractAiOption();
        if (!option) return null;
        return {
            systemKw: option.systemKw > 0 ? option.systemKw : null,
            annualProductionKwh: option.annualKwh > 0 ? option.annualKwh : null,
            multiplier:
                option.multiplier != null && option.multiplier > 0 ? option.multiplier : null,
        };
    }, [
        boardStep,
        form.roofModuleLayouts,
        form.roofSelections,
        form.siteTrees,
        form.solarDesignRevision,
        form.systemUsageKwh,
    ]);

    const suggestedSolarPartner = useMemo(() => {
        if (!form.products.Solar) return null;
        const result = buildPreContractLivePpwQuotes(
            form,
            0.0299,
            null,
            selectedAiPricingOverride,
        );
        const best = pickRecommendedPartnerQuote(result.quotes, {
            preferEnfin: isEnergyCommunity,
        });
        return best?.partner?.trim() || null;
    }, [form, isEnergyCommunity, selectedAiPricingOverride]);

    suggestedSolarPartnerRef.current = suggestedSolarPartner;

    const solarFinancePartner = resolveSolarFinancePartner({
        designPartner:
            generatePartnerBridgeRef.current?.resolveDesignPartner('Solar') ?? null,
        opportunityPartner: readOpportunitySolarPartner(opportunityDetails),
        suggestedPartner: suggestedSolarPartner,
    });

    const handleSelectSolarFinanceOption = useCallback(
        (option: PreContractProductPricingOption) => {
            setSolarSelectedPricingOption(option);
            generatePartnerBridgeRef.current?.selectPricingOption('Solar', option);
        },
        [],
    );

    /** One source of truth for the losses-vendor pricing shown in the bar and the losses tabs. */
    const solarLossesPricing = usePreContractLossesPricing({
        form,
        financePartner: solarFinancePartner,
        financeOptions: solarPricingOptions,
        financePartnerEligibility,
        selectedPricingOption: solarSelectedPricingOption,
        metricsOverride:
            solarLayoutDetailsViewportPreview?.baseMetrics ?? selectedAiPricingOverride,
        viewportPreview: solarLayoutDetailsViewportPreview,
        onSelectFinanceOption: handleSelectSolarFinanceOption,
    });
    const oneDrawDesignChanges = useMemo(() => {
        if (!form.products.Solar) return [];
        const lastFinalized = resolvePreContractSolarMetrics({
            form,
            systemDesign,
            metricsOverride: null,
        });
        const cardMetrics = resolvePreContractSolarMetrics({
            form,
            systemDesign,
            metricsOverride: selectedAiPricingOverride,
        });
        const lastFinalizedDesign =
            selectedSavedDesignKey != null
                ? savedOneDrawDesigns.find(
                      (design) => savedDesignKey(design) === selectedSavedDesignKey,
                  )
                : savedOneDrawDesigns[0];
        const savedLosses = lastFinalizedDesign
            ? normalizeLossesVendor(lastFinalizedDesign.MultipliersVendor)
            : resolveActiveLossesVendor(null, systemDesignLosses);
        const maxLayoutSelected =
            allDesignsCard === 'max-onedraw' || allDesignsCard === 'max-enfin';
        const currentLosses = maxLayoutSelected
            ? allDesignsCard === 'max-enfin'
                ? 'EnFin'
                : 'oneDRAW'
            : (solarLossesViewportVendor ??
              resolveActiveLossesVendor(selectedLossesVendor, systemDesignLosses));
        return listSolarOneDrawDesignDetailChanges({
            savedSystemKw: lastFinalized.systemKw,
            savedProductionKwh: lastFinalized.annualProductionKwh,
            savedMultiplier: lastFinalized.multiplier,
            savedLossesVendor: savedLosses,
            liveSystemKw: cardMetrics.systemKw,
            liveProductionKwh: cardMetrics.annualProductionKwh,
            displayedProductionKwh: cardMetrics.annualProductionKwh,
            displayedMultiplier: cardMetrics.multiplier,
            liveLossesVendor: currentLosses,
        });
    }, [
        allDesignsCard,
        form,
        savedOneDrawDesigns,
        selectedAiPricingOverride,
        selectedLossesVendor,
        selectedSavedDesignKey,
        solarLossesViewportVendor,
        systemDesign,
        systemDesignLosses,
    ]);
    const oneDrawFinalizePending = Boolean(form.products.Solar && oneDrawDesignChanges.length > 0);
    oneDrawFinalizePendingRef.current = oneDrawFinalizePending;
    const oneDrawProductsLockedReason =
        'Finalize the current oneDRAW design before changing opportunity products or finance partners.';
    const utilityChanges = listPreContractUtilityDetailChanges(utilityDetailsBaseline, form);
    const utilityDetailsDirty = utilityChanges.length > 0;
    utilityDetailsDirtyRef.current = utilityDetailsDirty;
    const maxFillViewportPreview = useMemo(() => {
        const layouts = expandLayoutsToMaxFill(
            form.roofModuleLayouts,
            loadPreContractFullModuleLayouts(),
            {
                includeGreyPanels: form.includeGreyPanels,
                includePurplePanels: form.includePurplePanels,
            },
        );
        return {
            label: 'Max-fill layout',
            layouts,
            roofSelections: preContractSelectionsFromLayouts(layouts),
            baseMetrics: null,
            modified: true,
            showPreviewFlags: false as const,
        };
    }, [form.includeGreyPanels, form.includePurplePanels, form.roofModuleLayouts]);
    const maxFillLossesPricing = usePreContractLossesPricing({
        form,
        financePartner: solarFinancePartner,
        financeOptions: solarPricingOptions,
        financePartnerEligibility,
        selectedPricingOption: solarSelectedPricingOption,
        metricsOverride: null,
        viewportPreview: maxFillViewportPreview,
        onSelectFinanceOption: handleSelectSolarFinanceOption,
    });
    finalizeLayoutMetricsRef.current = {
        systemKw: solarLossesPricing.systemKw > 0 ? solarLossesPricing.systemKw : null,
        annualProductionKwh: solarLossesPricing.activeProductionKwh,
        multiplier:
            solarLossesPricing.multiplier != null && solarLossesPricing.multiplier > 0
                ? solarLossesPricing.multiplier
                : null,
        lossesVendor: solarLossesPricing.lossesVendor,
    };

    /**
     * Shows one losses vendor in the 3D viewport without committing it. The vendor the
     * viewer was on is kept so toggling the preview off puts it back.
     */
    const handleShowLossesOnViewport = useCallback(
        (vendor: ActiveLossesVendor) => {
            if (solarLossesViewportVendor === vendor) {
                endLossesViewportPreview();
                syncSelectionToEmbedRef.current();
                return;
            }
            previewLossesVendorOnViewport(vendor);
            syncSelectionToEmbedRef.current();
        },
        [endLossesViewportPreview, previewLossesVendorOnViewport, solarLossesViewportVendor],
    );

    /** Committing a vendor ends the preview — the choice is no longer temporary. */
    const handleSelectLossesPricing = useCallback(
        (vendor: ActiveLossesVendor, partner: string | null) => {
            lossesViewportRestoreRef.current = null;
            setSolarLossesViewportVendor(null);
            solarLossesPricing.selectPricing(vendor, partner);
        },
        [solarLossesPricing],
    );

    /** oneDRAW runs in an iframe here, so the eligibility panel reads intake metrics instead of this window's viewer store. */
    const eligibilityMetrics = useMemo(() => {
        const metrics = resolvePreContractSolarMetrics({
            form,
            systemDesign,
            metricsOverride: selectedAiPricingOverride,
        });
        const { count } = countIncludedModules(form.roofModuleLayouts, form.roofSelections);
        const arrayCounts = form.roofModuleLayouts
            .filter((layout) => form.roofSelections[layout.roofIndex] !== false)
            .map((layout) => layout.modules.filter((cell) => cell?.included).length)
            .filter((moduleCount) => moduleCount > 0);
        const annualUsage = Number(form.annualUsageKwh.replace(/[,\s]/g, ''));

        return {
            multiplier: metrics.multiplier,
            totalSystemSize: metrics.systemKw,
            moduleCount: count,
            actualWattage: metrics.annualProductionKwh,
            annualUsageKwh: Number.isFinite(annualUsage) ? annualUsage : null,
            smallestArrayCount: arrayCounts.length > 0 ? Math.min(...arrayCounts) : null,
        };
    }, [form, selectedAiPricingOverride, systemDesign]);

    const utilityPopover =
        utilityPopoverOpen
            ? createPortal(
                  <div
                      ref={utilityPopoverRef}
                      className={`pci-utility-popover pci-utility-popover--portal${
                          utilityPopoverPos?.placement === 'above' ? ' pci-utility-popover--above' : ''
                      }`}
                      role="dialog"
                      aria-label="Utility details"
                      style={{
                          top: utilityPopoverPos?.top ?? -9999,
                          left: utilityPopoverPos?.left ?? -9999,
                          visibility: utilityPopoverPos ? 'visible' : 'hidden',
                      }}
                  >
                      <UtilityFormBody
                          form={form}
                          patchForm={patchForm}
                          utilityFromOpportunity={utilityFromOpportunity}
                          ppaRateLoading={ppaRateLoading}
                          minimumPpaRate={minimumPpaRate}
                          selectedSandboxPpaOffer={selectedSandboxPpaOffer}
                          sandboxPpaOfferCount={sandboxPpaOffers.length}
                          onOpenPpaRates={openPpaRatesModal}
                      />
                  </div>,
                  document.body,
              )
            : null;

    const designToolPopover =
        designToolPopoverOpen
            ? createPortal(
                  <div
                      ref={designToolPopoverRef}
                      className={`pci-utility-popover pci-utility-popover--portal pci-design-tool-popover${
                          designToolPopoverPos?.placement === 'above' ? ' pci-utility-popover--above' : ''
                      }`}
                      role="dialog"
                      aria-label="Design tool"
                      style={{
                          top: designToolPopoverPos?.top ?? -9999,
                          left: designToolPopoverPos?.left ?? -9999,
                          visibility: designToolPopoverPos ? 'visible' : 'hidden',
                      }}
                  >
                      <DesignToolFormBody
                          form={form}
                          patchForm={patchForm}
                          onSelect={selectDesignTool}
                      />
                  </div>,
                  document.body,
              )
            : null;

    const moduleViewportSlot = moduleViewportActive ? (
        <section className="pci-module-step__card pci-module-step__card--viewport">
            {designViewerSlotMode === 'popup' ? (
                <aside
                    className="pci-module-step__viewport pci-module-step__viewport--delegated pci-design-viewer-slot pci-design-viewer-slot--module"
                    aria-label="Site design viewport"
                >
                    <span className="pci-module-step__viewport-hint">3D view open</span>
                </aside>
            ) : (
                <aside
                    ref={designViewerSlotRef}
                    className="pci-module-step__viewport pci-design-viewer-slot pci-design-viewer-slot--module"
                    aria-label="Site design viewport"
                />
            )}
        </section>
    ) : null;

    return (
        <div
            className={`pci-page pci-page--board-v2 pci-page--unified${
                pricingPanelOpen ? ' pci-page--board-v2-pricing' : ''
            }`}
        >
            {utilityPopover}
            {designToolPopover}
            <FinancePartnerEligibility
                open={financeEligibilityOpen}
                onOpenChange={setFinanceEligibilityOpen}
                hideFloatingToggle
                metricsOverride={eligibilityMetrics}
                onPartnerEligibilityChange={setFinancePartnerEligibility}
                sceneValues={embedEligibility}
                presentation="modal"
            />
            <DesignLossesModal
                open={designLossesOpen}
                onClose={() => setDesignLossesOpen(false)}
                losses={systemDesignLosses}
            />
            <div className="pci-board-v2-shell pci-unified-shell">
            <main className="pci-board-v2-main pci-unified-main">
                <div className="pci-unified-workbench pci-unified-workbench--single-column">
                <div
                    id="pci-unified-stack"
                    className={`pci-unified-stack${
                        showSelectedSection ? '' : ' pci-unified-stack--compact'
                    }`}
                >
                <div className="pci-board-v2-card pci-unified-header">
                    <div className="pci-unified-header__inner">
                        <div className="pci-unified-header__row">
                            <button
                                type="button"
                                className="pci-board-v2-back pci-unified-header__back"
                                onClick={goBackFromBoard}
                                aria-label={backAriaLabel}
                            >
                                <ArrowLeft size={16} weight="bold" />
                            </button>
                            <div className="pci-unified-header__lead">
                                <h1 className="pci-unified-header__title">{site.customerName}</h1>
                                <span className="pci-unified-header__stage">{site.stage}</span>
                            </div>
                            <div className="pci-unified-header__address" title={installDisplay}>
                                <MapPin size={14} weight="fill" aria-hidden />
                                <span>{installDisplay || '—'}</span>
                            </div>
                        </div>

                        {designToolReady ? (
                            <div
                                className="pci-unified-header__toolbar"
                                role="toolbar"
                                aria-label="Setup sections"
                            >
                                <span
                                    className={`pci-unified-header__ready${
                                        designToolViewportStatus === 'error'
                                            ? ' pci-unified-header__ready--error'
                                            : designToolViewportStatus === 'loading'
                                              ? ' pci-unified-header__ready--loading'
                                              : ''
                                    }`}
                                    role="status"
                                    title={
                                        siteViewportError ??
                                        (designToolViewportStatus === 'loading'
                                            ? siteViewportLoadingMessage ?? undefined
                                            : undefined)
                                    }
                                >
                                    {designToolViewportLabel}
                                </span>
                                <div className="pci-unified-header__nav">
                                    {isEnergyCommunity ? (
                                        <span
                                            className="pci-unified-nav__badge pci-unified-nav__badge--energy-community"
                                            title="This property likely qualifies as an energy community."
                                            role="status"
                                        >
                                            <Info size={13} weight="fill" aria-hidden />
                                            Energy Community
                                        </span>
                                    ) : null}
                                    {showAddTitleButton ? (
                                        <button
                                            type="button"
                                            className={`pci-unified-nav__btn${
                                                titleValidation.status === 'complete'
                                                    ? ' pci-unified-nav__btn--complete'
                                                    : ' pci-unified-nav__btn--needs'
                                            }${
                                                highlightValidation && titleValidation.status !== 'complete'
                                                    ? ' pci-unified-nav__btn--attention'
                                                    : ''
                                            }`}
                                            disabled={Boolean(opportunityBlockReason)}
                                            title={opportunityBlockReason ?? undefined}
                                            onClick={() => {
                                                if (opportunityBlockReason) return;
                                                void openAddTitleContactsModal();
                                            }}
                                        >
                                            <IdentificationBadge size={13} weight="bold" aria-hidden />
                                            Add Title
                                        </button>
                                    ) : (
                                        <button
                                            type="button"
                                            className={`pci-unified-nav__btn${
                                                activePanel === 'title' ? ' pci-unified-nav__btn--active' : ''
                                            }${
                                                titleValidation.status === 'complete'
                                                    ? ' pci-unified-nav__btn--complete'
                                                    : ' pci-unified-nav__btn--needs'
                                            }${
                                                highlightValidation && titleValidation.status !== 'complete'
                                                    ? ' pci-unified-nav__btn--attention'
                                                    : ''
                                            }`}
                                            disabled={Boolean(opportunityBlockReason)}
                                            title={opportunityBlockReason ?? undefined}
                                            onClick={() => {
                                                if (opportunityBlockReason) return;
                                                openPanel('title');
                                            }}
                                        >
                                            Title
                                        </button>
                                    )}
                                    <div ref={utilityWrapRef} className="pci-utility-popover-wrap">
                                        <button
                                            ref={utilityButtonRef}
                                            type="button"
                                            className={`pci-unified-nav__btn${
                                                utilityPopoverOpen ? ' pci-unified-nav__btn--active' : ''
                                            }${
                                                utilityValidation.status === 'complete'
                                                    ? ' pci-unified-nav__btn--complete'
                                                    : ' pci-unified-nav__btn--needs'
                                            }${
                                                highlightValidation && utilityValidation.status !== 'complete'
                                                    ? ' pci-unified-nav__btn--attention'
                                                    : ''
                                            }`}
                                            disabled={Boolean(opportunityBlockReason)}
                                            title={opportunityBlockReason ?? undefined}
                                            aria-expanded={utilityPopoverOpen}
                                            aria-haspopup="dialog"
                                            onClick={(event) => {
                                                if (opportunityBlockReason) return;
                                                toggleUtilityPopover(event);
                                            }}
                                        >
                                            Utility
                                        </button>
                                    </div>
                                    <button
                                        type="button"
                                        className={`pci-unified-nav__btn pci-unified-nav__btn--losses pci-unified-nav__btn--finance-eligibility${
                                            financeEligibilityOpen ? ' pci-unified-nav__btn--active' : ''
                                        }`}
                                        aria-expanded={financeEligibilityOpen}
                                        aria-haspopup="dialog"
                                        title={
                                            solarFinancePartner
                                                ? `Finance eligibility for ${solarFinancePartner}`
                                                : 'Finance eligibility'
                                        }
                                        onClick={toggleFinanceEligibility}
                                    >
                                        <Bank size={13} weight="fill" aria-hidden />
                                        {solarFinancePartner
                                            ? `Finance Eligibility: ${solarFinancePartner}`
                                            : 'Finance Eligibility'}
                                    </button>
                                    {showAiSection ? (
                                        <button
                                            type="button"
                                            className={`pci-unified-nav__btn pci-unified-nav__btn--ai${
                                                solarDesignsPanelActive
                                                    ? ' pci-unified-nav__btn--active'
                                                    : ''
                                            }`}
                                            aria-expanded={solarDesignsPanelActive}
                                            aria-haspopup="dialog"
                                            onClick={openAiDesignsPopup}
                                        >
                                            <Sparkle size={13} weight="fill" aria-hidden />
                                            AI Designs
                                        </button>
                                    ) : null}
                                    {systemDesignLosses ? (
                                        <button
                                            type="button"
                                            className={`pci-unified-nav__btn pci-unified-nav__btn--losses${
                                                designLossesOpen ? ' pci-unified-nav__btn--active' : ''
                                            }`}
                                            aria-expanded={designLossesOpen}
                                            aria-haspopup="dialog"
                                            title={
                                                designLossesLabel
                                                    ? `Design losses source: ${designLossesLabel}`
                                                    : 'View design losses'
                                            }
                                            onClick={() => setDesignLossesOpen(true)}
                                        >
                                            <TrendDown size={13} weight="fill" aria-hidden />
                                            {designLossesLabel ?? 'Losses'}
                                        </button>
                                    ) : null}
                                </div>
                            </div>
                        ) : (
                            <p className="pci-unified-header__unavailable" role="status">
                                Aurora design unavailable — choose a design tool to continue.
                            </p>
                        )}
                    </div>

                    {!designToolReady ? (
                    <div id="pci-board-v2-card-body" className="pci-unified-header__setup">
                        <div className="pci-board-v2-section pci-board-v2-section--unavailable">
                            <div className="pci-design-unavailable" role="status">
                                <WarningCircle size={22} weight="fill" />
                                <div className="pci-design-unavailable__copy">
                                    <strong>
                                        {form.auroraAvailable
                                            ? 'Select a design tool'
                                            : 'Aurora design is not available'}
                                    </strong>
                                    <p>
                                        {form.auroraAvailable
                                            ? 'Choose Aurora or Hover to continue with products, setup, and details.'
                                            : 'Products, setup, and details are hidden until a design tool is selected. Choose Hover if it is available for this opportunity.'}
                                    </p>
                                </div>
                                <button
                                    ref={designToolButtonRef}
                                    type="button"
                                    className="pci-btn pci-btn--primary pci-btn--sm"
                                    aria-expanded={designToolPopoverOpen}
                                    aria-haspopup="dialog"
                                    onClick={toggleDesignToolPopover}
                                >
                                    Choose design tool
                                </button>
                            </div>
                        </div>
                    </div>
                    ) : null}
                </div>

                {opportunityBlockReason ? (
                    <div
                        className={`pci-opportunity-banner pci-opportunity-banner--page${
                            opportunityLoading
                                ? ' pci-opportunity-banner--loading'
                                : ' pci-opportunity-banner--error'
                        }`}
                        role={opportunityLoading ? 'status' : 'alert'}
                    >
                        {opportunityLoading ? (
                            <CircleNotch
                                className="pci-opportunity-banner__spinner"
                                size={18}
                                weight="bold"
                                aria-hidden
                            />
                        ) : (
                            <WarningCircle size={18} weight="fill" aria-hidden />
                        )}
                        <div className="pci-opportunity-banner__body">
                            <p className="pci-opportunity-banner__title">
                                {opportunityLoading ? 'Loading opportunity' : 'Opportunity not loaded'}
                            </p>
                            <p className="pci-opportunity-banner__copy">{opportunityBlockReason}</p>
                        </div>
                        {!opportunityLoading ? (
                            <button
                                type="button"
                                className="pci-opportunity-banner__retry"
                                onClick={() => void hydrateOpportunityForIntake()}
                            >
                                Try again
                            </button>
                        ) : null}
                    </div>
                ) : null}

                {(siteViewportError || siteViewportLoadingMessage) && showSolarLayoutStatus ? (
                    <p
                        className={`pci-board-fab__status pci-board-fab__status--stack${
                            siteViewportError ? ' pci-board-fab__status--error' : ''
                        }`}
                        role={siteViewportError ? 'alert' : 'status'}
                    >
                        {siteViewportError ? (
                            <WarningCircle size={14} weight="fill" aria-hidden />
                        ) : (
                            <CircleNotch
                                className="pci-board-v2-next__loading-icon"
                                size={14}
                                weight="bold"
                                aria-hidden
                            />
                        )}
                        <span>{siteViewportError || siteViewportLoadingMessage}</span>
                    </p>
                ) : null}

                {designToolReady && !opportunityBlockReason && !productsPrefillReady ? (
                    <section className="pci-unified-deal" aria-label="Products">
                        <header className="pci-unified-deal__head">
                            <h2 className="pci-unified-deal__title">Products</h2>
                            <p className="pci-unified-deal__subtitle">Loading opportunity products</p>
                        </header>
                        <ProductsSectionLoading />
                    </section>
                ) : designToolReady && !opportunityBlockReason && selectedProducts.length > 0 ? (
                    <PreContractProductsSetupProvider
                        salesForceId={site.salesForceId}
                        selectedProducts={selectedProducts}
                        suggestedSolarPartner={suggestedSolarPartner}
                        opportunityBlockReason={opportunityBlockReason}
                        designPendingBlockReason={
                            oneDrawFinalizePending ? oneDrawProductsLockedReason : null
                        }
                    >
                        <PreContractIntakeProductsWorkbench
                            generatePartnerBridgeRef={generatePartnerBridgeRef}
                            onSolarPricingOptionChange={setSolarSelectedPricingOption}
                            onSolarPricingOptionsChange={setSolarPricingOptions}
                            form={form}
                            ppaRateLoading={ppaRateLoading}
                            onOpenPpaRates={
                                sandboxPpaOffers.length > 0 ? openPpaRatesModal : undefined
                            }
                            selectedProducts={selectedProducts}
                            availableProductsToAdd={availableProductsToAdd}
                            savedDesignChecked={savedDesignChecked}
                            selectedAiPricingOverride={selectedAiPricingOverride}
                            summaryItems={summaryItems}
                            isEnergyCommunity={isEnergyCommunity}
                            solarProductAllowed={solarProductAllowed}
                            solarProductBlockReason={solarProductBlockReason}
                            solarDesignModified={solarDesignModified}
                            roofDesignModified={roofDesignModified}
                            oneDrawFinalizePending={oneDrawFinalizePending}
                            utilityDetailsDirty={utilityDetailsDirty}
                            oneDrawProductsLockedReason={oneDrawProductsLockedReason}
                            solarDesignChanges={oneDrawDesignChanges}
                            utilityChanges={utilityChanges}
                            openProductDesign={openProductDesign}
                            removeProduct={removeProduct}
                            handleOpenPricingPanel={handleOpenPricingPanel}
                            setProductPickerOpen={setProductPickerOpen}
                            onGenerateProduct={startProductGenerate}
                            onRunGenerateStep={runProductGenerateStep}
                            generateDisabled={Boolean(generateQuoteDisabledReason)}
                            generateDisabledReason={generateQuoteDisabledReason}
                            generateRunning={generateRunning}
                            activeGenerateProduct={activeGenerateProduct}
                            generateSteps={generateSteps}
                            linkedQuotes={linkedQuotes}
                            onUnlinkQuote={(product) => {
                                unlinkQuote(product);
                                void runProductGenerateStep(product, 'quote');
                            }}
                            onOpenDashboardV2={handleOpenDashboardV2}
                        />
                    </PreContractProductsSetupProvider>
                ) : designToolReady && !opportunityBlockReason ? (
                    <section className="pci-unified-deal" aria-label="Products">
                        <header className="pci-unified-deal__head">
                            <h2 className="pci-unified-deal__title">Products</h2>
                            <p className="pci-unified-deal__subtitle">
                                Tap a card to open its design
                            </p>
                        </header>
                        <div
                            className="pci-product-cards pci-product-cards--empty"
                            role="list"
                            aria-label="Selected products"
                        >
                            {availableProductsToAdd.length > 0 ? (
                                <button
                                    type="button"
                                    role="listitem"
                                    className="pci-product-card-tile pci-product-card-tile--add"
                                    aria-label="Add product"
                                    title="Add product"
                                    onClick={() => setProductPickerOpen(true)}
                                >
                                    <Plus size={32} weight="bold" aria-hidden />
                                    <span className="pci-product-card-tile__add-label">Add product</span>
                                </button>
                            ) : null}
                        </div>
                    </section>
                ) : null}

                {designToolReady && !opportunityBlockReason && selectedProducts.length > 0 ? (
                    <PreContractCommerceSection
                        selectedProducts={selectedProducts}
                        onOpenDashboardV2={handleOpenDashboardV2}
                    />
                ) : null}

                {showSelectedSection ? (
                    <PreContractSelectedDesignStep
                        form={form}
                        patchForm={patchForm}
                        embedded
                        onBack={goBackFromBoard}
                        onOpenPricing={handleOpenPricingPanel}
                        onOpenOneDraw={handleOpenOneDrawFromAi}
                        onFinalize={handleFinalizeFromAi}
                        onSaveOneButton={handleSaveOneButtonFromSelected}
                        actionsDisabled={!allComplete || !opportunityReady}
                    />
                ) : null}
                </div>
                </div>
            </main>
            {pricingPanelOpen
                ? createPortal(
                      <>
                          <button
                              type="button"
                              className="pci-pricing-sidepanel-backdrop"
                              onClick={() => setPricingPanelOpen(false)}
                              aria-label="Close pricing panel"
                          />
                          <aside className="pci-pricing-sidepanel" aria-label="Finance partner pricing">
                              <PreContractLivePricingRail
                                  form={form}
                                  metricsOverride={selectedAiPricingOverride}
                                  preferEnfinPartner={isEnergyCommunity}
                                  onClose={() => setPricingPanelOpen(false)}
                              />
                          </aside>
                      </>,
                      getPciViewportPortalRoot(),
                  )
                : null}
            </div>

            {designToolReady ? (
                <nav
                    className={`pci-unified-mobile-nav${
                        showAiSection ? '' : ' pci-unified-mobile-nav--three'
                    }`}
                    aria-label="Quick navigation"
                >
                    <button
                        type="button"
                        className="pci-unified-mobile-nav__btn"
                        onClick={() => openSolarLayoutPopup()}
                    >
                        <Sun size={18} weight="duotone" aria-hidden />
                        <span>Layout</span>
                    </button>
                    {showAiSection ? (
                        <button
                            type="button"
                            className={`pci-unified-mobile-nav__btn${
                                solarDesignsPanelActive
                                    ? ' pci-unified-mobile-nav__btn--active'
                                    : ''
                            }`}
                            onClick={openAiDesignsPopup}
                        >
                            <Sparkle size={18} weight="fill" aria-hidden />
                            <span>AI</span>
                        </button>
                    ) : null}
                    <button
                        type="button"
                        className="pci-unified-mobile-nav__btn"
                        onClick={handleOpenPricingPanel}
                    >
                        <CurrencyDollar size={18} weight="bold" aria-hidden />
                        <span>Pricing</span>
                    </button>
                    <button
                        type="button"
                        className="pci-unified-mobile-nav__btn"
                        onClick={() => openPanel('design')}
                    >
                        <Wrench size={18} weight="duotone" aria-hidden />
                        <span>Setup</span>
                    </button>
                </nav>
            ) : null}

            {activePanel ? (
                <>
                    <button
                        type="button"
                        className="pci-drawer-backdrop"
                        onClick={closePanel}
                        aria-label="Close panel"
                    />
                    <aside
                        className={`pci-drawer${roofDesignDrawerOpen ? ' pci-drawer--roof-design' : ''}`}
                        aria-label={designDrawerTitle || undefined}
                    >
                        <div className="pci-drawer__handle" aria-hidden />
                        <header className="pci-drawer__head">
                            <h2>{designDrawerTitle}</h2>
                            {activePanelValidation ? (
                                <ValidationBadge validation={activePanelValidation} compact />
                            ) : null}
                            <button
                                type="button"
                                className="pci-drawer__close"
                                onClick={closePanel}
                                aria-label="Close"
                            >
                                <X size={18} weight="bold" />
                            </button>
                        </header>
                        {activePanelValidation && activePanelValidation.status !== 'complete' ? (
                            <ValidationPanel validation={activePanelValidation} />
                        ) : null}
                        <div className="pci-drawer__body">
                            <StepBody
                                stepId={PANEL_STEPS[activePanel]}
                                form={form}
                                patchForm={patchForm}
                                site={site}
                                installDisplay={installDisplay}
                                designIssues={designIssues}
                                utilityFromOpportunity={utilityFromOpportunity}
                                ppaRateLoading={ppaRateLoading}
                                minimumPpaRate={minimumPpaRate}
                                selectedSandboxPpaOffer={selectedSandboxPpaOffer}
                                sandboxPpaOfferCount={sandboxPpaOffers.length}
                                onOpenPpaRates={openPpaRatesModal}
                                titleLoading={titleLoading}
                                titleInfo={titleInfo}
                                titleInfoAvailable={titleInfoAvailable}
                                titleContactName={titleContactName}
                                onOpenAddTitle={() => void openAddTitleContactsModal()}
                                onManageContacts={openManageContactsModal}
                                designPanelOpen={activePanel === 'design'}
                                onDesignPanelClose={closePanel}
                                onDesignDrawerFooterChange={setDesignDrawerFooter}
                                onRoofDesignSaved={() => {
                                    roofSavedDirtyRef.current = true;
                                    setRoofDesignDirty(true);
                                }}
                                theme={theme}
                                designProductFocus={designProductFocus}
                                opportunityBlockReason={opportunityBlockReason}
                                opportunityLoading={opportunityLoading}
                            />
                        </div>
                        <footer
                            className={`pci-drawer__footer${
                                designDrawerFooter ? ' pci-drawer__footer--embedded' : ''
                            }`}
                        >
                            {designDrawerFooter ?? (
                                <button
                                    type="button"
                                    className="pci-drawer__footer-close"
                                    onClick={closePanel}
                                >
                                    Close
                                </button>
                            )}
                        </footer>
                    </aside>
                </>
            ) : null}

            {productPickerOpen
                ? createPortal(
                      <div
                          className="pci-product-picker-popup"
                          role="dialog"
                          aria-modal="true"
                          aria-labelledby="pci-product-picker-title"
                      >
                          <button
                              type="button"
                              className="pci-product-picker-popup__backdrop"
                              onClick={() => setProductPickerOpen(false)}
                              aria-label="Close product picker"
                          />
                          <div className="pci-product-picker-popup__dialog">
                              <header className="pci-product-picker-popup__head">
                                  <h2 id="pci-product-picker-title">Add product</h2>
                                  <button
                                      type="button"
                                      className="pci-product-picker-popup__close"
                                      onClick={() => setProductPickerOpen(false)}
                                      aria-label="Close"
                                  >
                                      <X size={18} weight="bold" />
                                  </button>
                              </header>
                              <div className="pci-product-picker-popup__body">
                                  {availableProductsToAdd.length > 0 ? (
                                      <div className="pci-product-picker-popup__grid">
                                          {availableProductsToAdd.map((product) => {
                                              const meta = PRODUCT_META[product];
                                              const blockReason = isProductAddBlocked(product);
                                              const blocked = Boolean(blockReason);
                                              return (
                                                  <button
                                                      key={product}
                                                      type="button"
                                                      className={`pci-product-picker-card pci-product-picker-card--${meta.accent}${
                                                          blocked ? ' pci-product-picker-card--disabled' : ''
                                                      }`}
                                                      disabled={blocked}
                                                      title={blockReason ?? undefined}
                                                      aria-disabled={blocked}
                                                      onClick={() => addProductAndOpenDesign(product)}
                                                  >
                                                      <span className="pci-product-picker-card__icon">
                                                          {meta.icon}
                                                      </span>
                                                      <span className="pci-product-picker-card__name">
                                                          {product}
                                                      </span>
                                                      <span className="pci-product-picker-card__desc">
                                                          {blockReason ?? meta.description}
                                                      </span>
                                                  </button>
                                              );
                                          })}
                                      </div>
                                  ) : (
                                      <p className="pci-product-picker-popup__empty" role="status">
                                          All available products have been added.
                                      </p>
                                  )}
                              </div>
                          </div>
                      </div>,
                      getPciViewportPortalRoot(),
                  )
                : null}

            {solarLayoutPopupActive
                ? createPortal(
                      <div
                          className="pci-solar-layout-popup"
                          role="dialog"
                          aria-modal="true"
                          aria-labelledby="pci-solar-layout-popup-title"
                      >
                          <button
                              type="button"
                              className="pci-solar-layout-popup__backdrop"
                              onClick={closeSolarLayoutPopup}
                              aria-label="Close solar layout"
                          />
                          <div className="pci-solar-layout-popup__dialog">
                              <header className="pci-solar-layout-popup__head">
                                  <h2 id="pci-solar-layout-popup-title">Solar layout</h2>
                                  <button
                                      type="button"
                                      className="pci-solar-layout-popup__close"
                                      onClick={closeSolarLayoutPopup}
                                      aria-label="Close"
                                  >
                                      <X size={18} weight="bold" />
                                  </button>
                              </header>
                              <div className="pci-solar-layout-popup__body">
                                  <PreContractSolarLayoutDetailsBar
                                      form={form}
                                      summaryItems={summaryItems}
                                      metricsOverride={
                                          solarLayoutDetailsViewportPreview?.baseMetrics ??
                                          selectedAiPricingOverride
                                      }
                                      viewportPreview={solarLayoutDetailsViewportPreview}
                                      selectedPricingOption={solarSelectedPricingOption}
                                      financeOptions={solarPricingOptions}
                                      financePartnerEligibility={financePartnerEligibility}
                                      onSelectFinanceOption={handleSelectSolarFinanceOption}
                                      financePartner={solarFinancePartner}
                                      designRevision={form.solarDesignRevision}
                                      designModified={oneDrawFinalizePending}
                                      appliedDesignLabel={form.appliedAiDesignTitle}
                                      utilityPopoverOpen={utilityPopoverOpen}
                                      utilityComplete={utilityValidation.status === 'complete'}
                                      utilityDisabled={Boolean(opportunityBlockReason)}
                                      utilityDisabledReason={opportunityBlockReason}
                                      utilityWrapRef={utilitySolarLayoutWrapRef}
                                      utilityButtonRef={utilitySolarLayoutButtonRef}
                                      onToggleUtility={toggleUtilityPopover}
                                      highlightValidation={highlightValidation}
                                      onFinalize={handleFinalizeFromCurrentLayout}
                                      finalizeDisabled={!opportunityReady}
                                  />
                                  <div className="pci-unified-layout-row">
                                      <div
                                          ref={designViewerViewportRef}
                                          id="pci-section-viewport"
                                          className="pci-unified-section pci-unified-layout-row__viewport"
                                      >
                                          {moduleViewportSlot}
                                      </div>
                                      <div
                                          id="pci-section-layout"
                                          className="pci-module-step pci-module-step--embedded"
                                      >
                                          <section className="pci-module-step__card pci-module-step__card--layout pci-module-step__card--popup">
                                              <div className="pci-module-panel">
                                                  <div className="pci-module-panel__chrome pci-module-panel__chrome--primary">
                                                      <div
                                                          className="pci-solar-panel-tabs"
                                                          role="tablist"
                                                          aria-label="Solar layout panels"
                                                      >
                                                          <button
                                                              type="button"
                                                              role="tab"
                                                              className={`pci-solar-panel-tabs__btn pci-solar-panel-tabs__btn--saved${
                                                                  isAllDesignsTab(
                                                                      solarLayoutPanelTab,
                                                                  )
                                                                      ? ' pci-solar-panel-tabs__btn--on'
                                                                      : ''
                                                              }`}
                                                              aria-selected={isAllDesignsTab(
                                                                  solarLayoutPanelTab,
                                                              )}
                                                              title="All designs"
                                                              onClick={() =>
                                                                  handleSolarLayoutPanelTab('all')
                                                              }
                                                          >
                                                              <SquaresFour
                                                                  size={14}
                                                                  weight="bold"
                                                                  aria-hidden
                                                              />
                                                              <span className="pci-solar-panel-tabs__label">
                                                                  All Designs
                                                              </span>
                                                          </button>
                                                          <button
                                                              type="button"
                                                              role="tab"
                                                              className={`pci-solar-panel-tabs__btn pci-solar-panel-tabs__btn--config${
                                                                  solarLayoutPanelTab === 'config'
                                                                      ? ' pci-solar-panel-tabs__btn--on'
                                                                      : ''
                                                              }`}
                                                              aria-selected={
                                                                  solarLayoutPanelTab === 'config'
                                                              }
                                                              title="AI Config"
                                                              onClick={() =>
                                                                  handleSolarLayoutPanelTab(
                                                                      'config',
                                                                  )
                                                              }
                                                          >
                                                              <SlidersHorizontal
                                                                  size={14}
                                                                  weight="bold"
                                                                  aria-hidden
                                                              />
                                                              <span className="pci-solar-panel-tabs__label">
                                                                  AI Config
                                                              </span>
                                                          </button>
                                                          <button
                                                              type="button"
                                                              role="tab"
                                                              className={`pci-solar-panel-tabs__btn pci-solar-panel-tabs__btn--designs${
                                                                  solarLayoutPanelTab === 'designs'
                                                                      ? ' pci-solar-panel-tabs__btn--on'
                                                                      : ''
                                                              }`}
                                                              aria-selected={
                                                                  solarLayoutPanelTab === 'designs'
                                                              }
                                                              disabled={!showAiSection}
                                                              title="AI Designs"
                                                              onClick={() =>
                                                                  handleSolarLayoutPanelTab(
                                                                      'designs',
                                                                  )
                                                              }
                                                          >
                                                              <Sparkle
                                                                  size={14}
                                                                  weight="fill"
                                                                  aria-hidden
                                                              />
                                                              <span className="pci-solar-panel-tabs__label">
                                                                  AI Designs
                                                              </span>
                                                          </button>
                                                      </div>
                                                  </div>
                                                  {solarLayoutPanelTab === 'designs' ? (
                                                      <PreContractAiDesignsStep
                                                          key={aiDesignRunId}
                                                          form={form}
                                                          generationForm={
                                                              aiGenerationForm ?? form
                                                          }
                                                          patchForm={patchForm}
                                                          embedded
                                                          solarPanelChild
                                                          onBack={goBackFromBoard}
                                                          onApply={handleApplyAiDesign}
                                                          onNext={handleAiDesignsContinue}
                                                          onOpenViewport={openViewportPopup}
                                                          onPreviewAiOption={
                                                              handlePreviewAiOption
                                                          }
                                                          onPreviewOnViewport={handlePreviewOnViewport}
                                                          viewportPreviewId={solarViewportPreviewId}
                                                          viewportPreviewLayouts={
                                                              solarViewportPreview?.layouts
                                                          }
                                                          viewportPreviewRoofSelections={
                                                              solarViewportPreview?.roofSelections
                                                          }
                                                          onPreviewToggleModule={
                                                              handlePreviewToggleModule
                                                          }
                                                          onPreviewToggleRoof={handlePreviewToggleRoof}
                                                          onOpenSolarLayout={() =>
                                                              handleSolarLayoutPanelTab('config')
                                                          }
                                                          viewportAvailable={designViewerKept}
                                                          actionsDisabled={!allComplete}
                                                      />
                                                  ) : (
                                                      <>
                                                          <PreContractModuleStep
                                                              form={form}
                                                              patchForm={patchForm}
                                                              summaryItems={summaryItems}
                                                              onBack={closeSolarLayoutPopup}
                                                              onRunAiDesign={handleRunAiDesign}
                                                              onOpenPricing={handleOpenPricingPanel}
                                                              runAiDisabled={!canRunAiFromLayoutPopup}
                                                              runAiIssues={runAiIssues}
                                                              actionsDisabled={!allComplete}
                                                              hasPreviousAiDesigns={
                                                                  hasPreviousAiDesigns
                                                              }
                                                              embedded
                                                              solarPanelChild
                                                              hideBody={isAllDesignsTab(
                                                                  solarLayoutPanelTab,
                                                              )}
                                                              onShowBody={() =>
                                                                  handleSolarLayoutPanelTab(
                                                                      'config',
                                                                  )
                                                              }
                                                              renderViewport={false}
                                                              onTreeCuttingActiveChange={
                                                                  setTreeCuttingPreviewActive
                                                              }
                                                              onTreeGhostChange={
                                                                  handleTreeGhostChange
                                                              }
                                                          />
                                                          {isAllDesignsTab(solarLayoutPanelTab) ? (
                                                              <PreContractSavedDesignsPanel
                                                                  designs={savedOneDrawDesigns}
                                                                  loading={
                                                                      savedOneDrawDesignsLoading
                                                                  }
                                                                  selectedKey={
                                                                      selectedSavedDesignKey
                                                                  }
                                                                  selectingKey={
                                                                      selectingSavedDesignKey
                                                                  }
                                                                  onSelect={
                                                                      handleSelectSavedOneDrawDesign
                                                                  }
                                                                  maxLayoutCards={
                                                                      maxFillLossesPricing.cards
                                                                  }
                                                                  maxLayoutSystemKw={
                                                                      maxFillLossesPricing.systemKw
                                                                  }
                                                                  maxLayoutModuleCount={
                                                                      maxFillLossesPricing.moduleCount
                                                                  }
                                                                  selectedMaxVendor={
                                                                      allDesignsCard ===
                                                                      'max-enfin'
                                                                          ? 'EnFin'
                                                                          : allDesignsCard ===
                                                                              'max-onedraw'
                                                                            ? 'oneDRAW'
                                                                            : null
                                                                  }
                                                                  maxLayoutLoading={
                                                                      selectingMaxLayout
                                                                  }
                                                                  onSelectMaxLayout={
                                                                      handleSelectMaxLayoutCard
                                                                  }
                                                                  onToggleModule={
                                                                      handleLossesToggleModule
                                                                  }
                                                                  onToggleRoof={
                                                                      handleLossesToggleRoof
                                                                  }
                                                                  form={form}
                                                              />
                                                          ) : null}
                                                      </>
                                                  )}
                                              </div>
                                          </section>
                                      </div>
                                  </div>
                              </div>
                          </div>
                      </div>,
                      getPciViewportPortalRoot(),
                  )
                : null}

            {viewportPopupActive
                ? createPortal(
                      <div
                          className={`pci-viewport-popup${
                              viewportFinalizeOpen ? ' pci-viewport-popup--finalize' : ''
                          }`}
                          role="dialog"
                          aria-modal="true"
                          aria-labelledby="pci-viewport-popup-title"
                      >
                          <button
                              type="button"
                              className="pci-viewport-popup__backdrop"
                              onClick={closeViewportPopup}
                              aria-label="Close 3D view"
                          />
                          <div className="pci-viewport-popup__dialog">
                              <header className="pci-viewport-popup__head">
                                  <h2 id="pci-viewport-popup-title">
                                      {viewportFinalizeOpen ? 'Finalize design' : '3D view'}
                                  </h2>
                                  <button
                                      type="button"
                                      className="pci-viewport-popup__close"
                                      onClick={closeViewportPopup}
                                      aria-label="Close"
                                  >
                                      ×
                                  </button>
                              </header>
                              <div
                                  ref={
                                      designViewerSlotMode === 'popup'
                                          ? designViewerSlotRef
                                          : undefined
                                  }
                                  className="pci-viewport-popup__slot pci-design-viewer-slot"
                              />
                          </div>
                      </div>,
                      getPciViewportPortalRoot(),
                  )
                : null}
            {designViewerKept
                ? createPortal(
                      <div
                          ref={designViewerHostRef}
                          id="pci-onedraw-host"
                          className={`pci-onedraw-host${
                              designViewerDocked
                                  ? ` pci-onedraw-host--docked${
                                        dockHostElevated
                                            ? ' pci-onedraw-host--docked-elevated'
                                            : ' pci-onedraw-host--docked-popup'
                                    }`
                                  : ' pci-onedraw-host--parked'
                          }`}
                          style={
                              !designViewerDocked && lastDesignViewerDockRectRef.current
                                  ? {
                                        // Match last dock size while parked so reopen doesn't cold-resize WebGL.
                                        width: lastDesignViewerDockRectRef.current.width,
                                        height: lastDesignViewerDockRectRef.current.height,
                                    }
                                  : undefined
                          }
                          aria-hidden={!designViewerDocked}
                      >
                          {designViewerDocked &&
                          solarLayoutPopupOpen &&
                          solarViewportPreview ? (
                              <div
                                  className="pci-viewport-ai-design-tag pci-viewport-ai-design-tag--on-host"
                                  aria-live="polite"
                              >
                                  <Sparkle
                                      size={14}
                                      weight="fill"
                                      aria-hidden
                                  />
                                  <span className="pci-viewport-ai-design-tag__eyebrow">
                                      AI design preview
                                  </span>
                                  <strong className="pci-viewport-ai-design-tag__name">
                                      {solarViewportPreview.optionLabel}
                                  </strong>
                              </div>
                          ) : null}
                          <PreContractOneDrawEmbed salesForceId={site.salesForceId} />
                      </div>,
                      getPciViewportPortalRoot(),
                  )
                : null}
            <AddTitleContactsModal
                open={addTitleContactsModalOpen}
                setOpen={setAddTitleContactsModalOpen}
                initialData={titleSearchData}
                onSuccess={handleTitleSyncSuccess}
            />
            <DashboardV3SectionModals
                theme={theme === 'dark' ? 'dark' : 'light'}
                dashboardUiVariant="v2"
                contactsOpen={contactsModalOpen}
                onCloseContacts={closeManageContactsModal}
                aiDesignsOpen={false}
                onCloseAiDesigns={() => undefined}
                designsDashboardSlot={null}
                contactsProps={contactsModalProps}
            />
            <PreContractFinalizeConfirmModal
                open={finalizeConfirmOpen}
                metrics={(() => {
                    const solarMetrics = resolvePreContractSolarMetrics({
                        form,
                        systemDesign,
                        metricsOverride: selectedAiPricingOverride,
                    });
                    return {
                        systemSize: solarMetrics.systemKw
                            ? `${solarMetrics.systemKw.toFixed(2)} kW`
                            : '—',
                        production: solarMetrics.annualProductionKwh
                            ? `${Math.round(solarMetrics.annualProductionKwh).toLocaleString()} kWh`
                            : (summaryItems.find((item) => item.label === 'System Production')
                                  ?.value ?? '—'),
                        multiplier: solarMetrics.multiplier
                            ? String(Number(solarMetrics.multiplier.toFixed(3)))
                            : '—',
                        annualUsage:
                            summaryItems.find((item) => item.label === 'Utility Usage')?.value ??
                            '—',
                        losses: lossesSourceLabel(solarLossesPricing.lossesVendor),
                    };
                })()}
                onContinue={() => resolveFinalizeConfirm(true)}
                onCancel={() => resolveFinalizeConfirm(false)}
            />
            <PreContractFinalizeModal
                open={finalizeModalOpen}
                defaultAnnualUsage={
                    Number.parseFloat(form.annualUsageKwh) > 0
                        ? Number.parseFloat(form.annualUsageKwh)
                        : null
                }
                onClose={() => setFinalizeModalOpen(false)}
                onSaved={handleFinalizeSaved}
                onCancelled={() => {
                    const waiter = finalizeSavedWaiterRef.current;
                    finalizeSavedWaiterRef.current = null;
                    waiter?.reject(new Error('FINALIZE_CANCELLED'));
                }}
            />
            <PreContractQuoteReviewModal
                review={quoteReview}
                confirming={generateRunning}
                onConfirm={confirmQuoteReview}
                onCancel={cancelQuoteReview}
            />
            {confirmQuoteCellKey
                ? createPortal(
                      <GenerateQuoteModal
                          confirmQuoteCellKey={confirmQuoteCellKey}
                          quoteForm={quoteForm}
                          priceQuote={priceQuoteDetails}
                          setConfirmQuoteCellKey={handleSetConfirmQuoteCellKey}
                          setQuoteForm={setQuoteForm}
                          updateQuoteVersion={handleGenerateQuoteComplete}
                          rcomendedQuote
                      />,
                      getPciViewportPortalRoot(),
                  )
                : null}
            <PreContractSandboxPpaRatesModal
                open={ppaRatesModalOpen}
                offers={sandboxPpaOffers}
                selectedOfferId={selectedSandboxPpaOfferId}
                onSelect={handleSelectSandboxPpaOffer}
                onClose={closePpaRatesModal}
            />
        </div>
    );
};

function ValidationBadge({
    validation,
    compact = false,
}: {
    validation: StepValidation;
    compact?: boolean;
}) {
    const icon =
        validation.status === 'complete' ? (
            <CheckCircle size={compact ? 14 : 14} weight="fill" />
        ) : validation.status === 'error' ? (
            <WarningCircle size={compact ? 14 : 14} weight="fill" />
        ) : (
            <Circle size={compact ? 14 : 14} weight="bold" />
        );

    return (
        <span
            className={`pci-validation-badge pci-validation-badge--${validation.status}${
                compact ? ' pci-validation-badge--compact' : ''
            }`}
        >
            {icon}
            {validation.label}
        </span>
    );
}

function ValidationPanel({ validation }: { validation: StepValidation }) {
    const title =
        validation.status === 'error' ? 'Resolve before launch' : 'Complete this section';

    return (
        <div
            className={`pci-validation-panel pci-validation-panel--${validation.status}`}
            role="alert"
        >
            <WarningCircle size={18} weight="fill" />
            <div>
                <strong>{title}</strong>
                <ul className="pci-validation-panel__list">
                    {validation.issues.map((issue) => (
                        <li key={issue}>{issue}</li>
                    ))}
                </ul>
            </div>
        </div>
    );
}

type StepBodyProps = {
    stepId: WizardStepId;
    form: PreContractIntakeForm;
    patchForm: (patch: Partial<PreContractIntakeForm>) => void;
    site: PreContractSiteContext;
    installDisplay: string;
    designIssues: string[];
    utilityFromOpportunity: boolean;
    ppaRateLoading: boolean;
    minimumPpaRate: number | null;
    selectedSandboxPpaOffer: PreContractSandboxPpaOffer | null;
    sandboxPpaOfferCount: number;
    onOpenPpaRates: () => void;
    titleLoading: boolean;
    titleInfo: Record<string, unknown> | null;
    titleInfoAvailable: boolean;
    titleContactName: string;
    onOpenAddTitle: () => void;
    onManageContacts: () => void;
    designPanelOpen?: boolean;
    onDesignPanelClose?: () => void;
    onDesignDrawerFooterChange?: (footer: ReactNode | null) => void;
    onRoofDesignSaved?: () => void;
    theme?: string;
    designProductFocus?: PreContractProduct | null;
    opportunityBlockReason?: string | null;
    opportunityLoading?: boolean;
};

function UtilityFormBody({
    form,
    patchForm,
    utilityFromOpportunity,
    ppaRateLoading,
    minimumPpaRate,
    selectedSandboxPpaOffer,
    sandboxPpaOfferCount,
    onOpenPpaRates,
}: {
    form: PreContractIntakeForm;
    patchForm: (patch: Partial<PreContractIntakeForm>) => void;
    utilityFromOpportunity: boolean;
    ppaRateLoading: boolean;
    minimumPpaRate: number | null;
    selectedSandboxPpaOffer: PreContractSandboxPpaOffer | null;
    sandboxPpaOfferCount: number;
    onOpenPpaRates: () => void;
}) {
    const opportunityDetails = useOneButtonStore((s) => s.opportunityDetails);
    const stateCode = resolveUtilityCompanyState(form, opportunityDetails);
    const { catalog: companyCatalog, status: catalogStatus } =
        usePreContractUtilityCatalog(stateCode);

    const selectOptions = utilityCompanyOptionsFromCatalog(
        companyCatalog,
        catalogStatus,
        form.utilityCompany,
    );

    /** Opportunity prefills arrive as free text — snap them to the catalog name, or drop them. */
    useEffect(() => {
        if (catalogStatus !== 'ready') return;
        const name = form.utilityCompany.trim();
        if (!name) return;
        const exact = companyCatalog.find((item) => item.name === name);
        if (exact && form.utilityCompanyId === exact.Utility_company_id) return;
        patchForm(utilityCompanySelectionPatch(name, companyCatalog));
    }, [catalogStatus, companyCatalog, form.utilityCompany, form.utilityCompanyId, patchForm]);

    const handleUtilityCompanyChange = (event: ChangeEvent<HTMLSelectElement>) => {
        const patch = utilityCompanySelectionPatch(event.target.value, companyCatalog);
        patchForm(patch);
        console.log('Utility Company mapped', {
            Utility_company_id: patch.utilityCompanyId || null,
            name: patch.utilityCompany || null,
            state: stateCode || null,
        });
    };

    const handleAnnualUsageChange = (event: ChangeEvent<HTMLInputElement>) => {
        patchForm({ annualUsageKwh: event.target.value });
    };

    const handleUtilityRateChange = (event: ChangeEvent<HTMLInputElement>) => {
        patchForm({ utilityRate: event.target.value });
    };

    const handleTargetMinPpaRateChange = (event: ChangeEvent<HTMLInputElement>) => {
        patchForm({ targetMinPpaRate: event.target.value });
    };

    const escalator = resolvePreContractEscalator(form.targetEscalator);
    /** A partner offer can sit off the standard ladder (e.g. 2.9%) — keep it selectable. */
    const escalatorChoices = useMemo(() => {
        const values: number[] = [...PRECONTRACT_ESCALATOR_OPTIONS];
        if (!values.some((value) => Math.abs(value - escalator) < 1e-6)) values.push(escalator);
        return values.sort((a, b) => b - a);
    }, [escalator]);

    const handleEscalatorChange = (event: ChangeEvent<HTMLSelectElement>) => {
        patchForm({ targetEscalator: event.target.value });
    };

    return (
        <div className="pci-utility-popover__form">
            {utilityFromOpportunity ? (
                <p className="pci-opp-prefill pci-opp-prefill--compact" role="status">
                    <CheckCircle size={14} weight="fill" />
                    From opportunity Utility Details
                </p>
            ) : null}
            <label className="pci-field pci-field--full">
                <span>Escalator</span>
                <select value={String(escalator)} onChange={handleEscalatorChange}>
                    {escalatorChoices.map((value) => (
                        <option key={value} value={String(value)}>
                            {formatSandboxEscalator(value)}
                        </option>
                    ))}
                </select>
            </label>
            <label className="pci-field pci-field--full">
                <span>Utility company</span>
                <select
                    value={form.utilityCompany}
                    onChange={handleUtilityCompanyChange}
                    disabled={catalogStatus === 'loading'}
                >
                    <option value="">
                        {catalogStatus === 'loading'
                            ? 'Loading utility providers…'
                            : 'Select utility provider'}
                    </option>
                    {selectOptions.map((u) => (
                        <option key={u} value={u}>
                            {u}
                        </option>
                    ))}
                </select>
            </label>
            {catalogStatus === 'empty' ? (
                <p
                    className="pci-opp-prefill pci-opp-prefill--compact pci-opp-prefill--warn pci-field--full"
                    role="status"
                >
                    <WarningCircle size={14} weight="fill" />
                    {utilityCatalogEmptyMessage(stateCode)}
                </p>
            ) : null}
            <label className="pci-field">
                <span>Annual usage (kWh)</span>
                <input
                    type="number"
                    value={form.annualUsageKwh}
                    onChange={handleAnnualUsageChange}
                />
            </label>
            <label className="pci-field">
                <span>All-in rate ($/kWh)</span>
                <input
                    type="number"
                    step="0.001"
                    value={form.utilityRate}
                    onChange={handleUtilityRateChange}
                />
            </label>
            <div className="pci-field pci-field--full pci-field--ppa-rate">
                <span>Target/Min PPA Rate ($/kWh)</span>
                <input
                    type={ppaRateLoading ? 'text' : 'number'}
                    step="0.001"
                    min={minimumPpaRate ?? 0}
                    value={ppaRateLoading ? 'Loading rate…' : form.targetMinPpaRate}
                    onChange={handleTargetMinPpaRateChange}
                    disabled={ppaRateLoading}
                    title={
                        minimumPpaRate != null
                            ? `Minimum workbook rate: $${minimumPpaRate.toFixed(3)}/kWh`
                            : undefined
                    }
                />
                {selectedSandboxPpaOffer && !ppaRateLoading ? (
                    <p className="pci-field__hint">
                        {selectedSandboxPpaOffer.partner} ·{' '}
                        {formatSandboxEscalator(selectedSandboxPpaOffer.escalator)}
                    </p>
                ) : null}
                <button
                    type="button"
                    className="pci-field__link-btn"
                    onClick={onOpenPpaRates}
                    disabled={ppaRateLoading || sandboxPpaOfferCount === 0}
                >
                    View partner rates
                </button>
            </div>
        </div>
    );
}

function DesignToolFormBody({
    form,
    patchForm,
    onSelect,
}: {
    form: PreContractIntakeForm;
    patchForm: (patch: Partial<PreContractIntakeForm>) => void;
    onSelect: (tool: PreContractDesignTool) => void;
}) {
    const tools: {
        id: PreContractDesignTool;
        label: string;
        available: boolean;
        availableKey: 'auroraAvailable' | 'hoverAvailable';
    }[] = [
        {
            id: 'aurora',
            label: 'Aurora',
            available: form.auroraAvailable,
            availableKey: 'auroraAvailable',
        },
        {
            id: 'hover',
            label: 'Hover',
            available: form.hoverAvailable,
            availableKey: 'hoverAvailable',
        },
    ];

    return (
        <div className="pci-utility-popover__form pci-design-tool-form">
            <p className="pci-section-lead pci-design-tool-form__lead">
                Choose an available design tool for this opportunity.
            </p>
            <div className="pci-design-tool-form__list">
                {tools.map((tool) => {
                    const selected = form.preferredDesignTool === tool.id && tool.available;
                    return (
                        <div key={tool.id} className="pci-design-tool-option">
                            <button
                                type="button"
                                className={`pci-design-tool-option__select${
                                    selected ? ' pci-design-tool-option__select--on' : ''
                                }${tool.available ? '' : ' pci-design-tool-option__select--disabled'}`}
                                disabled={!tool.available}
                                onClick={() => onSelect(tool.id)}
                            >
                                <strong>{tool.label}</strong>
                                <span>{tool.available ? (selected ? 'Selected' : 'Available') : 'Not available'}</span>
                            </button>
                            <button
                                type="button"
                                className={`pci-design-tool-option__toggle${
                                    tool.available ? ' pci-design-tool-option__toggle--on' : ''
                                }`}
                                onClick={() => {
                                    const nextAvailable = !tool.available;
                                    const patch: Partial<PreContractIntakeForm> = {
                                        [tool.availableKey]: nextAvailable,
                                    };
                                    if (!nextAvailable && form.preferredDesignTool === tool.id) {
                                        patch.preferredDesignTool = null;
                                    }
                                    if (nextAvailable && !form.preferredDesignTool) {
                                        patch.preferredDesignTool = tool.id;
                                    }
                                    patchForm(patch);
                                }}
                            >
                                {tool.available ? 'Mark unavailable' : 'Mark available'}
                            </button>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

type PreContractIntakeProductsWorkbenchProps = {
    onSolarPricingOptionChange?: (option: PreContractProductPricingOption | null) => void;
    onSolarPricingOptionsChange?: (options: PreContractProductPricingOption[]) => void;
    generatePartnerBridgeRef: React.MutableRefObject<{
        resolveDesignPartner: (
            product: PreContractProduct,
        ) => PreContractDesignPartnerRecommendation | null;
        resolveSelectedPricingOption: (
            product: PreContractProduct,
        ) => import('../lib/preContractProductPartners').PreContractProductPricingOption | null;
        selectPricingOption: (
            product: PreContractProduct,
            option: PreContractProductPricingOption,
        ) => void;
        syncDesignPartner: (
            product: PreContractProduct,
            recommendation: PreContractDesignPartnerRecommendation,
        ) => Promise<{ ok: boolean; message?: string }>;
        openPartnerSyncOnBlock?: (
            product: PreContractProduct,
            partnerName: string,
            reason?: string,
        ) => boolean;
        openPartnerSetup?: (product: PreContractProduct) => void;
    } | null>;
    form: PreContractIntakeForm;
    ppaRateLoading: boolean;
    onOpenPpaRates?: () => void;
    selectedProducts: PreContractProduct[];
    availableProductsToAdd: PreContractProduct[];
    /** Saved design lookup settled — the opportunity link callout waits on it. */
    savedDesignChecked: boolean;
    selectedAiPricingOverride: PreContractPpwMetricsOverride | null;
    summaryItems: PreContractSummaryItem[];
    isEnergyCommunity: boolean;
    solarProductAllowed: boolean;
    solarProductBlockReason: string | null;
    solarDesignModified: boolean;
    roofDesignModified: boolean;
    oneDrawFinalizePending: boolean;
    utilityDetailsDirty: boolean;
    oneDrawProductsLockedReason: string;
    solarDesignChanges: SolarOneDrawDesignChange[];
    utilityChanges: import('../lib/preContractUtilityPrefill').PreContractUtilityDetailChange[];
    openProductDesign: (product: PreContractProduct) => void;
    removeProduct: (product: PreContractProduct) => void;
    handleOpenPricingPanel: () => void;
    setProductPickerOpen: (open: boolean) => void;
    onGenerateProduct: (product: PreContractProduct) => void;
    onRunGenerateStep?: (
        product: PreContractProduct,
        stepId: import('../lib/preContractProductGeneratePipeline').ProductGenerateStepId,
    ) => void;
    generateDisabled: boolean;
    generateDisabledReason?: string;
    generateRunning: boolean;
    activeGenerateProduct: PreContractProduct | null;
    generateSteps: import('../lib/preContractProductGeneratePipeline').ProductGenerateStep[];
    linkedQuotes?: Partial<Record<PreContractProduct, string>>;
    onUnlinkQuote?: (product: PreContractProduct) => void;
    onOpenDashboardV2?: () => void;
};

function PreContractIntakeProductsWorkbench({
    onSolarPricingOptionChange,
    onSolarPricingOptionsChange,
    generatePartnerBridgeRef,
    form,
    ppaRateLoading,
    onOpenPpaRates,
    selectedProducts,
    availableProductsToAdd,
    savedDesignChecked,
    selectedAiPricingOverride,
    summaryItems,
    isEnergyCommunity,
    solarProductAllowed,
    solarProductBlockReason,
    solarDesignModified,
    roofDesignModified,
    oneDrawFinalizePending,
    utilityDetailsDirty,
    oneDrawProductsLockedReason,
    solarDesignChanges,
    utilityChanges,
    openProductDesign,
    removeProduct,
    handleOpenPricingPanel,
    setProductPickerOpen,
    onGenerateProduct,
    onRunGenerateStep,
    generateDisabled,
    generateDisabledReason,
    generateRunning,
    activeGenerateProduct,
    generateSteps,
    linkedQuotes,
    onUnlinkQuote,
    onOpenDashboardV2,
}: PreContractIntakeProductsWorkbenchProps) {
    const {
        syncDesignPartner,
        syncingProduct,
        productSummaries,
        openProductsSetup,
        blocked,
        productsEditLocked,
        productsEditLockedReason,
        loading,
        statusError,
    } = usePreContractProductsSetup();
    const financeHubCtx = useOptionalProductsFinanceHubContext();
    const opportunityDetails = useOneButtonStore((s) => s.opportunityDetails);
    const storeOpportunityId = useOneButtonStore((s) => s.opportunityID);
    const storeSalesForceId = useOneButtonStore((s) => s.salesForceId);
    const setCommerceHubQuotesOpen = useOneButtonStore((s) => s.setCommerceHubQuotesOpen);
    const setCommerceHubAgreementsOpen = useOneButtonStore((s) => s.setCommerceHubAgreementsOpen);
    const savedSystemDesign = useOneButtonStore((s) => s.systemDesign);
    const financePartnerSyncStatus = useOneButtonStore((s) => s.financePartnerSyncStatus);
    const financePartnerRawSyncStatuses = useOneButtonStore((s) => s.financePartnerRawSyncStatuses);
    const theme = useOneButtonStore((s) => s.theme);
    const [pricingBundles, setPricingBundles] = useState<
        Partial<Record<PreContractProduct, PreContractProductPricingBundle>>
    >({});
    const [selectedPricingOptions, setSelectedPricingOptions] = useState<
        Partial<Record<PreContractProduct, PreContractProductPricingOption>>
    >({});
    const [designPartnersLoading, setDesignPartnersLoading] = useState(false);

    const resolveSavedPricingOption = useCallback(
        (product: PreContractProduct, options: PreContractProductPricingOption[]) => {
            const saved = productSummaries.find((entry) => entry.product === product);
            return findPricingOptionForSavedPartner(options, {
                partner: saved?.partner ?? null,
                method: saved?.method ?? null,
            });
        },
        [productSummaries],
    );

    const resolveWorkbenchDesignPartner = useCallback(
        (product: PreContractProduct): PreContractDesignPartnerRecommendation | null => {
            const bundle = pricingBundles[product];
            const options = bundle?.options ?? [];
            const selectedOption = selectedPricingOptions[product] ?? null;
            const savedOption = resolveSavedPricingOption(product, options);
            const option =
                (selectedOption &&
                savedOption &&
                pricingOptionsMatch(selectedOption, savedOption)
                    ? selectedOption
                    : null) ??
                savedOption ??
                selectedOption ??
                options.find((entry) => entry.recommended && entry.available) ??
                options.find((entry) => entry.available) ??
                options[0] ??
                null;
            if (option) {
                return optionToDesignRecommendation(product, option);
            }
            return bundle?.recommended ?? null;
        },
        [pricingBundles, resolveSavedPricingOption, selectedPricingOptions],
    );

    const productQuotePlans = useMemo(() => {
        const plans: Partial<Record<PreContractProduct, PreContractProductQuotePlan>> = {};
        selectedProducts.forEach((product) => {
            const saved = productSummaries.find((entry) => entry.product === product);
            const designPartner = resolveWorkbenchDesignPartner(product);
            const partner =
                saved?.partner?.trim() || designPartner?.partner?.trim() || '';
            const needsPartnerSync =
                !saved?.partner?.trim() ||
                Boolean(
                    designPartner?.available &&
                        designPartner.partner.trim() &&
                        resolvePartnerSyncState(
                            {
                                partner: saved?.partner ?? null,
                                method: saved?.method ?? null,
                            },
                            designPartner,
                        ) !== 'match',
                );
            const needsUtilityDetails =
                utilityDetailsDirty && (product === 'Solar' || product === 'Battery');
            plans[product] = {
                partner,
                needsPartnerSync,
                needsFinalize:
                    (product === 'Solar' || product === 'Battery') && oneDrawFinalizePending,
                needsUtilityDetails,
            };
        });
        return plans;
    }, [
        oneDrawFinalizePending,
        productSummaries,
        resolveWorkbenchDesignPartner,
        selectedProducts,
        utilityDetailsDirty,
    ]);

    const resolveWorkbenchSelectedPricingOption = useCallback(
        (product: PreContractProduct) => {
            const bundle = pricingBundles[product];
            const options = bundle?.options ?? [];
            const selectedOption = selectedPricingOptions[product] ?? null;
            const savedOption = resolveSavedPricingOption(product, options);
            return (
                (selectedOption &&
                savedOption &&
                pricingOptionsMatch(selectedOption, savedOption)
                    ? selectedOption
                    : null) ??
                savedOption ??
                selectedOption ??
                options.find((option) => option.recommended && option.available) ??
                options.find((option) => option.available) ??
                options[0] ??
                null
            );
        },
        [pricingBundles, resolveSavedPricingOption, selectedPricingOptions],
    );

    const openPartnerSyncOnBlock = useCallback(
        (product: PreContractProduct, partnerName: string, reason?: string) => {
            const quoteProductTypes = [product as 'Solar' | 'Roof' | 'Battery'];
            const scope = {
                quotePartner: partnerName,
                quoteProductTypes,
            };
            const syncGateBase = {
                opportunityDetails,
                financePartnerSyncStatus,
                financePartnerRawSyncStatuses,
            };
            const incomplete = findFirstIncompleteQuoteSyncTarget(syncGateBase, scope);
            const target = incomplete
                ? {
                      productTypeName: incomplete.productTypeName,
                      gridPartner: incomplete.partner,
                      productTypes: quoteProductTypes,
                  }
                : {
                      productTypeName: product as 'Solar' | 'Roof' | 'Battery',
                      gridPartner: partnerName,
                      productTypes: quoteProductTypes,
                  };
            const opened = openPartnerSyncForQuoteTarget(
                target,
                opportunityDetails,
                financeHubCtx,
            );
            if (!opened) {
                showPartnerSyncRequiredModal(
                    theme,
                    reason ??
                        'Complete Partner Sync on the product card for this finance partner before generating a quote.',
                );
            }
            return opened;
        },
        [
            financeHubCtx,
            financePartnerRawSyncStatuses,
            financePartnerSyncStatus,
            opportunityDetails,
            theme,
        ],
    );

    useEffect(() => {
        onSolarPricingOptionChange?.(resolveWorkbenchSelectedPricingOption('Solar'));
        onSolarPricingOptionsChange?.(pricingBundles.Solar?.options ?? []);
        generatePartnerBridgeRef.current = {
            resolveDesignPartner: resolveWorkbenchDesignPartner,
            resolveSelectedPricingOption: resolveWorkbenchSelectedPricingOption,
            selectPricingOption: (product, option) => {
                setSelectedPricingOptions((prev) => ({ ...prev, [product]: option }));
            },
            syncDesignPartner,
            openPartnerSyncOnBlock,
            openPartnerSetup: (product) => openProductsSetup(product),
        };
        return () => {
            generatePartnerBridgeRef.current = null;
        };
    }, [
        generatePartnerBridgeRef,
        onSolarPricingOptionChange,
        onSolarPricingOptionsChange,
        openPartnerSyncOnBlock,
        openProductsSetup,
        pricingBundles.Solar?.options,
        resolveWorkbenchDesignPartner,
        resolveWorkbenchSelectedPricingOption,
        syncDesignPartner,
    ]);

    const designPartnerInputsKey = useMemo(
        () =>
            JSON.stringify({
                products: form.products,
                installState: form.installState,
                utilityCompany: form.utilityCompany,
                utilityRate: form.utilityRate,
                systemUsageKwh: form.systemUsageKwh,
                roofModuleLayouts: form.roofModuleLayouts,
                roofSelections: form.roofSelections,
                moduleTypeWattage: form.moduleTypeWattage,
                roofSlopeLowOverride: form.roofSlopeLowOverride,
                roofSlopeNormalOverride: form.roofSlopeNormalOverride,
                roofSlopeLowTotal: form.roofSlopeLowTotal,
                roofSlopeNormalTotal: form.roofSlopeNormalTotal,
                batteryQuantity: form.batteryQuantity,
                batteryManufacturer: form.batteryManufacturer,
                batteryName: form.batteryName,
                metricsOverride: selectedAiPricingOverride,
                preferEnfin: isEnergyCommunity,
            }),
        [
            form.batteryManufacturer,
            form.batteryName,
            form.batteryQuantity,
            form.installState,
            form.moduleTypeWattage,
            form.products,
            form.roofModuleLayouts,
            form.roofSelections,
            form.roofSlopeLowOverride,
            form.roofSlopeLowTotal,
            form.roofSlopeNormalOverride,
            form.roofSlopeNormalTotal,
            form.systemUsageKwh,
            form.utilityCompany,
            form.utilityRate,
            isEnergyCommunity,
            selectedAiPricingOverride,
        ],
    );

    useEffect(() => {
        let cancelled = false;
        setDesignPartnersLoading(true);

        fetchProductPricingBundles(form, selectedAiPricingOverride, isEnergyCommunity)
            .then((next) => {
                if (cancelled) return;
                setPricingBundles(next);
                setSelectedPricingOptions((prev) => {
                    const merged = { ...prev };
                    for (const product of selectedProducts) {
                        const bundle = next[product];
                        if (!bundle) {
                            delete merged[product];
                            continue;
                        }
                        const keep = prev[product];
                        const freshMatch =
                            keep &&
                            bundle.options.find((option) => pricingOptionsMatch(option, keep));
                        const saved = productSummaries.find((entry) => entry.product === product);
                        const savedMatch = findPricingOptionForSavedPartner(bundle.options, {
                            partner: saved?.partner ?? null,
                            method: saved?.method ?? null,
                        });
                        const keepMatchesSaved =
                            Boolean(
                                keep &&
                                    savedMatch &&
                                    pricingOptionsMatch(keep, savedMatch),
                            );
                        merged[product] =
                            (keepMatchesSaved ? freshMatch : null) ??
                            savedMatch ??
                            freshMatch ??
                            bundle.options.find((option) => option.recommended && option.available) ??
                            bundle.options.find((option) => option.available) ??
                            bundle.options[0];
                    }
                    return merged;
                });
            })
            .catch((error) => {
                if (cancelled || quoteServices.isPricingRequestAborted(error)) return;
                setPricingBundles({});
                setSelectedPricingOptions({});
            })
            .finally(() => {
                if (!cancelled) setDesignPartnersLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [designPartnerInputsKey, form, isEnergyCommunity, productSummaries, selectedAiPricingOverride, selectedProducts]);

    const partnerSyncDisabled =
        blocked || productsEditLocked || loading || Boolean(statusError) || designPartnersLoading;
    const priceQuoteOverrides = useOneButtonStore((s) => s.priceQuoteOverrides);
    const productQuoteLocked = useMemo(() => {
        const reasons: Partial<Record<PreContractProduct, string | null>> = {};
        for (const product of selectedProducts) {
            reasons[product] = productQuoteDesignBlockReason(product, {
                form,
                systemDesign: savedSystemDesign,
                priceQuoteOverrides,
                metricsOverride: selectedAiPricingOverride,
                summaryItems,
                ppaRateLoading,
            });
        }
        return reasons;
    }, [
        form,
        ppaRateLoading,
        priceQuoteOverrides,
        savedSystemDesign,
        selectedAiPricingOverride,
        selectedProducts,
        summaryItems,
    ]);

    const productsCollapsed = generateRunning;

    const opportunityLink = useMemo(
        () =>
            resolvePreContractOpportunityLink({
                opportunityDetails,
                systemDesign: savedSystemDesign,
                products: selectedProducts,
            }),
        [opportunityDetails, savedSystemDesign, selectedProducts],
    );

    return (
        <>
            <section
                className={`pci-unified-deal${
                    productsCollapsed ? ' pci-unified-deal--collapsed' : ''
                }`}
                aria-label="Products"
            >
                <header className="pci-unified-deal__head">
                    <h2 className="pci-unified-deal__title">Products</h2>
                </header>
                {loading || !savedDesignChecked ? (
                    <ProductsSectionLoading />
                ) : (
                    <PreContractOpportunityLinkBanner
                        link={opportunityLink}
                        onReview={blocked || productsEditLocked ? undefined : () => openProductsSetup()}
                        showCommerce
                        onOpenQuotes={() => setCommerceHubQuotesOpen(true)}
                        onOpenAgreements={() => setCommerceHubAgreementsOpen(true)}
                        salesForceId={
                            String(
                                (opportunityDetails as { SalesForce_Id?: string; Salesforce_Id?: string } | null)
                                    ?.SalesForce_Id ??
                                    (opportunityDetails as { Salesforce_Id?: string } | null)?.Salesforce_Id ??
                                    storeSalesForceId ??
                                    '',
                            ).trim() || null
                        }
                        opportunityNumericId={
                            storeOpportunityId ??
                            (opportunityDetails as { Opportunity_Id?: number } | null)?.Opportunity_Id ??
                            null
                        }
                        salesPerson={
                            (opportunityDetails as { SalesPerson?: OpportunitySalesPerson } | null)
                                ?.SalesPerson ?? null
                        }
                        ahj={String(
                            (
                                (opportunityDetails as { OpportunityHighlight?: { AHJ?: string } } | null)
                                    ?.OpportunityHighlight?.AHJ ?? ''
                            ),
                        ).trim()}
                        onOpenDashboardV2={onOpenDashboardV2}
                    />
                )}
                {loading || !savedDesignChecked ? null : (
                <div
                    className="pci-unified-deal__body pci-product-cards obd-card--system-design obd-theme-light obd-system-design__grid"
                    aria-hidden={productsCollapsed}
                    role="list"
                    aria-label="Selected products"
                >
                    {selectedProducts.map((product) => {
                        const meta = PRODUCT_META[product];
                        const saved = productSummaries.find((entry) => entry.product === product);
                        const bundle = pricingBundles[product];
                        const options = bundle?.options ?? [];
                        const selectedOption = resolveWorkbenchSelectedPricingOption(product);
                        const designPartner = selectedOption
                            ? optionToDesignRecommendation(product, selectedOption)
                            : bundle?.recommended ?? null;
                        const partnerSyncState = resolvePartnerSyncState(
                            {
                                partner: saved?.partner ?? null,
                                method: saved?.method ?? null,
                            },
                            designPartner,
                        );

                        return (
                            <PreContractProductDealCard
                                key={product}
                                product={product}
                                form={form}
                                ppaRateLoading={ppaRateLoading}
                                onOpenPpaRates={onOpenPpaRates}
                                icon={meta.icon}
                                accent={meta.accent}
                                label={productPillLabel(product)}
                                metricsOverride={selectedAiPricingOverride}
                                summaryItems={summaryItems}
                                preferEnfinPartner={isEnergyCommunity}
                                onOpenDesign={() => openProductDesign(product)}
                                designActionDisabled={
                                    product === 'Solar' ? !solarProductAllowed : false
                                }
                                designActionDisabledReason={
                                    product === 'Solar' ? solarProductBlockReason : null
                                }
                                onRemove={() => removeProduct(product)}
                                designPartner={designPartner}
                                savedPartner={saved?.partner ?? null}
                                savedMethod={saved?.method ?? null}
                                partnerSyncState={partnerSyncState}
                                partnerSyncing={syncingProduct === product}
                                pricingLoading={designPartnersLoading}
                                pricingOptions={options}
                                selectedPricingOption={selectedOption}
                                onSelectPricingOption={(option) =>
                                    setSelectedPricingOptions((prev) => ({ ...prev, [product]: option }))
                                }
                                onOpenFinanceSetup={
                                    blocked || productsEditLocked
                                        ? undefined
                                        : () => openProductsSetup(product)
                                }
                                financeSetupLocked={blocked || productsEditLocked}
                                financeSetupLockedReason={
                                    productsEditLocked
                                        ? productsEditLockedReason ?? oneDrawProductsLockedReason
                                        : statusError
                                }
                                onSyncPartner={
                                    designPartner
                                        ? () => void syncDesignPartner(product, designPartner)
                                        : undefined
                                }
                                partnerSyncDisabled={partnerSyncDisabled}
                                designRevision={
                                    product === 'Solar' ? form.solarDesignRevision : null
                                }
                                designModified={
                                    product === 'Solar'
                                        ? oneDrawFinalizePending
                                        : product === 'Roof'
                                          ? roofDesignModified
                                          : false
                                }
                                appliedDesignLabel={
                                    product === 'Solar' ? form.appliedAiDesignTitle : null
                                }
                            />
                        );
                    })}
                    {availableProductsToAdd.length > 0 ? (
                        <button
                            type="button"
                            role="listitem"
                            className="pci-product-card-tile pci-product-card-tile--add"
                            aria-label="Add product"
                            title="Add product"
                            onClick={() => setProductPickerOpen(true)}
                        >
                            <Plus size={32} weight="bold" aria-hidden />
                            <span className="pci-product-card-tile__add-label">Add product</span>
                        </button>
                    ) : null}
                </div>
                )}
            </section>
            {loading || !savedDesignChecked ? null : (
            <PreContractProductCombinationsSection
                products={selectedProducts}
                onGenerateProduct={onGenerateProduct}
                onRunGenerateStep={onRunGenerateStep}
                generateDisabled={generateDisabled}
                generateDisabledReason={generateDisabledReason}
                generateRunning={generateRunning}
                activeGenerateProduct={activeGenerateProduct}
                generateSteps={generateSteps}
                productQuotePlans={productQuotePlans}
                productQuoteLocked={productQuoteLocked}
                solarDesignChanges={solarDesignChanges}
                utilityChanges={utilityChanges}
                linkedQuotes={linkedQuotes}
                onUnlinkQuote={onUnlinkQuote}
            />
            )}
        </>
    );
}

function StepBody({
    stepId,
    form,
    patchForm,
    site,
    installDisplay,
    designIssues,
    utilityFromOpportunity,
    ppaRateLoading,
    minimumPpaRate,
    selectedSandboxPpaOffer,
    sandboxPpaOfferCount,
    onOpenPpaRates,
    titleLoading,
    titleInfo,
    titleInfoAvailable,
    titleContactName,
    onOpenAddTitle,
    onManageContacts,
    designPanelOpen = false,
    onDesignPanelClose,
    onDesignDrawerFooterChange,
    onRoofDesignSaved,
    theme = 'light',
    designProductFocus = null,
    opportunityBlockReason = null,
    opportunityLoading = false,
}: StepBodyProps) {
    switch (stepId) {
        case 'products':
            return (
                <>
                    <p className="pci-section-lead">Select every product included in this deal.</p>
                    <div className="pci-product-grid">
                        {PRE_CONTRACT_PRODUCTS.map((product) => {
                            const meta = PRODUCT_META[product];
                            const selected = form.products[product];
                            const disabled = PRE_CONTRACT_DISABLED_PRODUCTS.has(product);
                            return (
                                <button
                                    key={product}
                                    type="button"
                                    className={`pci-product-card pci-product-card--${meta.accent}${
                                        selected ? ' pci-product-card--selected' : ''
                                    }${disabled ? ' pci-product-card--disabled' : ''}`}
                                    aria-disabled={disabled}
                                    disabled={disabled}
                                    title={disabled ? 'Coming soon' : undefined}
                                    onClick={() => {
                                        if (disabled) return;
                                        patchForm({
                                            products: {
                                                ...form.products,
                                                [product]: !selected,
                                            },
                                        });
                                    }}
                                >
                                    <span className="pci-product-card__icon">{meta.icon}</span>
                                    <span className="pci-product-card__name">{product}</span>
                                    <span className="pci-product-card__desc">{meta.description}</span>
                                    {selected ? (
                                        <span className="pci-product-card__check">
                                            <Check size={14} weight="bold" />
                                        </span>
                                    ) : null}
                                </button>
                            );
                        })}
                    </div>
                </>
            );

        case 'site':
            return (
                <>
                    <p className="pci-section-lead">Confirm the job site address from the sites table.</p>
                    <div className="pci-site-layout">
                        <div className="pci-field-stack">
                            <label className="pci-field pci-field--full">
                                <span>Street address</span>
                                <input
                                    value={form.installStreet}
                                    onChange={(e) => patchForm({ installStreet: e.target.value })}
                                />
                            </label>
                            <div className="pci-field-row">
                                <label className="pci-field">
                                    <span>City</span>
                                    <input
                                        value={form.installCity}
                                        onChange={(e) => patchForm({ installCity: e.target.value })}
                                    />
                                </label>
                                <label className="pci-field">
                                    <span>State</span>
                                    <select
                                        value={form.installState}
                                        onChange={(e) => patchForm({ installState: e.target.value })}
                                    >
                                        <option value="">Select</option>
                                        {US_STATE_OPTIONS.map((st) => (
                                            <option key={st.value} value={st.value}>
                                                {st.label}
                                            </option>
                                        ))}
                                    </select>
                                </label>
                                <label className="pci-field pci-field--zip">
                                    <span>ZIP</span>
                                    <input
                                        value={form.installZip}
                                        onChange={(e) => patchForm({ installZip: e.target.value })}
                                    />
                                </label>
                            </div>
                        </div>
                        <div className="pci-map-preview">
                            <MapPin size={28} weight="duotone" />
                            <span>{installDisplay}</span>
                        </div>
                    </div>
                </>
            );

        case 'property': {
            if (titleLoading) {
                return (
                    <div className="pci-title-empty" role="status">
                        <CircleNotch className="pci-board-v2-next__loading-icon" size={18} weight="bold" />
                        <p>Loading title records…</p>
                    </div>
                );
            }

            if (!titleInfoAvailable) {
                return (
                    <div className="pci-title-empty" role="status">
                        <WarningCircle size={22} weight="fill" />
                        <div className="pci-title-empty__copy">
                            <strong>Title info is not available</strong>
                            <p>
                                Title records are not available. Add title contacts, or run Black Knight
                                from that window if no contacts are found.
                            </p>
                        </div>
                        <div className="pci-title-empty__actions">
                            <button
                                type="button"
                                className="pci-btn pci-btn--secondary pci-btn--sm"
                                onClick={onManageContacts}
                            >
                                <UsersThree size={16} weight="duotone" aria-hidden />
                                Manage contacts
                            </button>
                            <button
                                type="button"
                                className="pci-btn pci-btn--primary pci-btn--sm"
                                onClick={onOpenAddTitle}
                            >
                                <IdentificationBadge size={16} weight="bold" aria-hidden />
                                Add Title
                            </button>
                        </div>
                    </div>
                );
            }

            const fields = extractTitleFields(titleInfo);
            const owner = fields.owner || titleContactName || site.customerName;
            const ownerLooksMatched =
                Boolean(fields.owner || titleContactName) &&
                site.customerName
                    .toLowerCase()
                    .split(/\s+/)
                    .filter((part) => part.length > 2)
                    .some((part) => owner.toLowerCase().includes(part));
            return (
                <>
                    <div className="pci-drawer-section-head">
                        <p className="pci-section-lead pci-drawer-section-head__lead">
                            Verify ownership matches your primary customer.
                        </p>
                        <button
                            type="button"
                            className="pci-drawer-manage-contacts"
                            onClick={onManageContacts}
                        >
                            <UsersThree size={15} weight="duotone" aria-hidden />
                            Manage contacts
                        </button>
                    </div>
                    <div className="pci-verify-card">
                        <div className="pci-verify-card__status">
                            <Sparkle size={18} weight="fill" />
                            {fields.owner || fields.apn
                                ? 'Pulled from title records'
                                : 'Synced from opportunity contacts'}
                        </div>
                        <dl className="pci-verify-list">
                            <div>
                                <dt>Owner on title</dt>
                                <dd>{owner}</dd>
                            </div>
                            <div>
                                <dt>Property type</dt>
                                <dd>{fields.propertyType || '—'}</dd>
                            </div>
                            <div>
                                <dt>APN</dt>
                                <dd>{fields.apn || '—'}</dd>
                            </div>
                            <div>
                                <dt>Match status</dt>
                                <dd className={ownerLooksMatched ? 'pci-verify-match' : undefined}>
                                    {ownerLooksMatched ? 'Customer name aligns' : 'Review owner against the primary customer'}
                                </dd>
                            </div>
                        </dl>
                    </div>
                </>
            );
        }

        case 'utility':
            return (
                <UtilityFormBody
                    form={form}
                    patchForm={patchForm}
                    utilityFromOpportunity={utilityFromOpportunity}
                    ppaRateLoading={ppaRateLoading}
                    minimumPpaRate={minimumPpaRate}
                    selectedSandboxPpaOffer={selectedSandboxPpaOffer}
                    sandboxPpaOfferCount={sandboxPpaOfferCount}
                    onOpenPpaRates={onOpenPpaRates}
                />
            );

        case 'design': {
            const showRoofProductDesign =
                form.products.Roof && designProductFocus !== 'Battery';
            const showSolarSlopeOnly =
                form.products.Solar &&
                !form.products.Roof &&
                designProductFocus !== 'Battery';
            const showBattery =
                form.products.Battery && designProductFocus !== 'Roof';
            return (
                <div
                    className={`pci-design-step${
                        showRoofProductDesign ? ' pci-design-step--roof-modal' : ''
                    }`}
                >
                    {designIssues.length > 0 && !showRoofProductDesign ? (
                        <div className="pci-alert" role="alert">
                            <WarningCircle size={18} weight="fill" />
                            {designIssues.join(' · ')}
                        </div>
                    ) : null}

                    {showRoofProductDesign && opportunityBlockReason ? (
                        <div className="pci-alert pci-alert--inline" role="alert">
                            <WarningCircle size={18} weight="fill" />
                            <span>
                                {opportunityLoading
                                    ? opportunityBlockReason
                                    : `Roof settings cannot be saved until opportunity details load. ${opportunityBlockReason}`}
                            </span>
                        </div>
                    ) : null}

                    {showRoofProductDesign ? (
                        <div
                            className={`pci-roof-design-host obd-product-designs-modal-root obd-product-designs-modal-root--roof obd-theme-${
                                theme === 'dark' ? 'dark' : 'light'
                            }`}
                        >
                            <RoofDetailsModal
                                embedded
                                embeddedActive={designPanelOpen}
                                open={designPanelOpen}
                                onClose={onDesignPanelClose ?? (() => undefined)}
                                onEmbeddedFooterChange={onDesignDrawerFooterChange}
                                onDesignSaved={onRoofDesignSaved}
                            />
                        </div>
                    ) : null}

                    {showSolarSlopeOnly ? (
                        <PreContractRoofSlopeSection
                            salesForceId={site.salesForceId}
                            form={form}
                            patchForm={patchForm}
                        />
                    ) : null}

                    {showBattery ? (
                        <section className="pci-design-block pci-design-block--battery">
                            {opportunityBlockReason ? (
                                <div className="pci-alert pci-alert--inline" role="alert">
                                    <WarningCircle size={18} weight="fill" />
                                    <span>
                                        {opportunityLoading
                                            ? opportunityBlockReason
                                            : `Battery settings cannot be saved until opportunity details load. ${opportunityBlockReason}`}
                                    </span>
                                </div>
                            ) : null}
                            <div
                                className={`pci-battery-design-host obd-product-designs-modal-root obd-product-designs-modal-root--battery obd-theme-${
                                    theme === 'dark' ? 'dark' : 'light'
                                }`}
                            >
                                <BatteryDetailsModal
                                    embedded
                                    embedFooterInline
                                    embeddedActive={Boolean(designPanelOpen)}
                                    open={Boolean(designPanelOpen)}
                                    onClose={onDesignPanelClose ?? (() => undefined)}
                                    batteryData={
                                        form.batteryManufacturer || form.batteryName
                                            ? {
                                                  manufacturer: form.batteryManufacturer,
                                                  name: form.batteryName,
                                                  quantity: form.batteryQuantity,
                                              }
                                            : null
                                    }
                                />
                            </div>
                        </section>
                    ) : null}
                </div>
            );
        }

        default:
            return null;
    }
}

export default PreContractIntake;
